import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AppConfiguration } from 'src/app/app.configuration';
import { ConfigurationService, TurnoService } from 'src/app/core';
import { UserManagerService } from 'src/app/core/auth/user-manager.service';
import { TokenManagerService } from 'src/app/core/auth/token-manager.service';
import { ErrorToast } from 'src/app/core/toast/error.toast';
import {
  CVacanceData,
  GenerarNumAlbaranIdentificadorType,
  OtrosFopData,
  PinPandResponse,
} from '../models/pago.model';
import { MotivosService } from './motivos.service';
import { ProcedenciaService } from './procedencia.service';
import { VentaService } from './venta.service';
import { AmigosThyssenService } from '../../amigos-thyssen/services/amigos-thyssen.service';
import { environment } from 'src/environments/pedrera/environment.development-pedrera';
import JSZip from 'jszip';
import { TranslateService } from 'src/app/shared/services/translate.service';
import { getErrorMessage } from 'src/app/shared/enums/error-messages';
import { SuccessToast } from 'src/app/core/toast/success.toast';
import { SinpeService } from 'src/app/shared/services/sinpe.service';
import { CardPaymentFactory } from 'src/app/core/hardware/card-payment/cardPayment.factory';
import { CardPaymentType } from 'src/app/core/hardware/card-payment/cardPaymentType.enum';
import { PinpadCardPayment } from 'src/app/core/hardware/card-payment/pinpadCardPayment';

export interface Agrupados {
  tipoProducto: string;
  promoId: string;
  entradaId: string;
  posicionVenta: string;
  numEntrada: string;
  numPromoEntrada: string;
  codigoPromocional: string;
}

export type OpcionesImpresionQR = {
  itemsReimprimir?: string; // Definimos itemsReimprimir como opcional
  tpvId?: string;
  tpvImpresion?: string;
};

@Injectable({
  providedIn: 'root',
})
export class PagoService {
  private pagoTotalSubject: BehaviorSubject<any>;
  public pagoTotal: Observable<any>;

  private operacionesTarjetaSubject: BehaviorSubject<any>;
  public operacionesTarjeta: Observable<any>;

  private pagoDesglosadoSubject: BehaviorSubject<any>;
  public pagoDesglosado: Observable<any>;

  private formularioCorrectoSubject: BehaviorSubject<Boolean>;
  public formularioCorrecto: Observable<Boolean>;

  private opcionesPagoSubject: BehaviorSubject<any>;
  public opcionesPago: Observable<any>;

  private cambioSubject: BehaviorSubject<any>;
  public cambio: Observable<any>;

  private diferenciaSubject: BehaviorSubject<any>;
  public diferencia: Observable<any>;

  private hayQueFacturarSubject: BehaviorSubject<any>;
  public hayQueFacturar: Observable<any>;

  private hayQueFacturarSimpleSubject: BehaviorSubject<any>;
  public hayQueFacturarSimple: Observable<any>;

  private hayContactosSubject: BehaviorSubject<any>;
  public hayContactos: Observable<any>;

  private hayQueImprimirAlbaranSubject: BehaviorSubject<any>;
  public hayQueImprimirAlbaran: Observable<any>;

  private hayQueImprimirEtiquetasSubject: BehaviorSubject<any>;
  public hayQueImprimirEtiquetas: Observable<any>;

  private pagoSimpeSubject: BehaviorSubject<any>;
  public pagoSimpe: Observable<any>;

  private otrasFopData: BehaviorSubject<OtrosFopData[]>;
  public $otrasFopData: Observable<OtrosFopData[]>;

  private cVaccanceData: BehaviorSubject<CVacanceData>;
  public $cVaccanceData: Observable<CVacanceData>;

  //Enviar mails
  private sendMailsSubject: BehaviorSubject<boolean>;
  public sendMails$: Observable<boolean>;
  private stringMailsSubject: BehaviorSubject<string>;
  public stringMails$: Observable<string>;
  private sendMailsGestionSubject: BehaviorSubject<boolean>;
  public sendMailsGestion$: Observable<boolean>;
  private stringMailsGestionSubject: BehaviorSubject<string>;
  public stringMailsGestion$: Observable<string>;
  private noMailSubject: BehaviorSubject<boolean>;
  public noMail$: Observable<boolean>;

  // Yaribel 30102020  añadimos observable para el codigo de Tarjeta Monedero
  private codigoTarjetaMonederoSubject: BehaviorSubject<any>;
  public codigoTarjetaMonedero: Observable<any>;
  // Yaribel 30102020 fin de añadir observable

  //<<Yaribel 20210728 añadimos los observables para gestionar el descuento cliente mapfre VSTS 8370
  private hayDescuentoSubject: BehaviorSubject<boolean>;
  public hayDescuento$: Observable<boolean>;
  private descuentoObjetoSubject: BehaviorSubject<any>;
  public descuentoObjeto$: Observable<any>;
  //Yaribel 20210728 añadimos los observables para gestionar el descuento cliente mapfre VSTS 8370 >>

  private contactoId: BehaviorSubject<string>;
  public contactoId$: Observable<string>;
  private insercionBodyPayload: BehaviorSubject<any>;
  public insercionBodyPayload$: Observable<any>;
  private insercionVentaResponse: BehaviorSubject<any>;
  public insercionVentaResponse$: Observable<any>;

  conf: any;
  idioma = 'es-ES';
  window = window as any;
  minDia;
  maxDia;
  datosTarjeta: GenerarNumAlbaranIdentificadorType;
  mailsTicket: boolean;
  mailsJustificante: boolean;

  /**
   * Last successful CashKeeper sale ('N') Introducido/Devuelto (cents) for the next InsercionVenta.
   * Consumed when building the body in generarPeticionPago.
   */
  private lastCashKeeperInsercionCents: {
    introducedCents: number;
    returnedCents: number;
  } | null = null;

  /**
   * Card charge (Pin Pad / card reader) already authorized; if InsercionVenta fails, a terminal-only reversal may be required.
   */
  private pendingCardReversalAfterFailedInsertion = false;

  constructor(
    private userService: UserManagerService,
    private turnoService: TurnoService,
    private configuration: ConfigurationService,
    private venta: VentaService,
    private http: HttpClient,
    private config: AppConfiguration,
    private motivoService: MotivosService,
    private procedenciaService: ProcedenciaService,
    private toastService: ToastrService,
    private sinpeService: SinpeService,
    private translate: TranslateService,
    private tokenManager: TokenManagerService,
    private amigosThyssenService: AmigosThyssenService
  ) {
    this.pagoTotalSubject = new BehaviorSubject(undefined);
    this.pagoTotal = this.pagoTotalSubject.asObservable();

    this.cambioSubject = new BehaviorSubject(undefined);
    this.cambio = this.cambioSubject.asObservable();

    this.diferenciaSubject = new BehaviorSubject(undefined);
    this.diferencia = this.diferenciaSubject.asObservable();

    this.pagoDesglosadoSubject = new BehaviorSubject(undefined);
    this.pagoDesglosado = this.pagoDesglosadoSubject.asObservable();

    this.formularioCorrectoSubject = new BehaviorSubject(false);
    this.formularioCorrecto = this.pagoDesglosadoSubject.asObservable();

    this.opcionesPagoSubject = new BehaviorSubject(undefined);
    this.opcionesPago = this.opcionesPagoSubject.asObservable();

    this.hayQueFacturarSubject = new BehaviorSubject(false);
    this.hayQueFacturar = this.hayQueFacturarSubject.asObservable();

    this.hayQueFacturarSimpleSubject = new BehaviorSubject(false);
    this.hayQueFacturar = this.hayQueFacturarSimpleSubject.asObservable();

    this.hayQueImprimirAlbaranSubject = new BehaviorSubject(false);
    this.hayQueImprimirAlbaran =
      this.hayQueImprimirAlbaranSubject.asObservable();

    this.hayQueImprimirEtiquetasSubject = new BehaviorSubject(false);
    this.hayQueImprimirEtiquetas =
      this.hayQueImprimirEtiquetasSubject.asObservable();

    this.hayContactosSubject = new BehaviorSubject(false);
    this.hayQueFacturar = this.hayQueFacturarSubject.asObservable();

    //Enviar Mail
    this.sendMailsSubject = new BehaviorSubject(false);
    this.sendMails$ = this.sendMailsSubject.asObservable();
    this.stringMailsSubject = new BehaviorSubject('');
    this.stringMails$ = this.stringMailsSubject.asObservable();
    this.sendMailsGestionSubject = new BehaviorSubject(false);
    this.sendMailsGestion$ = this.sendMailsGestionSubject.asObservable();
    this.stringMailsGestionSubject = new BehaviorSubject('');
    this.stringMailsGestion$ = this.stringMailsGestionSubject.asObservable();
    this.noMailSubject = new BehaviorSubject(false);
    this.noMail$ = this.noMailSubject.asObservable();

    this.operacionesTarjetaSubject = new BehaviorSubject(false);
    this.operacionesTarjeta = this.operacionesTarjetaSubject.asObservable();

    this.otrasFopData = new BehaviorSubject([]);
    this.$otrasFopData = this.otrasFopData.asObservable();

    this.cVaccanceData = new BehaviorSubject({
      FOP: '',
      Importe: 0,
      CVaccance: [],
    });
    this.$cVaccanceData = this.cVaccanceData.asObservable();

    // Yaribel 30102020 inicializamos observable para el codigo de tarjeta monedero
    this.codigoTarjetaMonederoSubject = new BehaviorSubject(false);
    this.codigoTarjetaMonedero =
      this.codigoTarjetaMonederoSubject.asObservable();
    // Yaribel 30102020 finalizamos inicializacion de observable

    //<< Yaribel 20210728 añadimos los observables para gestionar el descuento cliente mapfre VSTS 8370
    this.hayDescuentoSubject = new BehaviorSubject(false);
    this.hayDescuento$ = this.hayDescuentoSubject.asObservable();
    this.descuentoObjetoSubject = new BehaviorSubject(undefined);
    this.descuentoObjeto$ = this.descuentoObjetoSubject.asObservable();
    // Yaribel 20210728 añadimos los observables para gestionar el descuento cliente mapfre VSTS 8370 >>
    this.contactoId = new BehaviorSubject('');
    this.contactoId$ = this.contactoId.asObservable();
    this.insercionBodyPayload = new BehaviorSubject(null);
    this.insercionBodyPayload$ = this.insercionBodyPayload.asObservable();
    this.insercionVentaResponse = new BehaviorSubject(null);
    this.insercionVentaResponse$ = this.insercionVentaResponse.asObservable();
  }

  // << Yaribel 20210728 añadimos los observables para gestionar el descuento cliente mapfre VSTS 8370
  public get hayDescuentoValue() {
    return this.venta.carritoValue.hayDescuento;
  }

  public setHayDescuento(descuento) {
    this.venta.carritoValue.hayDescuento = descuento;
    this.hayDescuentoSubject.next(descuento);
  }
  public get descuentoObjetoValue() {
    return this.descuentoObjetoSubject.value;
  }

  public setDescuentoObjeto(descuento) {
    this.descuentoObjetoSubject.next(descuento);
  }
  // Yaribel 20210728 añadimos los observables para gestionar el descuento cliente mapfre VSTS 8370 >>

  // Yaribel 30102020 añadimos get y set para el codigo de tarjeta monedero
  public get codigoTarjetaMonederoValue() {
    return this.codigoTarjetaMonederoSubject.value;
  }

  setCodigoTarjetaMonederoValue(codigoTarjetaMonedero) {
    this.codigoTarjetaMonederoSubject.next(codigoTarjetaMonedero);
  }
  // Yaribel 30102020 fin de añadir get y set

  public get operacionesTarjetaValue() {
    return this.operacionesTarjetaSubject.value;
  }

  setOperacionesTarjetaValue(operacionesTarjeta) {
    this.operacionesTarjetaSubject.next(operacionesTarjeta);
  }

  setPendingCardReversalAfterFailedInsertion(pending: boolean) {
    this.pendingCardReversalAfterFailedInsertion = pending;
  }

  /**
   * Terminal-only reversal after InsercionVenta fails (no AnularVenta: sale was not persisted).
   */
  tryReverseCardChargeAfterFailedInsertion(): {
    attempted: boolean;
    ok: boolean;
  } {
    if (!this.pendingCardReversalAfterFailedInsertion) {
      return { attempted: false, ok: true };
    }
    this.pendingCardReversalAfterFailedInsertion = false;

    const op = this.operacionesTarjetaValue as Record<string, any> | false;
    const datos = this.datosTarjeta;
    const pd = this.pagoDesglosadoSubject.value || {};

    const importeTc =
      Number(pd.pinpad ?? 0) + Number(pd.datafono ?? 0);
    const pinPadPart = Number(pd.pinpad ?? 0);

    if (
      !datos?.Identificador ||
      !datos?.NumAlbaran ||
      importeTc <= 0 ||
      !op ||
      typeof op !== 'object'
    ) {
      console.warn(
        '[PagoService] Reversión TC omitida: faltan datos de pedido tarjeta, operación o importe.',
        { datos, importeTc, op }
      );
      return { attempted: true, ok: false };
    }

    const apiTarCfg =
      this.configuration.appConfig?.value?.DatosResult?.AppConfigJsonTarjeta;
    if (apiTarCfg?.TarjetaPeriferico === 'D') {
      console.warn(
        '[PagoService] Reversión TC omitida: periférico tarjeta en modo escritorio.'
      );
      return { attempted: true, ok: false };
    }

    const deviceConf = this.configuration.deviceConfiguration;
    const infoTarjeta = deviceConf?.AppConfigJsonTarjeta;
    if (!infoTarjeta) {
      console.warn('[PagoService] Reversión TC: sin AppConfigJsonTarjeta.');
      return { attempted: true, ok: false };
    }

    const tipoPayment = infoTarjeta.TarjetaTipoComunicacion;
    const operacionVenta = tipoPayment === 'JCC' ? 'Refund' : 'A';
    const importStr = ('' + importeTc).replace('.', ',');
    const impVent = tipoPayment === 'JCC' ? importStr : '0';
    const impVentDev = tipoPayment === 'JCC' ? '0' : importStr;

    const idTpvPad = this.padTpvId(datos.TpvId);
    const numTarjeta = String(op.tarjetaClienteRecibo ?? '');
    const caducidad = String(op.caducidad ?? '');
    const numOperacionOrig = String(op.numOperacion ?? op.pedido ?? '');
    const datosAdicionales = String(op.identificadorRTS ?? '');
    const tpvOrigenStr = String(datos.TpvId ?? '');

    const cardConnection = CardPaymentFactory.getCardPayment(
      pinPadPart !== 0 ? CardPaymentType.PINPAD : CardPaymentType.DATAFONO
    );

    let respuestaPago: string;
    try {
      if (tipoPayment === 'JCC' && cardConnection instanceof PinpadCardPayment) {
        const rrnJcc = String(op.pedido ?? '');
        const authJcc = String(op.codigoRespuesta ?? '');
        respuestaPago = cardConnection.pay(
          idTpvPad,
          '2',
          '0',
          datos.NumAlbaran,
          datos.Identificador,
          operacionVenta,
          tipoPayment,
          impVent,
          impVentDev,
          tpvOrigenStr,
          numTarjeta,
          caducidad,
          numOperacionOrig,
          datosAdicionales,
          infoTarjeta.TarjetaMCPTerminal,
          infoTarjeta.TarjetaMCPVersion,
          infoTarjeta.TarjetaMCPComercio,
          infoTarjeta.TarjetaMCPClave,
          infoTarjeta.TarjetaMCPPuertoCOM,
          '0',
          rrnJcc,
          authJcc
        );
      } else {
        respuestaPago = cardConnection.pay(
          idTpvPad,
          '2',
          '0',
          datos.NumAlbaran,
          datos.Identificador,
          operacionVenta,
          tipoPayment,
          impVent,
          impVentDev,
          tpvOrigenStr,
          numTarjeta,
          caducidad,
          numOperacionOrig,
          datosAdicionales,
          infoTarjeta.TarjetaMCPTerminal,
          infoTarjeta.TarjetaMCPVersion,
          infoTarjeta.TarjetaMCPComercio,
          infoTarjeta.TarjetaMCPClave,
          infoTarjeta.TarjetaMCPPuertoCOM,
          '0'
        );
      }
    } catch (e) {
      console.error('[PagoService] Excepción en reverso TC:', e);
      return { attempted: true, ok: false };
    }

    let okRevert = false;
    if (tipoPayment === 'JCC') {
      try {
        const parsed = this.parseJCCResponseString(respuestaPago);
        okRevert = parsed?.Results?.[0]?.ResponseCode === '00';
      } catch (e) {
        console.error('[PagoService] Reverso JCC: respuesta no válida', e);
        okRevert = false;
      }
    } else {
      const trimmed = (respuestaPago ?? '').trim();
      const tipoRespuesta = trimmed.split(':')[0];
      okRevert = tipoRespuesta === 'OK';
    }

    if (okRevert) {
      this.setOperacionesTarjetaValue(false);
    }

    return { attempted: true, ok: okRevert };
  }

  /**
   * After InsercionVenta fails (HTTP or body without DatosResult), attempt terminal card reversal
   * and show UX toasts (single place for all failure paths).
   */
  private notifyInsertionFailureTerminalReversal(): void {
    const rev = this.tryReverseCardChargeAfterFailedInsertion();
    if (!rev.attempted) {
      return;
    }
    if (rev.ok) {
      this.toastService.success(
        this.translate.instant('INSERCION_VENTA_REVERT_TC_OK'),
        '',
        { toastComponent: SuccessToast, timeOut: 15000 }
      );
    } else {
      this.toastService.error(
        '',
        this.translate.instant('INSERCION_VENTA_REVERT_TC_FALLIDA'),
        { toastComponent: ErrorToast, timeOut: 20000 }
      );
    }
  }

  private padTpvId(tpId: string | undefined): string {
    let s = String(tpId ?? '');
    while (s.length < 4) {
      s = '0' + s;
    }
    return s;
  }

  public get opcionesPagoValue() {
    return this.opcionesPagoSubject.value;
  }

  setOpcionesPagoValue(opcionesPago) {
    this.opcionesPagoSubject.next(opcionesPago);
  }

  public get hayQueFacturarValue() {
    return this.hayQueFacturarSubject.value;
  }

  setHayQueFacturarValue(boolean) {
    this.hayQueFacturarSubject.next(boolean);
  }

  public get hayQueFacturarSimpleValue() {
    return this.hayQueFacturarSimpleSubject.value;
  }

  setHayQueFacturarSimpleValue(boolean) {
    this.hayQueFacturarSimpleSubject.next(boolean);
  }

  public get hayContactosValue() {
    return this.hayContactosSubject.value;
  }

  setHayContactosValue(boolean) {
    this.hayContactosSubject.next(boolean);
  }

  //Enviar mails
  public get sendMailValue() {
    return this.sendMailsSubject.value;
  }

  setSendMailValue(check: boolean) {
    this.sendMailsSubject.next(check);
  }

  public get stringMailValue() {
    return this.stringMailsSubject.value;
  }

  setStringMailValue(cadena: string) {
    this.stringMailsSubject.next(cadena);
  }

  public get sendMailGestionValue() {
    return this.sendMailsGestionSubject.value;
  }

  setSendMailGestionValue(check: boolean) {
    this.sendMailsGestionSubject.next(check);
  }

  public get stringMailGestionValue() {
    return this.stringMailsGestionSubject.value;
  }

  setStringMailGestionValue(cadena: string) {
    this.stringMailsGestionSubject.next(cadena);
  }

  public get noMailValue() {
    return this.noMailSubject.value;
  }

  setNoMailValue(check: boolean) {
    this.noMailSubject.next(check);
  }

  public showEnvioMail = (config?: any): boolean => {
    const configApp = config ? config : this.configuration.deviceConfiguration;
    return configApp &&
      configApp.AppConfigJsonConfig &&
      configApp.AppConfigJsonConfig.EnvioDocPorEmail &&
      configApp.AppConfigJsonConfig.EnvioDocPorEmail == '1'
      ? true
      : false;
  };

  public checkEnvioMail = (config?: any): boolean => {
    const configApp = config ? config : this.configuration.deviceConfiguration;
    return configApp &&
      configApp.AppConfigJsonConfig &&
      configApp.AppConfigJsonConfig.EnvioDocPorEmailPorDefecto &&
      configApp.AppConfigJsonConfig.EnvioDocPorEmailPorDefecto == '1'
      ? true
      : false;
  };

  public cadenaMails = (cadena: string): string => {
    let result = '';
    if (cadena.includes('emails=')) {
      cadena = `&${cadena}`;
      let cadenaComas = '';
      const arrCadena = cadena.split('&emails=');
      arrCadena.forEach((c) => {
        cadenaComas += c.length > 0 ? c + ',' : c;
      });
      result = cadenaComas.slice(0, -1);
    } else {
      result = cadena;
    }
    return result;
  };

  public get hayQueImprimirAlbaranValue() {
    return this.hayQueImprimirAlbaranSubject.value;
  }

  setHayQueImprimirEtiquetasValue(boolean) {
    this.hayQueImprimirEtiquetasSubject.next(boolean);
  }

  public get HayQueImprimirEtiquetasValue() {
    return this.hayQueImprimirEtiquetasSubject.value;
  }

  setHayQueImprimirAlbaranValue(boolean) {
    this.hayQueImprimirAlbaranSubject.next(boolean);
  }

  public get pagoDesglosadoValue() {
    return this.pagoDesglosadoSubject.value;
  }

  setPagoDesglosado(pago: any) {
    this.pagoDesglosadoSubject.next(pago);
  }

  public get formularioCorrectoValue() {
    return this.formularioCorrectoSubject.value;
  }

  setFormularioCorrecto(formulario: Boolean) {
    this.formularioCorrectoSubject.next(formulario);
  }

  resetPagoDesglosado() {
    const valores = {
      prepago: 0,
      transferencia: 0,
      pinpad: 0,
      datafono: 0,
      efectivo: 0,
      monedero: 0, // Yaribel 30102020 Añadimos monedero a pago desglosado
      aplazado: 0,
      sinpe: 0,
    };
    this.pagoDesglosadoSubject.next(valores);
  }

  public get pagoTotalValue() {
    return this.pagoTotalSubject.value;
  }

  setPagoTotal(pagoTotal) {
    this.pagoTotalSubject.next(pagoTotal);
  }

  public get cambioValue() {
    return this.cambioSubject.value;
  }

  setCambio(cambio) {
    this.cambioSubject.next(cambio);
  }

  public get diferenciaValue() {
    return this.diferenciaSubject.value;
  }

  setDiferencia(cambio) {
    this.diferenciaSubject.next(cambio);
  }

  public get pagoSimpeValue() {
    return this.pagoSimpeSubject.value;
  }

  setPagoSimpe(simpe) {
    this.pagoSimpeSubject.next(simpe);
  }

  esVentaCC() {
    return this.pagoDesglosadoValue.pinpad !== 0 ||
      this.pagoDesglosadoValue.datafono !== 0
      ? 1
      : 0;
  }

  /**
   * Resto a cobrar por efectivo/CashKeeper (precioTotal - prepago - tarjeta - otros medios), en céntimos.
   * Usar cuando el gestor está activo y el campo efectivo está vacío (pago mixto tarjeta + CashKeeper).
   */
  getRestoEfectivoCentimos(): number {
    const pd = this.pagoDesglosadoValue || {};
    const importeCC = Number(pd.pinpad ?? 0) + Number(pd.datafono ?? 0);
    const precioTotal = this.venta.carritoValue?.precioTotal ?? 0;
    const precioPrepago = this.venta.carritoValue?.precioPrepago ?? 0;
    const transferencia = Number(pd.transferencia ?? 0);
    const prepago = Number(pd.prepago ?? 0);
    const monedero = Number(pd.monedero ?? 0);
    const aplazado = Number(pd.aplazado ?? 0);
    const simpe = Number(pd.simpe ?? 0);
    const otrasFopSum = (this.otrasFopData.value || []).reduce(
      (s, f) => s + Number((f as any)?.Importe ?? 0),
      0
    );
    const resto =
      precioTotal -
      precioPrepago -
      importeCC -
      transferencia -
      prepago -
      monedero -
      aplazado -
      simpe -
      otrasFopSum;
    return Math.max(0, Math.round(resto * 100));
  }

  calcularTotal() {
    let cantidadTotal = 0;
    this.venta.carritoValue.entradas.forEach((entrada) => {
      if (
        entrada.entrada.TipoProducto === '2' &&
        ('noEditable' in entrada.entrada ||
          entrada.entrada.noEditable === undefined)
      ) {
        if (entrada.entrada.PromocionEntradas && entrada.entrada.PromocionEntradas.length > 0) {
          entrada.entrada.PromocionEntradas.forEach((val) => {
            cantidadTotal =
              cantidadTotal +
              Math.floor(val.NumeroEntradas) * Math.floor(entrada.cantidad);
          });
        } else if (entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0) {
          // Contar las entradas individuales en FarId
          if (entrada.entrada.FarId && entrada.entrada.FarId.length > 0) {
            cantidadTotal = cantidadTotal + entrada.entrada.FarId.length;
          } else {
            // Fallback: contar por NumOrdenPromosConEntradas
            entrada.entrada.NumOrdenPromosConEntradas.forEach((promo) => {
              if (promo.PromocionEntradas && promo.PromocionEntradas.length > 0) {
                promo.PromocionEntradas.forEach((entradaPromo) => {
                  cantidadTotal = cantidadTotal + Math.floor(entradaPromo.NumeroEntradas || 1);
                });
              }
            });
          }
        } else {
          cantidadTotal = cantidadTotal + Math.floor(entrada.cantidad);
        }
      } else if (
        entrada.entrada.TipoProducto === '1' &&
        ('noEditable' in entrada.entrada ||
          entrada.entrada.noEditable === undefined)
      ) {
        cantidadTotal = cantidadTotal + Math.floor(entrada.cantidad);
      }
    });
    return cantidadTotal;
  }

  generarPeticionPago(entradaConjunta) {
    const conexionId = JSON.parse(localStorage.getItem('conexionId'));
    this.conf = this.configuration.appConfig.value;
    const datosInicio = this.configuration.datosInicioValue;
    const test2 = this.generarMediosPagoVenta();
    const test =
      this.conf.DatosResult.AppConfigJsonPago.PagoMultiple != '1'
        ? this.generarMediosPagoVenta().filter(
            (fop) =>
              (fop && fop.Importe > 0) ||
              (fop &&
                fop.Importe == 0 &&
                fop.FOP === 'CA' &&
                this.venta.carritoValue.precioTotal -
                  this.venta.carritoValue.precioPrepago ==
                  0)
          )
        : // .slice(0, 1)
          this.generarMediosPagoVenta().filter(
            (fop) =>
              (fop && fop.Importe > 0) ||
              (fop &&
                fop.Importe == 0 &&
                fop.FOP === 'CA' &&
                this.venta.carritoValue.precioTotal -
                  this.venta.carritoValue.precioPrepago ==
                  0)
          );
    console.log(test2, test);
    const findFop = this.generarMediosPagoVenta().find(
      (mpv) =>
        (mpv && mpv.Importe > 0) ||
        (mpv &&
          mpv.Importe == 0 &&
          mpv.FOP === 'CA' &&
          this.venta.carritoValue.precioTotal -
            this.venta.carritoValue.precioPrepago ==
            0)
    );

    const amigoActivo = this.amigosThyssenService.getAmigoActivo();
    
    // Obtener ClienteId de la configuración de internet
    const clienteIdInternet = this.configuration.internetConfig?.DatosResult?.ClienteId;
    
    // Aplicar la misma lógica de validación que en resumen-compra:
    // CodigoClienteId = "0" OR CodigoClienteId = ClienteId (de TraerDatosInicioInternet)
    const codigoCliente = amigoActivo
      ? '0' // Forzar 0 cuando viene de AMIGOS THYSSEN
      : this.venta.clienteSeleccionadoValue
      ? (() => {
          const clienteCodigo = this.venta.clienteSeleccionadoValue.Codigo;
          // logica original:
          // const clienteCodigoValido =  parseInt(clienteCodigo, 10) < 1;
          const clienteCodigoValido =  parseInt(clienteCodigo, 10) > 0;
          const puedeFacturaSimplificada = 
            clienteCodigoValido || 
            clienteCodigo === clienteIdInternet;
          
          return puedeFacturaSimplificada ? clienteCodigo : '0';
        })()
      : '0';

    // 🔍 [CANJEAR RESERVA] Log antes de construir body de InsercionVenta
    console.log('🔍 [CANJEAR RESERVA] ===== CONSTRUYENDO BODY InsercionVenta =====');
    const tieneReserva = this.venta.carritoValue.reserva && Object.keys(this.venta.carritoValue.reserva).length > 0;
    const tieneLocalizador = this.venta.carritoValue.localizador && Object.keys(this.venta.carritoValue.localizador).length > 0;
    console.log('🔍 [CANJEAR RESERVA] ¿Es canje de reserva?', {
      tieneReserva: tieneReserva,
      tieneLocalizador: tieneLocalizador,
      reservaVendida: this.venta.carritoValue.reserva,
      fechaSeleccionadaValue: this.venta.fechaSeleccionadaValue,
      FechaHoraAcceso: this.venta.carritoValue.FechaHoraAcceso
    });
    
    if (tieneLocalizador) {
      const localizador = this.venta.carritoValue.localizador;
      console.log('🔍 [CANJEAR RESERVA] Datos del localizador:', {
        SinFechaEntrada: localizador.SinFechaEntrada,
        FechaHoraEntrada: localizador.FechaHoraEntrada,
        NumReservaItems: localizador.ReservaItems?.length || 0
      });
    }
    
    // 🔍 [CANJEAR RESERVA] Calcular valores finales que se usarán en el body
    const fechaHoraEntradaFinal = tieneLocalizador && this.venta.carritoValue.localizador.FechaHoraEntrada !== undefined && this.venta.carritoValue.localizador.FechaHoraEntrada !== null
      ? this.venta.carritoValue.localizador.FechaHoraEntrada
      : (this.venta.fechaSeleccionadaValue === ''
        ? ''
        : this.venta.carritoValue.FechaHoraAcceso);
    const sinFechaEntradaFinal = tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && this.venta.carritoValue.localizador.SinFechaEntrada !== null
      ? this.venta.carritoValue.localizador.SinFechaEntrada
      : (this.venta.fechaSeleccionadaValue === '' ? 1 : 0);
    
    console.log('🔍 [CANJEAR RESERVA] Valores finales que se usarán en el body:', {
      SinFechaEntrada: sinFechaEntradaFinal,
      FechaHoraEntrada: fechaHoraEntradaFinal,
      tieneLocalizador: tieneLocalizador,
      heredadoDelLocalizador: tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && this.venta.carritoValue.localizador.SinFechaEntrada !== null
    });
    console.log('🔍 [CANJEAR RESERVA] ===========================================');

    // Medios de pago filtrados (los que se envían): TotalPagar debe coincidir con su suma para evitar DV05
    const mediosPagoFiltrados =
      this.conf.DatosResult.AppConfigJsonPago.PagoMultiple != '1'
        ? this.generarMediosPagoVenta().filter(
            (fop) =>
              (fop && fop.Importe > 0) ||
              (fop &&
                fop.Importe == 0 &&
                fop.FOP === 'CA' &&
                this.venta.carritoValue.precioTotal -
                  this.venta.carritoValue.precioPrepago ==
                  0)
          )
        : this.generarMediosPagoVenta().filter(
            (fop) =>
              (fop && fop.Importe > 0) ||
              (fop &&
                fop.Importe == 0 &&
                fop.FOP === 'CA' &&
                this.venta.carritoValue.precioTotal -
                  this.venta.carritoValue.precioPrepago ==
                  0)
          );
    // Referencia de total de venta (comportamiento previo a fa34a652: TotalPagar venía de precioTotal).
    // Si la suma de medios supera ese total (p. ej. CV nominal 20 € con venta 11,50 €), DV06.
    const totalVentaReferencia =
      Number.parseFloat(
        String(this.venta.carritoValue.precioTotal ?? 0).replace(',', '.')
      ) || 0;
    const sumaImporteMedios = (medios: any[], excluirFop?: string) =>
      medios
        .filter((m) => m && m.FOP && m.FOP !== excluirFop)
        .reduce((s, m) => s + (Number(m.Importe) || 0), 0);
    let mediosPagoParaBody = mediosPagoFiltrados.map((m) =>
      m && typeof m === 'object' ? { ...m } : m
    );
    const cvIdx = mediosPagoParaBody.findIndex((m) => m && m.FOP === 'CV');
    if (cvIdx >= 0) {
      const sumSinCv = sumaImporteMedios(mediosPagoParaBody, 'CV');
      const maxCv = Math.max(0, totalVentaReferencia - sumSinCv);
      const impCv = Number(mediosPagoParaBody[cvIdx].Importe) || 0;
      if (impCv > maxCv) {
        mediosPagoParaBody[cvIdx] = {
          ...mediosPagoParaBody[cvIdx],
          Importe: maxCv,
        };
      }
    }
    const cbIdx = mediosPagoParaBody.findIndex((m) => m && m.FOP === 'CB');
    if (cbIdx >= 0) {
      const sumSinCb = sumaImporteMedios(mediosPagoParaBody, 'CB');
      const maxCb = Math.max(0, totalVentaReferencia - sumSinCb);
      const impCb = Number(mediosPagoParaBody[cbIdx].Importe) || 0;
      if (impCb > maxCb) {
        const filaCb = { ...mediosPagoParaBody[cbIdx], Importe: maxCb };
        const ch = filaCb.Cheques;
        if (ch && Array.isArray(ch) && ch.length === 1) {
          filaCb.Cheques = [{ ...ch[0], Importe: maxCb }];
        }
        mediosPagoParaBody[cbIdx] = filaCb;
      }
    }
    mediosPagoParaBody = mediosPagoParaBody.filter(
      (fop) =>
        (fop && Number(fop.Importe) > 0) ||
        (fop &&
          fop.Importe == 0 &&
          fop.FOP === 'CA' &&
          this.venta.carritoValue.precioTotal -
            this.venta.carritoValue.precioPrepago ==
            0)
    );
    const totalPagar = mediosPagoParaBody.reduce(
      (sum, mp) => sum + (Number(mp.Importe) || 0),
      0
    );

    const caMedio = this.generarMediosPagoVenta().find((mpv) => mpv.FOP === 'CA');
    const caImporteNum = caMedio
      ? Number.parseFloat(String(caMedio.Importe).replace(',', '.')) || 0
      : 0;
    const cambioManualNum =
      Number.parseFloat(String(this.cambioValue ?? 0).replace(',', '.')) || 0;

    let cambioRecibidoNum: number;
    let cambioDevueltoNum: number;
    const ckSnap = this.lastCashKeeperInsercionCents;
    if (ckSnap) {
      this.lastCashKeeperInsercionCents = null;
      cambioRecibidoNum = ckSnap.introducedCents / 100;
      cambioDevueltoNum = ckSnap.returnedCents / 100;
    } else {
      cambioRecibidoNum = caImporteNum + cambioManualNum;
      cambioDevueltoNum = cambioManualNum;
    }

    const body = {
      ConexionIacpos: conexionId,
      esVentaCC: this.esVentaCC(),
      NumAlbaran: this.esVentaCC() ? this.datosTarjeta.NumAlbaran : '',
      Identificador: this.esVentaCC() ? this.datosTarjeta.Identificador : '',
      contadorAlbaranes: {
        TpvId: this.turnoService.turnoValue.TpvId,
      },
      contadorTickets: {
        TpvId: this.turnoService.turnoValue.TpvId,
        NumEntradasAlbaran: this.calcularTotal(),
      },
      contadorOperacionesTarjeta: {
        TpvId: this.turnoService.turnoValue.TpvId,
      },
      CodigoCliente: codigoCliente,
      UsuarioID: this.turnoService.turnoValue.UsuarioId,
      TurnoId: this.turnoService.turnoValue.pkid,
      TPVId: this.turnoService.turnoValue.TpvId,
      TotalEntradas: this.calcularTotal(),
      PrecioBruto: this.venta.carritoValue.precioBase,
      DivisaId: this.turnoService.turnoValue.DivisaId,
      TieneDescuento:
        this.venta.clienteSeleccionadoValue &&
        this.venta.clienteSeleccionadoValue.ConDescuento === 'True'
          ? 1
          : 0,
      Descuento: this.venta.clienteSeleccionadoValue
        ? this.venta.clienteSeleccionadoValue.Dto
        : '',
      GenerarFactura: this.venta.clienteSeleccionadoValue
        ? this.venta.clienteSeleccionadoValue.FP_Facturar
        : '0',
      // cambiar formas de pago
      FOP: findFop ? findFop.FOP : '',
      // : this.venta.clienteSeleccionadoValue ? this.venta.clienteSeleccionadoValue.FP_FOP : 'CA',
      FechaHoraEntrada: fechaHoraEntradaFinal,
      EntradasConMotivo: this.venta.carritoValue.hayDescuentoMotivo ? 1 : 0,
      SinFechaEntrada: sinFechaEntradaFinal,
      EsDevolucion: 0,
      TotalDescuento:
        this.venta.clienteSeleccionadoValue &&
        this.venta.clienteSeleccionadoValue.ConDescuento === 'True'
          ? this.venta.carritoValue.importeDescuentoCliente
          : '',
      FormaPagoId: this.venta.clienteSeleccionadoValue
        ? this.venta.clienteSeleccionadoValue.FP_Id
        : '1',
      TotalIVA: this.venta.carritoValue.ivaTotal,
      TotalPagar: totalPagar,
      CambioRecibido: cambioRecibidoNum,
      CambioDevuelto: cambioDevueltoNum,
      // SIEMPRE EUROS DE MOMENTO
      DivisaRecibidaId: datosInicio.DivisaRefId, // Divisa con lo que han pagado,
      DivisaDevueltaId: datosInicio.DivisaRefId, // Divisa devuelta
      NumAlbaranOrigen: 0,
      Preventa: 0,
      BonoTexto: '',
      GrupoTipoEntradaId: this.venta.carritoValue.entradas[0].entrada.GrupoId,
      Prefijo: this.venta.carritoValue.entradas[0].entrada.Prefijo,
      mediosPago: mediosPagoParaBody,
      entradasVendidas:
        this.venta.carritoValue.reservaAgrupada == '1'
          ? this.generarEntradasVendidasReservaAgrupada(entradaConjunta)
          : this.generarEntradasVendidas(entradaConjunta),
      // entradaConjunta
      //   ? this.generarEntradasVendidasConjunta()
      //   : this.generarEntradasVendidasIndividual(),
      recintosVenta: this.generarRecintosVenta(),
      aforosVenta: this.generarAforosVenta(),
      motivosgrupos: this.generarMotivosGrupos(),
      cajasTurno_cantidad: this.generarCajasTurnoCantidad(),
      resumenTurno: this.generarResumenTurno(totalPagar),
      contactoId: this.contactoId.value || '0',
      reservaVendida: this.venta.carritoValue.reserva,
      idMarcadorExterno: this.venta.idMarcadoExterno,
      operacionesTarjeta:
        this.esVentaCC() === 1 ? this.generarOperacionesTarjeta() : undefined,
      DescuentoClienteAsociadoDatos: this.hayDescuentoValue
        ? this.venta.carritoValue.descuentoClienteAsociadoDatos
        : '',
      GenerarQR: '1',
      OperacionesSINPE: this.generarOperacionesSINPE(),
    };

    // 🔍 [CANJEAR RESERVA] Log del body completo antes de enviar
    console.log('🔍 [CANJEAR RESERVA] ===== BODY COMPLETO InsercionVenta =====');
    console.log('🔍 [CANJEAR RESERVA] Campos relevantes:', {
      SinFechaEntrada: body.SinFechaEntrada,
      FechaHoraEntrada: body.FechaHoraEntrada,
      numEntradasVendidas: body.entradasVendidas?.length || 0,
      reservaVendida: body.reservaVendida
    });
    if (body.entradasVendidas && body.entradasVendidas.length > 0) {
      console.log('🔍 [CANJEAR RESERVA] Muestra de primeras 3 entradasVendidas:', body.entradasVendidas.slice(0, 3).map(e => ({
        TipoEntradaId: e.TipoEntradaId,
        FechaHoraEntrada: e.FechaHoraEntrada,
        SinFechaEntrada: e.SinFechaEntrada
      })));
    }
    console.log('🔍 [CANJEAR RESERVA] ===========================================');
    //
    //
    return body;
  }

  /**
   * Store CashKeeper Introducido/Devuelto (cents) from a successful sale ('N') for InsercionVenta.
   */
  setCashKeeperInsercionCambioFromGestor(
    introducedCents: number,
    returnedCents: number
  ): void {
    this.lastCashKeeperInsercionCents = {
      introducedCents: Math.max(0, Math.round(introducedCents)),
      returnedCents: Math.max(0, Math.round(returnedCents)),
    };
  }

  clearCashKeeperInsercionCambio(): void {
    this.lastCashKeeperInsercionCents = null;
  }

  private generarOperacionesSINPE() {
    const isSinpe = this.sinpeService.getTotalPagar()
      ? Number(this.sinpeService.getTotalPagar()) > 0
      : false;

    if (isSinpe) {
      const sinpesListado = this.sinpeService.getRespuestaVinculacion();
      return sinpesListado.map(({ descripcion, ...rest }) => rest);
    }

    return []; // Retorna un array vacío si no hay total a pagar
  }
  //cuando es pago multiple Operaciones Tarjeta solo debe coger lo de tarjeta
  generarOperacionesTarjeta() {
    const findPago = this.generarMediosPagoVenta().find(
      (fop) => fop && fop.FOP === 'CC'
    );

    const body = {
      FechaHora: moment().format('YYYY/MM/DD HH:mm:ss'),
      Importe:
        this.generarMediosPagoVenta().find((fop) => fop.FOP === 'CC') &&
        this.generarMediosPagoVenta().find((fop) => fop.FOP === 'CC').Importe,
      //this.venta.carritoValue.precioTotal,
      TipoOperacion: this.operacionesTarjetaValue.tipoOperacion || '01',
      NumAutorizacion2oBloque: '',
      CodigoAceptacion: 'AC', // TODO control de otros codigos
      TextoInformativo: this.operacionesTarjetaValue.resultado,
      TextoReservadoControlador: this.operacionesTarjetaValue.codigoRespuesta,
      NumTarjeta: this.operacionesTarjetaValue.tarjetaClienteRecibo,
      FechaCaducidad: this.operacionesTarjetaValue.caducidad,
      NumAutorizacion1erBloque: '',
      FechaConciliacion1erBloque: '',
      FechaConciliacion2oBloque: '',

      TieneLector: this.pagoDesglosadoValue.datafono !== 0 ? '0' : '1',
      LecturaElectronica: this.pagoDesglosadoValue.datafono !== 0 ? '0' : '1',

      PresenciaTitular: '1',
      TarjetaPresente: '1',
      TipoTarjeta: this.operacionesTarjetaValue.etiquetaApp,
      NumOperacion:
        this.operacionesTarjetaValue.numOperacion ||
        this.operacionesTarjetaValue.pedido,
      IdentEstablecimiento2oBloque: this.operacionesTarjetaValue.comercio,
      DatosAdicionales: this.operacionesTarjetaValue.identificadorRTS,
      DatosAdicionalesPrivados: this.operacionesTarjetaValue.terminal,
      CodigoAccion: '',
      PedidoBase: this.operacionesTarjetaValue.pedido,
      CodigoClienteID: '',
      NumReimpresiones: '1',
      IntroduccionManual: '0',
      DivisaId: this.turnoService.turnoValue.DivisaId,
    };
    return body;
  }

  pagar(entradaConjunta) {
    const body = this.generarPeticionPago(entradaConjunta);
    // this.insercionBodyPayload.next(body);
    return this.http
      .post(
        `${this.config.getConfig('API_URL')}api/VentaTaquilla/InsercionVenta`,
        body
      )
      .pipe(
        catchError((error: any) => {
          // Manejo de errores HTTP (500, 400, etc.)
          console.error('Error en insercionVenta:', error);
          let mensajeError = 'Error de servidor';
          if (error.status === 500) {
            mensajeError = 'Error de servidor. Por favor, avise al encargado.';
          } else if (error.status === 400) {
            mensajeError = 'Error en la petición. Por favor, verifique los datos.';
          } else if (error.status === 404) {
            mensajeError = 'Servicio no encontrado. Por favor, avise al encargado.';
          } else if (error.status === 0) {
            mensajeError = 'Error de conexión. Por favor, verifique su conexión.';
          } else if (error.error && error.error.Mensajes && error.error.Mensajes.length > 0) {
            mensajeError = error.error.Mensajes[0].DescripcionMensaje || 'Error en la inserción de la venta';
          } else if (error.message) {
            mensajeError = error.message;
          }

          this.toastService.error('', mensajeError, {
            toastComponent: ErrorToast,
            timeOut: 10000,
          });

          this.notifyInsertionFailureTerminalReversal();

          // Relanzar el error para que el componente pueda manejarlo y detener el flujo
          return throwError(() => error);
        }),
        switchMap((res: any) => {
          // Validar si hay DatosResult, si no, lanzar error
          if (!res.DatosResult) {
            // this.insercionVentaResponse.next(res);
            const mensajeError = res.Mensajes && res.Mensajes.length > 0 
              ? res.Mensajes[0].DescripcionMensaje 
              : 'Error en la inserción de la venta';
            
            this.toastService.error('', mensajeError, {
              toastComponent: ErrorToast,
              timeOut: 885000,
            });

            this.notifyInsertionFailureTerminalReversal();

            // Lanzar error cuando no hay DatosResult para detener el flujo
            return throwError(() => new Error(mensajeError));
          }

          this.setPendingCardReversalAfterFailedInsertion(false);

          if (body.mediosPago.find((x) => x && x.FOP === 'CA'))
            this.abrirCajon();

          return of({
            ...res.DatosResult,
            cambioDevuelto: body.CambioRecibido - body.CambioDevuelto,
            entradas: body.entradasVendidas.length,
          });
        })
      );
  }

  //JS 20201109
  marcarExterno(entradaConjunta) {
    const body = this.generarPeticionPago(entradaConjunta);
    return this.http
      .post(
        `${this.config.getConfig(
          'API_URL'
        )}api/VentaTaquilla/MarcarVentaExterna`,
        body
      )
      .pipe(
        map((res: any) => {
          // ;
          if (!res || !res.DatosResult) {
            return res;
          }
          return res.DatosResult;
        })
      );
  }


  //JS 20191231 Hay que meter los campos de Promotor (MIREYA DOIT )
  /**
   * Genera y descarga los códigos QR y/o archivos PDF para imprimir entradas.
   * @param numAlbaran El número de albarán para identificar las entradas.
   * @param entradas La cantidad de entradas a imprimir.
   * @returns Una promesa que se resuelve con los códigos QR generados.
   */
  impresionQR(
    numAlbaran,
    entradas,
    opciones: OpcionesImpresionQR = {},
    mails?: string
  ) {
    return of(null).pipe(
      switchMap(async () => {
        const currentToken = this.tokenManager.getTaquillaToken();
        if (!currentToken || !currentToken) {
          return throwError('No hay token de sesión disponible');
        }

        const conexionId = JSON.parse(localStorage.getItem('conexionId'));
        const idioma = this.translate.getLocale();
        const idiomaParam = `idioma=${idioma}`;
        const tpvIdParam = opciones.tpvId
          ? `tpvId=${opciones.tpvId}`
          : `tpvId=${this.turnoService.turnoValue.TpvId}`;
        const numAlbaranParam = `numAlbaran=${numAlbaran}`;
        const plataformaParam = 'plataforma=2';
        const tpvIdImpresion = `tpvIdImpresion=${this.turnoService.turnoValue.TpvId}`;
        let paginaReimprimirResult = entradas > 20 ? 1 : 0;
        const usuarioId = `usuarioId=${this.turnoService.turnoValue.UsuarioId}`;

        const headers = {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Credentials': 'true',
          'Access-Control-Allow-Headers': 'Content-Type',
          'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE',
          Authorization: `Bearer ${currentToken}`,
          'IAC-Connection-String': this.config.getConfig('chClaveEmpresa'),
        };
        let url = `${this.config.getConfig(
          'API_URL_TAQUILLA'
        )}/api/Ticket/GenerarPDF?conexionId=${conexionId}&${idiomaParam}&${tpvIdParam}&${numAlbaranParam}&${plataformaParam}&${tpvIdImpresion}&paginaReimprimir=${paginaReimprimirResult}&${usuarioId}`;
        if (opciones.itemsReimprimir) {
          url += `&itemsReimprimir=${opciones.itemsReimprimir}`;
        }
        if (mails) {
          url += `&envioEMail=1&${mails}`;
        }

        let codigosQrs = [];
        let pdfData = [];
        let flag = true;
        while (flag) {
          const data = await this.getToPromise(url, headers);
          if (data && data.datos) {
            codigosQrs.push(data);
            pdfData.push(data.datos);
            if (!mails) {
              try {
                if (this.canPrintPDFWithExternal()) {
                  this.window.external.imprimirPDFBinario(data.datos, 'ticket');
                }
              } catch (error) {
                return throwError(
                  () => new Error('Error al imprimir con external')
                );
              }
            }
            if (data.finImpresion) {
              flag = false;
              if (!mails) {
                if (pdfData.length == 1 && !this.canPrintPDFWithExternal()) {
                  this.downloadPDF(
                    data.datos,
                    `${this.translate.instant('ticket')}.pdf`
                  );
                }
                if (pdfData.length > 1 && !this.canPrintPDFWithExternal()) {
                  this.createZip(pdfData);
                }
              }
            }
            if (!mails) {
              paginaReimprimirResult += 1;
              url = `${this.config.getConfig(
                'API_URL_TAQUILLA'
              )}/api/Ticket/GenerarPDF?conexionId=${conexionId}&${idiomaParam}&${tpvIdParam}&${numAlbaranParam}&${plataformaParam}&${tpvIdImpresion}&paginaReimprimir=${paginaReimprimirResult}&${usuarioId}`;
            } else {
              if (!data.emailEnviado || data.emailEnviado === 0) {
                this.toastService.error(
                  'Error',
                  this.translate.instant('ERROR_MAILS_TICKETS'),
                  {
                    toastComponent: ErrorToast,
                    timeOut: 5000,
                  }
                );
                return throwError(
                  () => new Error('Error al enviar el email')
                );
              } else {
                if (data.emailEnviado && data.emailEnviado === 1) {
                  this.toastService.success(
                    this.translate.instant('SUCCESS_MAILS_TICKETS'),
                    '',
                    {
                      toastComponent: SuccessToast,
                      timeOut: 5000,
                    }
                  );
                }
              }
            }
          }
          if (data === null) {
            flag = false;
            return of(null)
          }
        }
        return of(true)
      }),
      catchError((error) => {
        const message = getErrorMessage(error.status);
        if (mails) {
          this.toastService.error(
            'Error',
            this.translate.instant('ERROR_MAILS_TICKETS'),
            {
              toastComponent: ErrorToast,
              timeOut: 5000,
            }
          );
        } else {
          this.toastService.error(
            'Error',
            this.translate.instant('ERROR_GENERAR_FACTURA_AVISAR_ENCARGADO'),
            {
              toastComponent: ErrorToast,
              timeOut: 5000,
            }
          );
        }

        // return throwError(error);
        return of(null);
      })
    );
  }

  impresionQR20(
    numAlbaran,
    entradas,
    opciones: OpcionesImpresionQR = {},
    mails?: string
  ) {
    return of(null).pipe(
      switchMap(async () => {
        const currentToken = this.tokenManager.getTaquillaToken();
        if (!currentToken || !currentToken) {
          return throwError('No hay token de sesión disponible');
        }

        const conexionId = JSON.parse(localStorage.getItem('conexionId'));
        const idioma = this.translate.getLocale();
        const idiomaParam = `idioma=${idioma}`;
        const tpvIdParam = opciones.tpvId
          ? `tpvId=${opciones.tpvId}`
          : `tpvId=${this.turnoService.turnoValue.TpvId}`;
        const numAlbaranParam = `numAlbaran=${numAlbaran}`;
        const plataformaParam = 'plataforma=2';
        const tpvIdImpresion = `tpvIdImpresion=${this.turnoService.turnoValue.TpvId}`;
        const paginaReimprimirResult = 'paginaReimprimir=0';
        const usuarioId = `usuarioId=${this.turnoService.turnoValue.UsuarioId}`;

        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
          'IAC-Connection-String': this.config.getConfig('chClaveEmpresa'),
        };

        const ticketsArray = opciones.itemsReimprimir.split(',') || [];
        const totalPaginas = Math.ceil(ticketsArray.length / 10); // Calcula el número total de páginas
        let codigosQrs = [];
        let pdfData = [];

        for (let i = 0; i < totalPaginas; i++) {
          const itemsReimprimir = ticketsArray
            .slice(i * 10, (i + 1) * 10)
            .join(',');

          let url = `${this.config.getConfig(
            'API_URL_TAQUILLA'
          )}/api/Ticket/GenerarPDF?conexionId=${conexionId}&${idiomaParam}&${tpvIdParam}&${numAlbaranParam}&${plataformaParam}&${tpvIdImpresion}&${paginaReimprimirResult}&${usuarioId}`;
          if (itemsReimprimir) {
            url += `&itemsReimprimir=${itemsReimprimir}`;
          }
          if (mails) {
            url += `&envioEMail=1&${mails}`;
          }

          const data = await this.getToPromise(url, headers);
          if (data && data.datos) {
            codigosQrs.push(data);
            pdfData.push(data.datos);
            if (!mails) {
              try {
                if (this.canPrintPDFWithExternal()) {
                  this.window.external.imprimirPDFBinario(data.datos, 'ticket');
                }
              } catch (error) {
                return throwError(
                  () => new Error('Error al imprimir con external')
                );
              }

              if (i === totalPaginas - 1 && !this.canPrintPDFWithExternal()) {
                this.createZip(pdfData);
              }
            } else {
              if (!data.emailEnviado || data.emailEnviado === 0) {
                this.toastService.error(
                  'Error',
                  this.translate.instant('ERROR_MAILS_TICKETS'),
                  {
                    toastComponent: ErrorToast,
                    timeOut: 5000,
                  }
                );
              }

              if (data.emailEnviado && data.emailEnviado === 1) {
                this.mailsTicket = true;
                this.toastService.success(
                  this.translate.instant('SUCCESS_MAILS_TICKETS'),
                  '',
                  {
                    toastComponent: SuccessToast,
                    timeOut: 5000,
                  }
                );
              }
            }
          }
        }

        return codigosQrs;
      }),
      catchError((error) => {
        const message = getErrorMessage(error.status);
        if (mails) {
          this.toastService.error(
            'Error',
            this.translate.instant('ERROR_MAILS_TICKETS'),
            {
              toastComponent: ErrorToast,
              timeOut: 5000,
            }
          );
        } else {
          this.toastService.error(
            'Error',
            this.translate.instant('ERROR_GENERAR_FACTURA_AVISAR_ENCARGADO'),
            {
              toastComponent: ErrorToast,
              timeOut: 5000,
            }
          );
        }
        // return throwError(error);
        return of(null);
      })
    );
  }
  /**
   * Método para imprimir un localizador junto con sus entradas.
   * @param localizador - El localizador que se imprimirá.
   * @param entradas - El número de entradas asociadas al localizador.
   * @returns Una promesa que se resuelve con los códigos QR generados para las entradas.
   */
  impresionLocalizador(localizador, entradas, mails?: string) {
    return of(null).pipe(
      switchMap(async () => {
        const currentToken = this.tokenManager.getTaquillaToken();
        if (!currentToken || !currentToken) {
          return throwError('No hay token de sesión disponible');
        }

        const conexionId = JSON.parse(localStorage.getItem('conexionId'));
        const idioma = this.translate.getLocale();
        const idiomaParam = `idioma=${idioma}`;
        const tpvIdParam = `tpvId=${this.turnoService.turnoValue.TpvId}`;
        const localizadorParam = `localizador=${localizador}`;
        const plataformaParam = 'plataforma=1';
        const usuarioBD = JSON.parse(localStorage.getItem('currentUser'));
        const tpvIdImpresion = `tpvIdImpresion=${this.turnoService.turnoValue.TpvId}`;
        let paginaReimprimirResult = entradas > 20 ? 1 : 0;
        const usuarioId = `usuarioId=${this.turnoService.turnoValue.UsuarioId}`;

        const headers = {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Credentials': 'true',
          'Access-Control-Allow-Headers': 'Content-Type',
          'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE',
          Authorization: `Bearer ${currentToken}`,
          'IAC-Connection-String': this.config.getConfig('chClaveEmpresa'),
        };
        let url = `${this.config.getConfig(
          'API_URL_TAQUILLA'
        )}/api/Ticket/GenerarPDF?conexionId=${conexionId}&${idiomaParam}&${tpvIdParam}&${localizadorParam}&${plataformaParam}&${tpvIdImpresion}&paginaReimprimir=${paginaReimprimirResult}&usuarioBD=${usuarioBD}&${usuarioId}`;

        if (mails) {
          url += `&envioEMail=1&${mails}`;
        }

        let codigosQrs = [];
        let pdfData = [];
        let flag = true;
        while (flag) {
          await this.getToPromise(url, headers).then((data) => {
            if (data && data.datos) {
              codigosQrs.push(data);
              pdfData.push(data.datos);

              if (!mails) {
                if (this.canPrintPDFWithExternal())
                  this.window.external.imprimirPDFBinario(data.datos, 'ticket');
                if (data.finImpresion) {
                  flag = false;
                  //Si el tamaño del pdfData es mayor a uno se descargara un .zip
                  if (pdfData.length == 1 && !this.canPrintPDFWithExternal())
                    this.downloadPDF(
                      data.datos,
                      `${this.translate.instant('ticket')}.pdf`
                    );
                  if (pdfData.length > 1 && !this.canPrintPDFWithExternal())
                    this.createZip(pdfData);
                }
                paginaReimprimirResult += 1;
                url = `${this.config.getConfig(
                  'API_URL_TAQUILLA'
                )}/api/Ticket/GenerarPDF?conexionId=${conexionId}&${idiomaParam}&${tpvIdParam}&${localizadorParam}&${plataformaParam}&${tpvIdImpresion}&paginaReimprimir=${paginaReimprimirResult}&usuarioBD=${usuarioBD}&${usuarioId}`;
              } else {
                if (data.finImpresion) flag = false;

                if (!data.emailEnviado || data.emailEnviado === 0) {
                  this.toastService.error(
                    'Error',
                    this.translate.instant('ERROR_MAILS_TICKETS'),
                    {
                      toastComponent: ErrorToast,
                      timeOut: 5000,
                    }
                  );
                }

                if (data.emailEnviado && data.emailEnviado === 1) {
                  this.mailsTicket = true;
                  this.toastService.success(
                    this.translate.instant('SUCCESS_MAILS_TICKETS'),
                    '',
                    {
                      toastComponent: SuccessToast,
                      timeOut: 5000,
                    }
                  );
                }
              }
            }

            if (data === null) flag = false;
          });
        }
        return codigosQrs;
      }),
      catchError((error) => {
        /* this.toastService.error('Error', this.translate.instant('ERROR_AVISAR_ENCARGADO'), {
          toastComponent: ErrorToast,
          timeOut: 5000,
        }); */
        // return throwError(error);
        return of(null);
      })
    );
  }

  impresionLocalizador20(
    localizador: string,
    entradas: number,
    opciones: OpcionesImpresionQR = {}
  ) {
    return of(null).pipe(
      switchMap(async () => {
        const currentToken = this.tokenManager.getTaquillaToken();
        if (!currentToken || !currentToken) {
          return throwError('No hay token de sesión disponible');
        }

        const conexionId = JSON.parse(localStorage.getItem('conexionId'));
        const idioma = this.translate.getLocale();
        const idiomaParam = `idioma=${idioma}`;
        const tpvIdParam = opciones.tpvId
          ? `tpvId=${opciones.tpvId}`
          : `tpvId=${this.turnoService.turnoValue.TpvId}`;
        const localizadorParam = `localizador=${localizador}`;
        const plataformaParam = 'plataforma=1';
        const usuarioBD = JSON.parse(localStorage.getItem('currentUser'));
        const tpvIdImpresion = `tpvIdImpresion=${this.turnoService.turnoValue.TpvId}`;
        const paginaReimprimirResult = 'paginaReimprimir=0';
        const usuarioId = `usuarioId=${this.turnoService.turnoValue.UsuarioId}`;

        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        };

        const ticketsArray = opciones.itemsReimprimir.split(',') || [];
        const totalPaginas = Math.ceil(ticketsArray.length / 20); // Calcula el número total de páginas
        let codigosQrs = [];
        let pdfData = [];

        for (let i = 0; i < totalPaginas; i++) {
          const itemsReimprimir = ticketsArray
            .slice(i * 20, (i + 1) * 20)
            .join(',');

          let url = `${this.config.getConfig(
            'API_URL_TAQUILLA'
          )}/api/Ticket/GenerarPDF?conexionId=${conexionId}&${idiomaParam}&${tpvIdParam}&${localizadorParam}&${plataformaParam}&${tpvIdImpresion}&${paginaReimprimirResult}&usuarioBD=${usuarioBD}&${usuarioId}`;

          if (itemsReimprimir) {
            url += `&itemsReimprimir=${itemsReimprimir}`;
          }

          const data = await this.getToPromise(url, headers);
          if (data && data.datos) {
            codigosQrs.push(data);
            pdfData.push(data.datos);

            try {
              if (this.canPrintPDFWithExternal()) {
                this.window.external.imprimirPDFBinario(data.datos, 'ticket');
              }
            } catch (error) {
              return throwError(
                () => new Error('Error al imprimir con external')
              );
            }

            if (i === totalPaginas - 1 && !this.canPrintPDFWithExternal()) {
              this.createZip(pdfData);
            }
          }
        }

        return codigosQrs;
      }),
      catchError((error) => {
        const message = getErrorMessage(error.status);
        this.toastService.error(
          'Error',
          this.translate.instant('ERROR_AVISAR_ENCARGADO'),
          {
            toastComponent: ErrorToast,
            timeOut: 5000,
          }
        );
        return throwError(error);
      })
    );
  }

  impresionJustificante(
    numAlbaran,
    nombreIdentificador,
    opciones: OpcionesImpresionQR = {},
    mails?: string
  ) {
    const currentToken = this.tokenManager.getTaquillaToken();
    if (!currentToken || !currentToken) {
      return throwError('No hay token de sesión disponible');
    }

    const conexionId = JSON.parse(localStorage.getItem('conexionId'));
    const idioma = this.translate.getLocale();
    const idiomaParam = `idioma=${idioma}`;
    const tpvIdParam = opciones.tpvId
      ? `tpvId=${opciones.tpvId}`
      : `tpvId=${this.turnoService.turnoValue.TpvId}`;
    const numAlbaranParam = `numAlbaran=${numAlbaran}`;
    const plataformaParam = 'plataforma=2';
    const usuarioId = `usuarioId=${this.turnoService.turnoValue.UsuarioId}`;

    const headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Credentials': 'true',
      'Access-Control-Allow-Headers': 'Content-Type',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE',
      Authorization: `Bearer ${currentToken}`,
      'IAC-Connection-String': this.config.getConfig('chClaveEmpresa'),
    };

    let url = `${this.config.getConfig(
      'API_URL_TAQUILLA'
    )}/api/Justificante/GenerarPDF?conexionId=${conexionId}&${idiomaParam}&${tpvIdParam}&${numAlbaranParam}&${plataformaParam}&${usuarioId}`;

    if (opciones.tpvImpresion) {
      url += `&tpvIdImpresion=${opciones.tpvImpresion}`;
    }
    if (mails) {
      url += `&envioEMail=1&${mails}`;
    }

    
    return this.http.get(url, { headers: headers, responseType: 'json' }).pipe(
      tap((data: any) => {
        if (data && data.datos) {
          if (!mails) {
            if (this.canPrintPDFWithExternal()) {
              this.window.external.imprimirPDFBinario(
                data.datos,
                'justificante'
              );
            }
            if (!this.canPrintPDFWithExternal()) {
              const nombreArchivo = `${nombreIdentificador}.pdf`;
              this.downloadPDF(data.datos, nombreArchivo);
            }
          } else {
            if (!data.emailEnviado || data.emailEnviado === 0) {
              this.toastService.error(
                'Error',
                this.translate.instant('ERROR_MAILS_JUSTIFICANTE'),
                {
                  toastComponent: ErrorToast,
                  timeOut: 5000,
                }
              );
            }

            if (data.emailEnviado && data.emailEnviado === 1) {
              this.mailsJustificante = true;
              this.toastService.success(
                this.translate.instant('SUCCESS_MAILS_JUSTIFICANTE'),
                '',
                {
                  toastComponent: SuccessToast,
                  timeOut: 5000,
                }
              );
            }
          }
        }
      }),
      catchError((error) => {
        if (mails) {
          this.toastService.error(
            'Error',
            this.translate.instant('ERROR_MAILS_JUSTIFICANTE'),
            {
              toastComponent: ErrorToast,
              timeOut: 5000,
            }
          );
        } else {
          this.toastService.error(
            'Error',
            this.translate.instant('ERROR_AVISAR_ENCARGADO'),
            {
              toastComponent: ErrorToast,
              timeOut: 5000,
            }
          );
        }
        return throwError(error);
      })
    );
  }

  /**
   * Crea un archivo ZIP que contiene archivos PDF a partir de una matriz de cadenas base64.
   * @param data Una matriz de cadenas base64 que representan los datos de los archivos PDF.
   * @returns Una promesa que se resuelve con el archivo ZIP generado.
   */
  async createZip(data: string[]) {
    const zip = new JSZip();
    data.forEach((base64, index) => {
      zip.file(
        `${this.translate.instant('ticket')}-${index + 1}.pdf`,
        this.createFilePdf(base64, 'application/pdf')
      );
    });
    zip.generateAsync({ type: 'blob' }).then(function (content) {
      // see FileSaver.js
      const blobUrl = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'tickets.zip';
      link.click();
      URL.revokeObjectURL(blobUrl);
    });
  }

  /**
   * Realiza una solicitud HTTP GET y devuelve una promesa que se resuelve con los datos de la respuesta.
   * @param url La URL a la que se realizará la solicitud GET.
   * @param headers Los encabezados HTTP que se enviarán con la solicitud.
   * @returns Una promesa que se resuelve con los datos de la respuesta HTTP.
   */
  getToPromise(url: string, headers): Promise<any> {
    return this.http
      .get(url, { headers: headers, observe: 'response' }) // 'observe' para obtener la respuesta completa
      .pipe(
        map((res: any) => {
          if (!res) {
            throw new Error('Response undefined');
          }
          return res.body;
        }),
        catchError((error) => {
          const statusMatch = error.match(/Error:\s(\d+)/);
          const statusCode = statusMatch ? parseInt(statusMatch[1], 10) : null;
          const customError = {
            status: statusCode,
            message: error.message,
          };
          return throwError(customError);
        })
      )
      .toPromise(); // Convertir el observable en una promesa
  }

  /**
   * Prints a PDF from a base64 string. If external printing is available,
   * it uses the external printing function; otherwise, it downloads the PDF.
   *
   * @param base64 - The base64 encoded string of the PDF.
   * @param fileName - The name of the file to be downloaded. Defaults to 'ticket.pdf'.
   */
  printPDFWithBase64(base64: string, fileName: string = 'ticket.pdf') {
    if (this.canPrintPDFWithExternal()) {
      this.window.external.imprimirPDFBinario(base64, 'ticket');
      //this.imprimirPDFBinario(base64);
    } else {
      this.downloadPDF(base64, fileName);
    }
  }

  /**
   * Creates a PDF file from a base64 string and triggers a download.
   *
   * @param base64 - The base64 encoded string of the PDF.
   * @param fileName - The name of the file to be downloaded.
   */
  downloadPDF(base64: string, fileName: string) {
    const blob = this.createFilePdf(base64, 'application/pdf');
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(blobUrl);
  }

  /**
   * Crea un objeto Blob a partir de una cadena base64 y un tipo de archivo.
   * @param base64 La cadena en formato base64 que representa los datos del archivo.
   * @param type El tipo MIME del archivo, por ejemplo: 'application/pdf'.
   * @returns Un objeto Blob que representa el archivo generado.
   */
  createFilePdf(base64: string, type: string): Blob {
    const binaryData = atob(base64);
    const arrayBuffer = new ArrayBuffer(binaryData.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < binaryData.length; i++) {
      uint8Array[i] = binaryData.charCodeAt(i) & 0xff;
    }
    return new Blob([arrayBuffer], { type });
  }

  /**
   * Checks if the external printing function is available.
   *
   * @returns A boolean indicating whether the external printing function is available.
   */
  canPrintPDFWithExternal(): boolean {
    return (
      this.window.external &&
      typeof this.window.external.imprimirPDFBinario === 'function'
    );
  }

  /**
   * Encodes a base64 string to a binary string using window.btoa.
   *
   * @param base64 - The base64 encoded string.
   * @returns The binary string encoded from the base64 input.
   */
  imprimirPDFBinario(base64: string): string {
    return window.btoa(base64);
  }



  impresionTarjeta(nombreDocumento: string, mails?: string) {
    const currentToken = this.tokenManager.getTaquillaToken();
    if (!currentToken || !currentToken) {
      return throwError('No hay token de sesión disponible');
    }

    const conexionId = JSON.parse(localStorage.getItem('conexionId'));
    const idioma = this.translate.getLocale();
    const idiomaParam = `idioma=${idioma}`;
    const tpvIdParam = `tpvId=${this.turnoService.turnoValue.TpvId}`;
    const numAlbaranParam = `numAlbaran=${this.datosTarjeta.NumAlbaran}`;
    const plataformaParam = 'plataforma=2';
    const usuarioId = `usuarioId=${this.turnoService.turnoValue.UsuarioId}`;

    const headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Credentials': 'true',
      'Access-Control-Allow-Headers': 'Content-Type',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE',
      Authorization: `Bearer ${currentToken}`,
    };

        let url = `${this.config.getConfig(
          'API_URL_TAQUILLA'
        )}/api/Justificante/GenerarTarjetaPDF?conexionId=${conexionId}&${idiomaParam}&${tpvIdParam}&${numAlbaranParam}&${plataformaParam}&${usuarioId}`;

        if (mails) {
          url += `&envioEMail=1&${mails}`;
        }

    return this.http
      .get(url, { headers: headers, responseType: 'json' })
      .pipe(
        map(
          (response: { datos: string; emailEnviado: number }) => response
        ),
        tap((pdfBase64) => {
          if (!mails) {
            this.printPDFTarjeta(pdfBase64.datos, nombreDocumento);
          } else {
            if (
              !pdfBase64 ||
              !pdfBase64.emailEnviado ||
              pdfBase64.emailEnviado === 0
            ) {
              this.toastService.error(
                'Error',
                this.translate.instant('ERROR_MAILS_TARJETA'),
                {
                  toastComponent: ErrorToast,
                  timeOut: 5000,
                }
              );
            } else {
              if (pdfBase64.emailEnviado && pdfBase64.emailEnviado === 1) {
                this.toastService.success(
                  this.translate.instant('SUCCESS_MAILS_TARJETA'),
                  '',
                  {
                    toastComponent: SuccessToast,
                    timeOut: 5000,
                  }
                );
              }
            }
          }
        }),
        catchError((error) => {
          const statusMatch = error.match(/Error:\s(\d+)/);
          const statusCode = statusMatch ? parseInt(statusMatch[1], 10) : null;

          const customError = {
            status: statusCode,
            message: error.message,
          };
          if (mails) {
            this.toastService.error(
              'Error',
              this.translate.instant('ERROR_MAILS_TARJETA'),
              {
                toastComponent: ErrorToast,
                timeOut: 5000,
              }
            );
          } else {
            this.toastService.error(
              'Error',
              this.translate.instant('ERROR_AVISAR_ENCARGADO'),
              {
                toastComponent: ErrorToast,
                timeOut: 5000,
              }
            );
          }
          return throwError(error);
        })
      );
  }

  printPDFTarjeta(base64: string, fileName: string = 'justificante.pdf') {
    if (
      this.window.external &&
      typeof this.window.external.imprimirPDFBinario === 'function'
    ) {
      this.window.external.imprimirPDFBinario(base64, 'justificante');
      //return window.btoa(base64);
    } else {
      const binaryData = atob(base64);
      const arrayBuffer = new ArrayBuffer(binaryData.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i) & 0xff;
      }

      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });

      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(blobUrl);
    }
  }

  private generarMediosPagoVenta(): any[] {
    const pd = this.pagoDesglosadoValue || {};
    const importeCC =
      Number(pd.pinpad ?? 0) + Number(pd.datafono ?? 0);
    const efectivoNum = Number(pd.efectivo ?? 0);
    const gestorActivo =
      this.conf?.DatosResult?.AppConfigJsonPago?.GestorEfectivo?.GestorActivo === '1';
    const precioTotal = this.venta.carritoValue?.precioTotal ?? 0;
    const precioPrepago = this.venta.carritoValue?.precioPrepago ?? 0;

    let importeCA: number;
    if (gestorActivo && efectivoNum === 0) {
      const transferencia = Number(pd.transferencia ?? 0);
      const prepago = Number(pd.prepago ?? 0);
      const monedero = Number(pd.monedero ?? 0);
      const aplazado = Number(pd.aplazado ?? 0);
      const simpe = Number(pd.simpe ?? 0);
      const otrasFopSum = (this.otrasFopData.value || []).reduce(
        (s, f) => s + Number((f as any)?.Importe ?? 0),
        0
      );
      const cv = this.cVaccanceData.value;
      const importeCV =
        cv && cv.FOP === 'CV'
          ? Number.parseFloat(String(cv.Importe ?? 0).replace(',', '.')) || 0
          : 0;
      const restoEfectivo =
        precioTotal -
        precioPrepago -
        importeCC -
        transferencia -
        prepago -
        monedero -
        aplazado -
        simpe -
        otrasFopSum -
        importeCV;
      importeCA = Math.max(0, restoEfectivo);
    } else {
      importeCA = efectivoNum - Number(this.cambioValue ?? 0);
    }

    return [
      {
        FOP: 'CC',
        Importe: importeCC,
      },
      {
        FOP: 'CA',
        Importe: Number(importeCA.toFixed(2)),
      },
      {
        FOP: 'TR',
        Importe: this.pagoDesglosadoValue.transferencia,
      },
      {
        FOP: 'PR', // Yaribel 20201217 modificamos la forma de pago IN por PR
        Importe: this.pagoDesglosadoValue.prepago,
      },
      // Yaribel 30102020 añadimos medio de pago TM
      {
        FOP: 'TM',
        Importe: this.pagoDesglosadoValue.monedero,
        TarjetaMonederoCodigo: this.codigoTarjetaMonederoValue,
      },
      // Yaribel 30102020 fin de añadir medio de pago
      {
        FOP: 'DP',
        Importe: this.pagoDesglosadoValue.aplazado,
      },
      {
        FOP: 'IN', // Yaribel 20201217 añadimos la forma de pago IN para la validación con las formas de pago de CajaTurno
        Importe: 0,
      },
      {
        FOP: 'SP',
        Importe: this.pagoDesglosadoValue.simpe,
      },

      ...this.otrasFopData.value,
      this.cVaccanceData.value,
      // {
      //   FOP: "CV",
      // }
    ];
  }

  private generarEntradasVendidas(entradaConjunta): any {
    let promocionNumOrden = 0;
    let contadorVisitantesHorarios = 0;
    let _this = this;
    const datosInicio = this.configuration.datosInicioValue;
    const entradas = [];
    const tipoEntradaIdAgrupadas: Agrupados[] = [];
    //para modificar con procedencias
    let procedencias = this.procedenciaService.getprocedenciasValue();
    let numProductoProcededncia = 0;
    let numEntradaProductoProcededncia = 0;
    let numEntradaCantidadProcededncia = 0;
    let totalProductosProcededncia = 1;
    let countEntrada = 0;
    const isVentanaSinFechaChecked = this.venta.isVentaSinFechaChecked;
    const tieneLocalizador = this.venta.carritoValue.localizador && Object.keys(this.venta.carritoValue.localizador).length > 0;
    this.venta.carritoValue.entradas.forEach((entrada) => {
      // ;
      if (
        entrada.entrada.noEditable === undefined ||
        entrada.entrada.noEditable === null ||
        entrada.entrada.noEditable === false
      ) {
        if (entrada.entrada.TipoProducto === '2') {
          let contadorNominacion = 0;
          numEntradaProductoProcededncia = 0;
          for (let i = 0; i < entrada.cantidad; i++) {
            promocionNumOrden++;
            // PrecioPagado = base (sin restar comisión) para que el API sume (PrecioPagado+Comision)=total y coincida con TotalPagar (DV06)
            let precioPagado = Number.parseFloat(
              entrada.entrada.PrecioEnDivisaReferencia.replace(',', '.')
            );
            let precioPagadoRedondeado = Math.round(precioPagado * 100) / 100;

            // 🔍 [CANJEAR RESERVA] Buscar ReservaItem correspondiente para promociones (no agrupada)
            let reservaItem = null;
            let fechaHoraEntradaReserva = '';
            let sinFechaEntradaReserva = null;
            if (tieneLocalizador && this.venta.carritoValue.localizador.ReservaItems) {
              reservaItem = this.venta.carritoValue.localizador.ReservaItems.find(
                item => item.ItemId === entrada.entrada.ItemId || 
                        item.ItemId === entrada.entrada.TipoPromocionId
              );
              if (reservaItem) {
                // 🔍 [CANJEAR RESERVA] Obtener valores de PosicionesVenta[0] si existe (nuevo cambio: ahora vienen de PosicionesVenta)
                const posicionVenta = reservaItem.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
                  ? reservaItem.PosicionesVenta[0] 
                  : null;
                
                // 🔍 [CANJEAR RESERVA] Log detallado de la estructura de PosicionesVenta
                if (tieneLocalizador) {
                  console.log(`🔍 [CANJEAR RESERVA] [NORMAL-PROMO] Análisis de PosicionesVenta (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                    tienePosicionesVenta: !!reservaItem.PosicionesVenta,
                    cantidadPosicionesVenta: reservaItem.PosicionesVenta?.length || 0,
                    posicionVentaEncontrada: !!posicionVenta,
                    posicionVentaCompleta: posicionVenta,
                    todasLasPosicionesVenta: reservaItem.PosicionesVenta?.map((pv, idx) => ({
                      indice: idx,
                      SinFechaEntrada: pv?.SinFechaEntrada || pv?.sinFechaEntrada,
                      sinFechaEntrada: pv?.sinFechaEntrada,
                      FechaHoraEntrada: pv?.FechaHoraEntrada || pv?.fechaEntrada,
                      fechaEntrada: pv?.fechaEntrada,
                      PosicionVenta: pv?.PosicionVenta
                    }))
                  });
                }
                
                // Heredar FechaHoraEntrada de PosicionesVenta[0] si existe (buscar tanto FechaHoraEntrada como fechaEntrada)
                const fechaHoraPosicionVenta = posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada;
                fechaHoraEntradaReserva = posicionVenta && fechaHoraPosicionVenta && fechaHoraPosicionVenta !== '' && fechaHoraPosicionVenta !== null
                  ? moment(fechaHoraPosicionVenta).isValid()
                    ? moment(fechaHoraPosicionVenta).format('YYYY/MM/DD HH:mm:ss')
                    : fechaHoraPosicionVenta
                  : (reservaItem.FechaHoraEntrada && reservaItem.FechaHoraEntrada !== '' && reservaItem.FechaHoraEntrada !== null
                    ? moment(reservaItem.FechaHoraEntrada).isValid()
                      ? moment(reservaItem.FechaHoraEntrada).format('YYYY/MM/DD HH:mm:ss')
                      : reservaItem.FechaHoraEntrada
                    : (entrada.entrada.FechaHoraEntrada || entrada.entrada.Fecha || ''));
                
                // Heredar sinFechaEntrada de PosicionesVenta[0] si existe (buscar tanto SinFechaEntrada como sinFechaEntrada, y convertir número a string)
                const sinFechaPosicionVenta = posicionVenta?.SinFechaEntrada !== undefined ? posicionVenta.SinFechaEntrada : 
                                             (posicionVenta?.sinFechaEntrada !== undefined ? posicionVenta.sinFechaEntrada : undefined);
                const sinFechaPosicionVentaStr = sinFechaPosicionVenta !== undefined && sinFechaPosicionVenta !== null 
                  ? String(sinFechaPosicionVenta) 
                  : '';
                
                let fuenteSinFechaEntrada = 'N/A';
                let razonDecision = '';
                
                if (posicionVenta && sinFechaPosicionVentaStr !== '') {
                  sinFechaEntradaReserva = sinFechaPosicionVentaStr;
                  fuenteSinFechaEntrada = 'PosicionesVenta[0] (explícito)';
                  razonDecision = 'Valor explícito encontrado en PosicionesVenta[0]';
                } else if (reservaItem['sinFechaEntrada'] !== undefined && reservaItem['sinFechaEntrada'] !== null && reservaItem['sinFechaEntrada'] !== '') {
                  // Fallback al ReservaItem si PosicionesVenta no tiene el valor
                  sinFechaEntradaReserva = reservaItem['sinFechaEntrada'];
                  fuenteSinFechaEntrada = 'ReservaItem (fallback)';
                  razonDecision = 'PosicionesVenta[0] no tiene valor, usando ReservaItem como fallback';
                } else {
                  // Inferir de FechaHoraEntrada (priorizar PosicionesVenta, luego ReservaItem)
                  const fechaHoraParaInferir = fechaHoraPosicionVenta || reservaItem.FechaHoraEntrada;
                  if (!fechaHoraParaInferir || fechaHoraParaInferir === '' || fechaHoraParaInferir === null) {
                    sinFechaEntradaReserva = '1'; // Sin fecha
                    fuenteSinFechaEntrada = 'Inferido (sin fecha)';
                    razonDecision = 'FechaHoraEntrada vacío/null → SinFechaEntrada = 1';
                  } else {
                    // Si tiene fecha pero el localizador dice sin fecha, priorizar el localizador
                    if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && 
                        this.venta.carritoValue.localizador.SinFechaEntrada !== null &&
                        this.venta.carritoValue.localizador.SinFechaEntrada === '1') {
                      sinFechaEntradaReserva = '1'; // Usar el del localizador (sin fecha)
                      fuenteSinFechaEntrada = 'Localizador (conflicto resuelto)';
                      razonDecision = 'Conflicto: ReservaItem tiene fecha pero localizador dice sin fecha → prioriza localizador';
                    } else {
                      sinFechaEntradaReserva = '0'; // Con fecha
                      fuenteSinFechaEntrada = 'Inferido (con fecha)';
                      razonDecision = 'FechaHoraEntrada tiene valor → SinFechaEntrada = 0';
                    }
                  }
                }
                
                // 🔍 [CANJEAR RESERVA] Log detallado del proceso de decisión
                if (tieneLocalizador) {
                  console.log(`🔍 [CANJEAR RESERVA] [NORMAL-PROMO] Proceso de decisión SinFechaEntrada (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                    fuente: fuenteSinFechaEntrada,
                    razon: razonDecision,
                    valorFinal: sinFechaEntradaReserva,
                    tienePosicionVenta: !!posicionVenta,
                    posicionVentaSinFechaEntrada: posicionVenta?.SinFechaEntrada || posicionVenta?.sinFechaEntrada,
                    posicionVentaFechaHoraEntrada: posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada,
                    sinFechaPosicionVentaStr: sinFechaPosicionVentaStr,
                    reservaItemSinFechaEntrada: reservaItem['sinFechaEntrada'],
                    reservaItemFechaHoraEntrada: reservaItem.FechaHoraEntrada,
                    fechaHoraParaInferir: fechaHoraPosicionVenta || reservaItem.FechaHoraEntrada,
                    localizadorSinFechaEntrada: this.venta.carritoValue.localizador.SinFechaEntrada,
                    hayConflicto: (fechaHoraPosicionVenta || reservaItem.FechaHoraEntrada) && 
                                  this.venta.carritoValue.localizador.SinFechaEntrada === '1'
                  });
                }
              } else if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && 
                         this.venta.carritoValue.localizador.SinFechaEntrada !== null) {
                // Si no se encuentra el ReservaItem, usar el del localizador principal
                sinFechaEntradaReserva = this.venta.carritoValue.localizador.SinFechaEntrada;
                fechaHoraEntradaReserva = this.venta.carritoValue.localizador.FechaHoraEntrada || entrada.entrada.FechaHoraEntrada || entrada.entrada.Fecha || '';
                
                // 🔍 [CANJEAR RESERVA] Log cuando NO se encuentra ReservaItem
                console.log(`🔍 [CANJEAR RESERVA] [NORMAL-PROMO] ⚠️ ReservaItem NO encontrado (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                  itemIdBuscado: entrada.entrada.ItemId,
                  tipoPromocionIdBuscado: entrada.entrada.TipoPromocionId,
                  reservaItemsDisponibles: this.venta.carritoValue.localizador.ReservaItems?.map(item => ({
                    ItemId: item.ItemId,
                    TipoProducto: item.TipoProducto
                  })) || [],
                  usandoLocalizadorPrincipal: true,
                  sinFechaEntradaDelLocalizador: this.venta.carritoValue.localizador.SinFechaEntrada,
                  fechaHoraEntradaDelLocalizador: this.venta.carritoValue.localizador.FechaHoraEntrada
                });
              } else {
                // 🔍 [CANJEAR RESERVA] Log cuando NO hay localizador
                console.log(`🔍 [CANJEAR RESERVA] [NORMAL-PROMO] ⚠️ Sin localizador ni ReservaItem (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                  tieneLocalizador: tieneLocalizador,
                  reservaItemEncontrado: false,
                  sinFechaEntradaReserva: sinFechaEntradaReserva,
                  fechaHoraEntradaReserva: fechaHoraEntradaReserva
                });
              }
            }
            
            // 🔍 [CANJEAR RESERVA] Log de valores calculados para promoción (no agrupada)
            if (tieneLocalizador) {
              const posicionVenta = reservaItem?.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
                ? reservaItem.PosicionesVenta[0] 
                : null;
              console.log(`🔍 [CANJEAR RESERVA] [NORMAL-PROMO] Valores calculados para promoción (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                reservaItemEncontrado: !!reservaItem,
                reservaItemId: reservaItem?.ItemId,
                posicionVenta: posicionVenta,
                posicionVentaSinFechaEntrada: posicionVenta?.SinFechaEntrada,
                posicionVentaFechaHoraEntrada: posicionVenta?.FechaHoraEntrada,
                reservaItemSinFechaEntrada: reservaItem?.['sinFechaEntrada'], // Fallback
                reservaItemFechaHoraEntrada: reservaItem?.FechaHoraEntrada, // Fallback
                sinFechaEntradaReserva: sinFechaEntradaReserva,
                fechaHoraEntradaReserva: fechaHoraEntradaReserva,
                localizadorSinFechaEntrada: this.venta.carritoValue.localizador.SinFechaEntrada,
                entradaFechaHoraEntrada: entrada.entrada.FechaHoraEntrada,
                entradaFecha: entrada.entrada.Fecha
              });
            }

            const fechaHoraEntrada = fechaHoraEntradaReserva !== '' ? fechaHoraEntradaReserva : entrada.entrada.FechaHoraEntrada || entrada.entrada.Fecha || ''
            
            let nuevaEntrada = {
              PrecioPagado: precioPagadoRedondeado.toFixed(2),
              DivisaId: datosInicio.DivisaRefId,
              NombreTipo: entrada.entrada.NombreTipo,
              TipoEntradaId: '',
              FechaHoraEntrada: isVentanaSinFechaChecked ? '' : fechaHoraEntrada, // Se puede sobrescribir en el loop de PromocionEntradas
              Operacion: 'SA',
              BaseImponible: Number.parseFloat(
                entrada.entrada.PrecioBase.replace(',', '.')
              ),
              IVA: entrada.entrada.IVA,
              Devuelta: 0, // en venta siempre será 0,
              TipoAbreviado: '',
              AbonadoId: 0, // no aplica
              ClienteAsociadoId: 0, // no aplica
              OrdenCjta: entradaConjunta ? 1 : 0,
              Comision: Number.parseFloat(
                entrada.entrada.Comision.replace(',', '.')
              ), // campo comision
              IvaComision: Number.parseFloat(
                entrada.entrada.IvaComision.replace(',', '.')
              ), // campo iva comision,
              BonoTexto: '',
              DNIAbonadosExt: String(entrada.entrada.DNIAbonadosExt || ''),
              Identificacion: '',
              CodigoPromocional: entrada.codigoPromocional,
              IdTarifa: entrada.entrada.idTarifa,
              GrupoTarifa: entrada.entrada.GrupoTarifa || '',
              PromocionId: entrada.entrada.TipoPromocionId,
              PromocionNombre: entrada.entrada.NombreTipo,
              PromocionNumOrden: promocionNumOrden,
              PosicionesVenta:
                entrada.entrada.PromocionEntradas &&
                entrada.entrada.PromocionEntradas[0].PosicionesVenta &&
                entrada.entrada.PromocionEntradas[0].PosicionesVenta[0]
                  ? entrada.entrada.PromocionEntradas[0].PosicionesVenta[0]
                      .PosicionVenta
                  : 0,
              TarjetaMonederoCodigo: '',
              EntradasVendidasPersonas: undefined,
              EntradasVendidasPersonasList: [],
              TipoProducto: entrada.entrada.TipoProducto,
              SinFechaEntrada: isVentanaSinFechaChecked ? 1 : sinFechaEntradaReserva !== null && sinFechaEntradaReserva !== undefined
                ? sinFechaEntradaReserva 
                : fechaHoraEntrada ? '0' : '1',
            };
            
            // 🔍 [CANJEAR RESERVA] Log de nuevaEntrada creada para promoción (no agrupada)
            if (tieneLocalizador) {
              console.log(`🔍 [CANJEAR RESERVA] [NORMAL-PROMO] nuevaEntrada creada (i=${i}):`, {
                SinFechaEntrada: nuevaEntrada.SinFechaEntrada,
                FechaHoraEntrada: nuevaEntrada.FechaHoraEntrada,
                TipoEntradaId: nuevaEntrada.TipoEntradaId,
                ItemId: entrada.entrada.ItemId
              });
            }
            
            entrada.entrada.PromocionEntradas.forEach((val) => {
              numEntradaCantidadProcededncia = 0;
              let tmpHora = '';
              if (
                val.CuentaVisitante &&
                val.CuentaVisitante == '1' &&
                this.estaenRecintoconHorarios(
                  val,
                  _this.venta.carritoValue.horarios
                )
              ) {
                //&& hay horario para este recinto
                //Yaribel 20210122 Modificamos para que obtenga el horario del recinto que le corresponde
                let horario =
                  _this.venta.carritoValue.horarios[
                    _this.venta.carritoValue.horarios.findIndex(
                      (item) =>
                        item.recinto.TipoEntrada === val.TipoEntradaId ||
                        (item.recinto.entradas != null &&
                          item.recinto.entradas.some(
                            (itemEntrada) => itemEntrada == val.TipoEntradaId
                          ))
                    )
                  ];
                // Yaribel 20210122
                tmpHora =
                  moment(horario.sesion.Fecha).format('YYYY/MM/DD') +
                  ' ' +
                  horario.sesion.HoraInicio +
                  ':00';
                if (
                  val.ArgumentosCaracteristica != null &&
                  val.ArgumentosCaracteristica.length != 0
                )
                  contadorVisitantesHorarios++;
              } else {
                tmpHora = val.FechaHoraAcceso;
              }
              // TODO ojo, verificar si la fecha viene como año/mes/dia, en el caso de forfait 2 horas es asi, por ejemplo
              for (let j = 0; j < +val.NumeroEntradas; j++) {
                //<< Yaribel 20210226 //<<Yaribel 20210226 Codigo para añadir posicionVenta añadimos la validacion de posicionVenta para los agrupados
                const miPosicionVenta =
                  val.PosicionesVenta && val.PosicionesVenta[0]
                    ? val.PosicionesVenta[0].PosicionVenta
                    : 0;
                //<< Yaribel 20210317 Codigo para añadir calculo de agrupadas con ImpresionTicket y ImprimirBarcode por entrada de la promocion
                let nEntrada = '0';
                let nPromoEntrada = '0';
                //modificamos nueva entrada para enviar entradas vendidas personas
                nuevaEntrada.FechaHoraEntrada = isVentanaSinFechaChecked ? '' : tmpHora;
                nuevaEntrada.TarjetaMonederoCodigo =
                  val.Caracteristicas !== undefined &&
                  val.Caracteristicas !== null &&
                  val.Caracteristicas.UsoMonedero === '1'
                    ? val.EntradasVendidasPersonas[contadorNominacion]
                        .TarjetaMonederoCodigo
                    : nuevaEntrada.TarjetaMonederoCodigo;
                //nuevaEntrada.EntradasVendidasPersonas =  this.cargarEntradasVendidasPersonas(entrada.entrada, val, contadorNominacion);
                let promocionEntrada = JSON.parse(JSON.stringify(nuevaEntrada));
                promocionEntrada.EntradasVendidasPersonas =
                  this.cargarEntradasVendidasPersonas(
                    entrada.entrada,
                    val,
                    contadorNominacion
                  );
                if (val.CuentaNominada == '1' || val.CuentaVisitante == '1') {
                  contadorNominacion++;
                }
                //Cargamos la procedencia si procede
                if (val.CuentaVisitante == '1') {
                  this.cargarProcedenciaEnEntrada(
                    promocionEntrada,
                    procedencias,
                    numProductoProcededncia,
                    numEntradaProductoProcededncia,
                    numEntradaCantidadProcededncia
                  );
                  // Actualizamos contadores
                  numEntradaCantidadProcededncia =
                    numEntradaCantidadProcededncia + 1;
                  totalProductosProcededncia = totalProductosProcededncia + 1;
                }

                if (
                  promocionEntrada.EntradasVendidasPersonas &&
                  promocionEntrada.EntradasVendidasPersonas !== ''
                )
                  nuevaEntrada.EntradasVendidasPersonasList.push({
                    TipoEntradaId: val.TipoEntradaId,
                    ...promocionEntrada.EntradasVendidasPersonas,
                  });
              }
              //this.generarRecintosVentaPorEntrada(entrada,nuevaEntrada, countEntrada, 0);
              this.generarRecintosVentaPorPromocion(
                entrada,
                nuevaEntrada,
                countEntrada,
                0
              );
              if (
                entrada.entrada.CaracteristicasEntrada != null &&
                entrada.entrada.CaracteristicasEntrada.length != 0 &&
                !(
                  entrada.entrada.PromocionEntradas != null &&
                  entrada.entrada.PromocionEntradas.length != 0
                )
              ) {
                contadorVisitantesHorarios++;
              }
            });
            entradas.push(nuevaEntrada);
            numEntradaProductoProcededncia++;
          }
        } else if (entrada.entrada.TipoProducto === '1') {
          //<<Yaribel 20210226 Codigo para añadir posicionVenta Inicializamos valores para PosicionVenta
          let miPosicionVenta = '0';
          let cantidadEntradas = 0;
          let indice = 0;
          numEntradaCantidadProcededncia = 0;
          //Yaribel 20210226 >>
          for (let i = 0; i < entrada.cantidad; i++) {
            //<<Yaribel 20210226 Codigo para añadir posicionVenta obtenemos la posicionventa de una entrada en concreto
            if (
              entrada.entrada.PosicionesVenta &&
              entrada.entrada.PosicionesVenta.length > 0 &&
              indice < entrada.entrada.PosicionesVenta.length
            ) {
              if (
                i >=
                entrada.entrada.PosicionesVenta[indice].length +
                  cantidadEntradas
              ) {
                miPosicionVenta =
                  entrada.entrada.PosicionesVenta[indice + 1].PosicionVenta;
                cantidadEntradas +=
                  entrada.entrada.PosicionesVenta[indice].length;
                indice += 1;
              }
            }
            //Yaribel 20210226 >>

            let tmpHora = '';
            if (
              entrada.entrada.CuentaVisitante &&
              entrada.entrada.CuentaVisitante == '1' &&
              this.estaenRecintoconHorarios(
                entrada.entrada,
                _this.venta.carritoValue.horarios
              )
            ) {
              //&& hay horario para este recinto
              //<< Yaribel 20210122 Modificamos para que obtenga el horario del recinto que le corresponde
              let horario =
                _this.venta.carritoValue.horarios[
                  _this.venta.carritoValue.horarios.findIndex(
                    (item) =>
                      item.recinto.TipoEntrada ===
                        entrada.entrada.TipoEntradaId ||
                      (item.recinto.entradas != null &&
                        item.recinto.entradas.some(
                          (itemEntrada) =>
                            itemEntrada == entrada.entrada.TipoEntradaId
                        ))
                  )
                ];
              // Yaribel 20210122 >>
              tmpHora =
                moment(horario.sesion.Fecha).format('YYYY/MM/DD') +
                ' ' +
                horario.sesion.HoraInicio +
                ':00';
              if (
                (entrada.entrada.ArgumentosCaracteristica != null &&
                  entrada.entrada.ArgumentosCaracteristica.length != 0) ||
                (entrada.entrada.CaracteristicasEntrada != null &&
                  entrada.entrada.CaracteristicasEntrada.length != 0)
              )
                contadorVisitantesHorarios++;
            } else {
              tmpHora = entrada.entrada.FechaHoraAcceso;
            }
            //<< Yaribel 20210317 Codigo para añadir calculo de agrupadas con ImpresionTicket y ImprimirBarcode
            const nEntrada =
              entrada.entrada.ImprimirTicket === '0' ? '' + i : '0';
            if (
              !tipoEntradaIdAgrupadas.find(
                (x) =>
                  x.posicionVenta == miPosicionVenta &&
                  x.tipoProducto == '1' &&
                  x.promoId == '0' &&
                  x.entradaId == entrada.entrada.TipoEntradaId &&
                  x.numEntrada == nEntrada &&
                  x.codigoPromocional == entrada.codigoPromocional
              )
            ) {
              tipoEntradaIdAgrupadas.push({
                promoId: '0',
                entradaId: entrada.entrada.TipoEntradaId,
                tipoProducto: '1',
                posicionVenta: miPosicionVenta,
                numEntrada: nEntrada,
                numPromoEntrada: '0',
                codigoPromocional: entrada.codigoPromocional,
              });
            }
            /*let precioPagado =
              Number.parseFloat(
                entrada.entrada.PrecioEnDivisaReferencia.replace(',', '.')
              ) - Number.parseFloat(entrada.entrada.Comision.replace(',', '.'));*/
            let precioPagado =
              Number.parseFloat(
                entrada.entrada.PrecioEnDivisaReferencia.replace(',', '.')
              );
            let precioPagadoRedondeado = Math.round(precioPagado * 100) / 100;
            
            // 🔍 [CANJEAR RESERVA] Buscar ReservaItem correspondiente para heredar fechas
            let reservaItem = null;
            let fechaHoraEntradaReserva = '';
            let sinFechaEntradaReserva = null;

            if (tieneLocalizador && this.venta.carritoValue.localizador.ReservaItems) {
              reservaItem = this.venta.carritoValue.localizador.ReservaItems.find(
                item => item.ItemId === entrada.entrada.ItemId || 
                        item.ItemId === entrada.entrada.TipoEntradaId ||
                        (entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 &&
                         item.ItemId === entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId)
              );

              if (reservaItem) {
                // 🔍 [CANJEAR RESERVA] Obtener valores de PosicionesVenta[0] si existe (nuevo cambio: ahora vienen de PosicionesVenta)
                const posicionVenta = reservaItem.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
                  ? reservaItem.PosicionesVenta[0] 
                  : null;
                
                // Heredar FechaHoraEntrada de PosicionesVenta[0] si existe (buscar tanto FechaHoraEntrada como fechaEntrada)
                const fechaHoraPosicionVenta = posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada;
                fechaHoraEntradaReserva = posicionVenta && fechaHoraPosicionVenta && fechaHoraPosicionVenta !== '' && fechaHoraPosicionVenta !== null
                  ? moment(fechaHoraPosicionVenta).isValid()
                    ? moment(fechaHoraPosicionVenta).format('YYYY/MM/DD HH:mm:ss')
                    : fechaHoraPosicionVenta
                  : (reservaItem.FechaHoraEntrada && reservaItem.FechaHoraEntrada !== '' && reservaItem.FechaHoraEntrada !== null
                    ? moment(reservaItem.FechaHoraEntrada).isValid()
                      ? moment(reservaItem.FechaHoraEntrada).format('YYYY/MM/DD HH:mm:ss')
                      : reservaItem.FechaHoraEntrada
                    : (moment(tmpHora).isValid() ? moment(tmpHora).format('YYYY/MM/DD HH:mm:ss') : ''));
                
                // Heredar sinFechaEntrada de PosicionesVenta[0] si existe (buscar tanto SinFechaEntrada como sinFechaEntrada, y convertir número a string)
                const sinFechaPosicionVenta = posicionVenta?.SinFechaEntrada !== undefined ? posicionVenta.SinFechaEntrada : 
                                             (posicionVenta?.sinFechaEntrada !== undefined ? posicionVenta.sinFechaEntrada : undefined);
                const sinFechaPosicionVentaStr = sinFechaPosicionVenta !== undefined && sinFechaPosicionVenta !== null 
                  ? String(sinFechaPosicionVenta) 
                  : '';
                
                if (posicionVenta && sinFechaPosicionVentaStr !== '') {
                  sinFechaEntradaReserva = sinFechaPosicionVentaStr;
                } else if (reservaItem['sinFechaEntrada'] !== undefined && reservaItem['sinFechaEntrada'] !== null && reservaItem['sinFechaEntrada'] !== '') {
                  // Fallback al ReservaItem si PosicionesVenta no tiene el valor
                  sinFechaEntradaReserva = reservaItem['sinFechaEntrada'];
                } else {
                  // Inferir de FechaHoraEntrada (priorizar PosicionesVenta, luego ReservaItem)
                  const fechaHoraParaInferir = fechaHoraPosicionVenta || reservaItem.FechaHoraEntrada;
                  if (!fechaHoraParaInferir || fechaHoraParaInferir === '' || fechaHoraParaInferir === null) {
                    sinFechaEntradaReserva = '1'; // Sin fecha
                  } else {
                    // Si tiene fecha pero el localizador dice sin fecha, priorizar el localizador
                    if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && 
                        this.venta.carritoValue.localizador.SinFechaEntrada !== null &&
                        this.venta.carritoValue.localizador.SinFechaEntrada === '1') {
                      sinFechaEntradaReserva = '1'; // Usar el del localizador (sin fecha)
                    } else {
                      sinFechaEntradaReserva = '0'; // Con fecha
                    }
                  }
                }
              } else if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && this.venta.carritoValue.localizador.SinFechaEntrada !== null) {
                // Si no se encuentra el ReservaItem, usar el del localizador principal
                sinFechaEntradaReserva = this.venta.carritoValue.localizador.SinFechaEntrada;
              }
            }
            
            // 🔍 [CANJEAR RESERVA] Log de valores calculados antes de crear nuevaEntrada (normal)
            if (tieneLocalizador) {
              const posicionVenta = reservaItem?.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
                ? reservaItem.PosicionesVenta[0] 
                : null;
              console.log(`🔍 [CANJEAR RESERVA] [NORMAL] Valores calculados para entrada (ItemId: ${entrada.entrada.ItemId || entrada.entrada.TipoEntradaId}, i=${i}):`, {
                reservaItemEncontrado: !!reservaItem,
                reservaItemId: reservaItem?.ItemId,
                posicionVenta: posicionVenta,
                posicionVentaSinFechaEntrada: posicionVenta?.SinFechaEntrada || posicionVenta?.sinFechaEntrada,
                posicionVentaFechaHoraEntrada: posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada,
                reservaItemSinFechaEntrada: reservaItem?.['sinFechaEntrada'], // Fallback
                reservaItemFechaHoraEntrada: reservaItem?.FechaHoraEntrada, // Fallback
                sinFechaEntradaReserva: sinFechaEntradaReserva,
                fechaHoraEntradaReserva: fechaHoraEntradaReserva,
                tmpHora: tmpHora,
                localizadorSinFechaEntrada: this.venta.carritoValue.localizador.SinFechaEntrada
              });
            }
            
            const fechaHoraEntrada = fechaHoraEntradaReserva !== '' 
                ? fechaHoraEntradaReserva
                : (moment(tmpHora).isValid() ? moment(tmpHora).format('YYYY/MM/DD HH:mm:ss') : '')
            
            // Yaribel 20210317 >>
            const nuevaEntrada: any = {
              PrecioPagado: precioPagadoRedondeado.toFixed(2),
              DivisaId: datosInicio.DivisaRefId,
              NombreTipo: entrada.entrada.NombreTipo,
              TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
              ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
              : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
              FechaHoraEntrada: isVentanaSinFechaChecked ? '' : fechaHoraEntrada,
              Operacion: 'SA',
              BaseImponible: Number.parseFloat(
                entrada.entrada.PrecioBase.replace(',', '.')
              ),
              IVA: entrada.entrada.IVA,
              Devuelta: 0, // en venta siempre será 0,
              TipoAbreviado: '',
              AbonadoId: 0, // no aplica
              ClienteAsociadoId: 0, // no aplica
              OrdenCjta: entradaConjunta ? 1 : 0, // Yaribel 20210121 Verificamos si es entrada conjunta para agrupar todas las entradas del mismo // Yaribel 20210226 Añadimos  PosicionVenta
              Comision: Number.parseFloat(
                entrada.entrada.Comision.replace(',', '.')
              ), // campo comision
              IvaComision: Number.parseFloat(
                entrada.entrada.IvaComision.replace(',', '.')
              ), // campo iva comision,
              BonoTexto: '',
              DNIAbonadosExt: String(entrada.entrada.DNIAbonadosExt || ''),
              Identificacion: '',
              IdTarifa: entrada.entrada.idTarifa,
              GrupoTarifa: entrada.entrada.GrupoTarifa || '',
              CodigoPromocional: entrada.codigoPromocional,
              PosicionesVenta: entrada.entrada.PosicionesVenta,
              TarjetaMonederoCodigo:
                entrada.entrada.Caracteristicas !== undefined &&
                entrada.entrada.Caracteristicas !== null &&
                entrada.entrada.Caracteristicas.UsoMonedero === '1'
                  ? entrada.entrada.EntradasVendidasPersonas[i]
                    ? entrada.entrada.EntradasVendidasPersonas[i]
                        .TarjetaMonederoCodigo
                    : ''
                  : '',
              EntradasVendidasPersonas: undefined, // this.cargarEntradasVendidasPersonas(entrada.entrada, entrada.entrada, i),
              EntradasVendidasPersonasList: [],
              TipoProducto: entrada.entrada.TipoProducto,
              SinFechaEntrada: isVentanaSinFechaChecked ? 1 : sinFechaEntradaReserva !== null && sinFechaEntradaReserva !== undefined
                ? sinFechaEntradaReserva 
                : (fechaHoraEntrada ? 0 : 1),
            };
            
            // 🔍 [CANJEAR RESERVA] Log de nuevaEntrada creada (normal)
            if (tieneLocalizador) {
              console.log(`🔍 [CANJEAR RESERVA] [NORMAL] nuevaEntrada creada (i=${i}):`, {
                SinFechaEntrada: nuevaEntrada.SinFechaEntrada,
                FechaHoraEntrada: nuevaEntrada.FechaHoraEntrada,
                TipoEntradaId: nuevaEntrada.TipoEntradaId,
                ItemId: entrada.entrada.ItemId
              });
            }
            
            let Entrada = JSON.parse(JSON.stringify(nuevaEntrada));
            Entrada.EntradasVendidasPersonas =
              this.cargarEntradasVendidasPersonas(
                entrada.entrada,
                entrada.entrada,
                i
              );
            //Cargamos la procedencia si procede
            if (entrada.entrada.CuentaVisitante == '1') {
              this.cargarProcedenciaEnEntrada(
                Entrada,
                procedencias,
                numProductoProcededncia,
                0,
                numEntradaCantidadProcededncia
              );
              // Actualizamos contadores
              numEntradaCantidadProcededncia =
                numEntradaCantidadProcededncia + 1;
              totalProductosProcededncia = totalProductosProcededncia + 1;
            }

            if (
              Entrada.EntradasVendidasPersonas &&
              Entrada.EntradasVendidasPersonas !== ''
            )
              nuevaEntrada.EntradasVendidasPersonasList.push({
                TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
              ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
              : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
                ...Entrada.EntradasVendidasPersonas,
              });

            // extraData solo lo tienen las butacas
            if (
              'extraData' in entrada.entrada &&
              entrada.entrada.extraData !== undefined &&
              entrada.entrada.extraData !== null &&
              entrada.entrada.extraData.espectaculo !== null &&
              entrada.entrada.extraData.espectaculo !== undefined
            ) {
              nuevaEntrada.FechaHoraEntrada = moment(
                entrada.entrada.FechaHoraEntrada
              ).format('YYYY/MM/DD HH:mm:ss');
              nuevaEntrada.EntradasVendidasButacas = {
                EspectaculoId:
                  entrada.entrada.extraData.espectaculo.iEspectaculoId,
                RecintoId: entrada.entrada.extraData.dataIdRecinto,
                FechaHora: moment(entrada.entrada.FechaHoraEntrada).format(
                  'YYYY/MM/DD HH:mm:ss'
                ),
                Fila: entrada.entrada.extraData.dataNumFila,
                Butaca: entrada.entrada.extraData.dataNumButaca,
              };
            }
            // ListaButacas solo lo tienen las butacas recuperadas del localizador
            if (
              'ListaButacas' in entrada.entrada &&
              entrada.entrada.ListaButacas !== null &&
              entrada.entrada.ListaButacas.length > 0
            ) {
              //
              nuevaEntrada.FechaHoraEntrada = moment(
                entrada.entrada.FechaHoraAccesoLocalizadorButaca
              ).format('YYYY/MM/DD HH:mm:ss');

              nuevaEntrada.EntradasVendidasButacas = {
                EspectaculoId: entrada.entrada.ListaButacas[i].IdEspectaculo,
                RecintoId: entrada.entrada.ListaButacas[i].IdRecinto,
                FechaHora: moment(
                  entrada.entrada.ListaButacas[i].FechaHora
                ).format('YYYY/MM/DD HH:mm:ss'),
                Fila: entrada.entrada.ListaButacas[i].NumFila,
                Butaca: entrada.entrada.ListaButacas[i].NumBuatca,
              };
            }
            this.generarRecintosVentaPorEntrada(
              entrada,
              nuevaEntrada,
              countEntrada,
              i
            );
            entradas.push(nuevaEntrada);
          }
        }
      }
      //Procedencias
      numProductoProcededncia = numProductoProcededncia + 1;
      countEntrada++;
    });
    
    // 🔍 [CANJEAR RESERVA] Log al final de generarEntradasVendidasReservaAgrupada
    console.log('🔍 [CANJEAR RESERVA] Entradas generadas (agrupada):', entradas.length);
    if (entradas.length > 0 && tieneLocalizador) {
      console.log('🔍 [CANJEAR RESERVA] Muestra de primeras 3 entradas (agrupada):', entradas.slice(0, 3).map(e => ({
        TipoEntradaId: e.TipoEntradaId,
        FechaHoraEntrada: e.FechaHoraEntrada,
        SinFechaEntrada: e.SinFechaEntrada
      })));
    }
    console.log('🔍 [CANJEAR RESERVA] ===========================================');
    
    return entradas;
  }

  private generarEntradasVendidasReservaAgrupada(entradaConjunta): any {
    let promocionNumOrden = 0;
    let contadorVisitantesHorarios = 0;
    let _this = this;
    const datosInicio = this.configuration.datosInicioValue;
    const entradas = [];
    const tipoEntradaIdAgrupadas: Agrupados[] = [];
    
    // 🔍 [CANJEAR RESERVA] Log al inicio de generarEntradasVendidasReservaAgrupada
    console.log('🔍 [CANJEAR RESERVA] ===== generarEntradasVendidasReservaAgrupada =====');
    const tieneReserva = this.venta.carritoValue.reserva && Object.keys(this.venta.carritoValue.reserva).length > 0;
    const tieneLocalizador = this.venta.carritoValue.localizador && Object.keys(this.venta.carritoValue.localizador).length > 0;
    console.log('🔍 [CANJEAR RESERVA] ¿Es canje de reserva agrupada?', {
      tieneReserva: tieneReserva,
      tieneLocalizador: tieneLocalizador,
      numEntradas: this.venta.carritoValue.entradas?.length || 0
    });
    
    if (tieneLocalizador && this.venta.carritoValue.localizador.ReservaItems) {
      console.log('🔍 [CANJEAR RESERVA] ReservaItems disponibles (agrupada):', this.venta.carritoValue.localizador.ReservaItems.map(item => ({
        ItemId: item.ItemId,
        TipoProducto: item.TipoProducto,
        sinFechaEntrada: item['sinFechaEntrada'],
        FechaHoraEntrada: item.FechaHoraEntrada
      })));
    }
    
    //para modificar con procedencias
    let procedencias = this.procedenciaService.getprocedenciasValue();
    let numProductoProcededncia = 0;
    let numEntradaProductoProcededncia = 0;
    let numEntradaCantidadProcededncia = 0;
    let totalProductosProcededncia = 1;
    let countEntrada = 0;
    const isVentanaSinFechaChecked = this.venta.isVentaSinFechaChecked;
    this.venta.carritoValue.entradas.forEach((entrada) => {
      // ;
      if (
        entrada.entrada.noEditable === undefined ||
        entrada.entrada.noEditable === null ||
        entrada.entrada.noEditable === false
      ) {
        if (entrada.entrada.TipoProducto === '2') {
          let contadorNominacion = 0;
          let arrayFarId = [];
          let arrayNumOrden = [];
          entrada.entrada.PosicionesVenta.forEach((posicion) => {
            posicion.NumOrden.forEach((num) => {
              arrayNumOrden.push(num);
            });
            posicion.FarId.forEach((farId) => {
              arrayFarId.push(farId);
            });
          });
          let arrayNumOrdenPromos = [];
          entrada.entrada.NumOrdenPromosConEntradas.forEach((num) => {
            arrayNumOrdenPromos.push(num.NumOrdenPromo);
          });

          //const arrayNumOrdenPromos = entrada.entrada.NumOrdenPromos;

          ////  Buscamos los datos en el localizador
          ////   el FarId, ReservaItems, PosicionVenta que le corresponde
          const indexEntrada =
            this.venta.carritoValue.localizador.ReservaItems.findIndex(
              (x) => x.TipoProducto == '2' && x.ItemId == entrada.entrada.ItemId
            );
          // Yaribel 20210629 >>
          let indexNumOrdenPromos = -1;
          numEntradaProductoProcededncia = 0;
          for (let i = 0; i < entrada.cantidad; i++) {
            promocionNumOrden++;
            indexNumOrdenPromos++;
            let miPosicionVenta = '0';
            let misFarIds = [];
            entrada.entrada.NumOrdenPromosConEntradas.forEach((x) => {
              if (x.NumOrdenPromo == arrayNumOrdenPromos[indexNumOrdenPromos]) {
                misFarIds.push(x.PromocionEntradas);
              }
            });
            miPosicionVenta =
              misFarIds.length > 0 &&
              misFarIds[0].FarIds &&
              misFarIds[0].FarIds[0]
                ? entrada.entrada.PosicionesVenta.find((x) =>
                    x.FarId.includes(misFarIds[0].FarIds[0])
                  ).PosicionVenta
                : 0;
            // PrecioPagado = base (sin restar comisión) para que el API sume (PrecioPagado+Comision)=total y coincida con TotalPagar (DV06)
            let precioPagado = Number.parseFloat(
              entrada.entrada.PrecioEnDivisaReferencia.replace(',', '.')
            );
            let precioPagadoRedondeado = Math.round(precioPagado * 100) / 100;

            // 🔍 [CANJEAR RESERVA] Buscar ReservaItem correspondiente para promociones
            let reservaItem = null;
            let fechaHoraEntradaReserva = '';
            let sinFechaEntradaReserva = null;
            if (tieneLocalizador && this.venta.carritoValue.localizador.ReservaItems) {
              reservaItem = this.venta.carritoValue.localizador.ReservaItems.find(
                item => item.ItemId === entrada.entrada.ItemId ||
                        item.ItemId === entrada.entrada.TipoPromocionId
              );
              if (reservaItem) {
                // 🔍 [CANJEAR RESERVA] Obtener valores de PosicionesVenta[0] si existe (nuevo cambio: ahora vienen de PosicionesVenta)
                const posicionVenta = reservaItem.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
                  ? reservaItem.PosicionesVenta[0] 
                  : null;
                
                // 🔍 [CANJEAR RESERVA] Log detallado de la estructura de PosicionesVenta
                if (tieneLocalizador) {
                  console.log(`🔍 [CANJEAR RESERVA] [AGRUPADA-PROMO] Análisis de PosicionesVenta (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                    tienePosicionesVenta: !!reservaItem.PosicionesVenta,
                    cantidadPosicionesVenta: reservaItem.PosicionesVenta?.length || 0,
                    posicionVentaEncontrada: !!posicionVenta,
                    posicionVentaCompleta: posicionVenta,
                    todasLasPosicionesVenta: reservaItem.PosicionesVenta?.map((pv, idx) => ({
                      indice: idx,
                      SinFechaEntrada: pv?.SinFechaEntrada || pv?.sinFechaEntrada,
                      sinFechaEntrada: pv?.sinFechaEntrada,
                      FechaHoraEntrada: pv?.FechaHoraEntrada || pv?.fechaEntrada,
                      fechaEntrada: pv?.fechaEntrada,
                      PosicionVenta: pv?.PosicionVenta
                    }))
                  });
                }
                
                // Heredar FechaHoraEntrada de PosicionesVenta[0] si existe (buscar tanto FechaHoraEntrada como fechaEntrada)
                const fechaHoraPosicionVenta = posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada;
                fechaHoraEntradaReserva = posicionVenta && fechaHoraPosicionVenta && fechaHoraPosicionVenta !== '' && fechaHoraPosicionVenta !== null
                  ? moment(fechaHoraPosicionVenta).isValid()
                    ? moment(fechaHoraPosicionVenta).format('YYYY/MM/DD HH:mm:ss')
                    : fechaHoraPosicionVenta
                  : (reservaItem.FechaHoraEntrada && reservaItem.FechaHoraEntrada !== '' && reservaItem.FechaHoraEntrada !== null
                    ? moment(reservaItem.FechaHoraEntrada).isValid()
                      ? moment(reservaItem.FechaHoraEntrada).format('YYYY/MM/DD HH:mm:ss')
                      : reservaItem.FechaHoraEntrada
                    : (entrada.entrada.FechaHoraEntrada || entrada.entrada.Fecha || ''));
                
                // Heredar sinFechaEntrada de PosicionesVenta[0] si existe (buscar tanto SinFechaEntrada como sinFechaEntrada, y convertir número a string)
                const sinFechaPosicionVenta = posicionVenta?.SinFechaEntrada !== undefined ? posicionVenta.SinFechaEntrada : 
                                             (posicionVenta?.sinFechaEntrada !== undefined ? posicionVenta.sinFechaEntrada : undefined);
                const sinFechaPosicionVentaStr = sinFechaPosicionVenta !== undefined && sinFechaPosicionVenta !== null 
                  ? String(sinFechaPosicionVenta) 
                  : '';
                
                if (posicionVenta && sinFechaPosicionVentaStr !== '') {
                  sinFechaEntradaReserva = sinFechaPosicionVentaStr;
                } else if (reservaItem['sinFechaEntrada'] !== undefined && reservaItem['sinFechaEntrada'] !== null && reservaItem['sinFechaEntrada'] !== '') {
                  // Fallback al ReservaItem si PosicionesVenta no tiene el valor
                  sinFechaEntradaReserva = reservaItem['sinFechaEntrada'];
                } else {
                  // Inferir de FechaHoraEntrada (priorizar PosicionesVenta, luego ReservaItem)
                  const fechaHoraParaInferir = fechaHoraPosicionVenta || reservaItem.FechaHoraEntrada;
                  if (!fechaHoraParaInferir || fechaHoraParaInferir === '' || fechaHoraParaInferir === null) {
                    sinFechaEntradaReserva = '1'; // Sin fecha
                  } else {
                    // Si tiene fecha pero el localizador dice sin fecha, priorizar el localizador
                    if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && 
                        this.venta.carritoValue.localizador.SinFechaEntrada !== null &&
                        this.venta.carritoValue.localizador.SinFechaEntrada === '1') {
                      sinFechaEntradaReserva = '1'; // Usar el del localizador (sin fecha)
                    } else {
                      sinFechaEntradaReserva = '0'; // Con fecha
                    }
                  }
                }
                
                // 🔍 [CANJEAR RESERVA] Log detallado del proceso de decisión
                if (tieneLocalizador) {
                  let fuenteSinFechaEntrada = 'N/A';
                  let razonDecision = '';
                  if (sinFechaPosicionVentaStr !== '') {
                    fuenteSinFechaEntrada = 'PosicionesVenta[0] (explícito)';
                    razonDecision = 'Valor explícito encontrado en PosicionesVenta[0]';
                  } else if (reservaItem['sinFechaEntrada'] !== undefined && reservaItem['sinFechaEntrada'] !== null && reservaItem['sinFechaEntrada'] !== '') {
                    fuenteSinFechaEntrada = 'ReservaItem (fallback)';
                    razonDecision = 'PosicionesVenta[0] no tiene valor, usando ReservaItem como fallback';
                  } else {
                    const fechaHoraParaInferir = fechaHoraPosicionVenta || reservaItem.FechaHoraEntrada;
                    if (!fechaHoraParaInferir || fechaHoraParaInferir === '' || fechaHoraParaInferir === null) {
                      fuenteSinFechaEntrada = 'Inferido (sin fecha)';
                      razonDecision = 'FechaHoraEntrada vacío/null → SinFechaEntrada = 1';
                    } else if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada === '1') {
                      fuenteSinFechaEntrada = 'Localizador (conflicto resuelto)';
                      razonDecision = 'Conflicto: ReservaItem tiene fecha pero localizador dice sin fecha → prioriza localizador';
                    } else {
                      fuenteSinFechaEntrada = 'Inferido (con fecha)';
                      razonDecision = 'FechaHoraEntrada tiene valor → SinFechaEntrada = 0';
                    }
                  }
                  
                  console.log(`🔍 [CANJEAR RESERVA] [AGRUPADA-PROMO] Proceso de decisión SinFechaEntrada (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                    fuente: fuenteSinFechaEntrada,
                    razon: razonDecision,
                    valorFinal: sinFechaEntradaReserva,
                    tienePosicionVenta: !!posicionVenta,
                    posicionVentaSinFechaEntrada: posicionVenta?.SinFechaEntrada || posicionVenta?.sinFechaEntrada,
                    posicionVentaFechaHoraEntrada: posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada,
                    sinFechaPosicionVentaStr: sinFechaPosicionVentaStr,
                    reservaItemSinFechaEntrada: reservaItem['sinFechaEntrada'],
                    reservaItemFechaHoraEntrada: reservaItem.FechaHoraEntrada,
                    fechaHoraParaInferir: fechaHoraPosicionVenta || reservaItem.FechaHoraEntrada,
                    localizadorSinFechaEntrada: this.venta.carritoValue.localizador.SinFechaEntrada,
                    hayConflicto: (fechaHoraPosicionVenta || reservaItem.FechaHoraEntrada) && 
                                  this.venta.carritoValue.localizador.SinFechaEntrada === '1'
                  });
                }
              } else if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && 
                         this.venta.carritoValue.localizador.SinFechaEntrada !== null) {
                // Si no se encuentra el ReservaItem, usar el del localizador principal
                sinFechaEntradaReserva = this.venta.carritoValue.localizador.SinFechaEntrada;
                fechaHoraEntradaReserva = this.venta.carritoValue.localizador.FechaHoraEntrada || entrada.entrada.FechaHoraEntrada || entrada.entrada.Fecha || '';
                
                // 🔍 [CANJEAR RESERVA] Log cuando NO se encuentra ReservaItem
                console.log(`🔍 [CANJEAR RESERVA] [AGRUPADA-PROMO] ⚠️ ReservaItem NO encontrado (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                  itemIdBuscado: entrada.entrada.ItemId,
                  tipoPromocionIdBuscado: entrada.entrada.TipoPromocionId,
                  reservaItemsDisponibles: this.venta.carritoValue.localizador.ReservaItems?.map(item => ({
                    ItemId: item.ItemId,
                    TipoProducto: item.TipoProducto
                  })) || [],
                  usandoLocalizadorPrincipal: true,
                  sinFechaEntradaDelLocalizador: this.venta.carritoValue.localizador.SinFechaEntrada,
                  fechaHoraEntradaDelLocalizador: this.venta.carritoValue.localizador.FechaHoraEntrada
                });
              } else {
                // 🔍 [CANJEAR RESERVA] Log cuando NO hay localizador
                console.log(`🔍 [CANJEAR RESERVA] [AGRUPADA-PROMO] ⚠️ Sin localizador ni ReservaItem (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                  tieneLocalizador: tieneLocalizador,
                  reservaItemEncontrado: false,
                  sinFechaEntradaReserva: sinFechaEntradaReserva,
                  fechaHoraEntradaReserva: fechaHoraEntradaReserva
                });
              }
            }
            
            // 🔍 [CANJEAR RESERVA] Log de valores calculados para promoción
            if (tieneLocalizador) {
              const posicionVenta = reservaItem?.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
                ? reservaItem.PosicionesVenta[0] 
                : null;
              console.log(`🔍 [CANJEAR RESERVA] [AGRUPADA-PROMO] Valores calculados para promoción (ItemId: ${entrada.entrada.ItemId}, i=${i}):`, {
                reservaItemEncontrado: !!reservaItem,
                reservaItemId: reservaItem?.ItemId,
                posicionVenta: posicionVenta,
                posicionVentaSinFechaEntrada: posicionVenta?.SinFechaEntrada || posicionVenta?.sinFechaEntrada,
                posicionVentaFechaHoraEntrada: posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada,
                reservaItemSinFechaEntrada: reservaItem?.['sinFechaEntrada'], // Fallback
                reservaItemFechaHoraEntrada: reservaItem?.FechaHoraEntrada, // Fallback
                sinFechaEntradaReserva: sinFechaEntradaReserva,
                fechaHoraEntradaReserva: fechaHoraEntradaReserva,
                localizadorSinFechaEntrada: this.venta.carritoValue.localizador.SinFechaEntrada,
                entradaFechaHoraEntrada: entrada.entrada.FechaHoraEntrada,
                entradaFecha: entrada.entrada.Fecha
              });
            }

            const fechaHoraEntrada = fechaHoraEntradaReserva !== '' 
                ? fechaHoraEntradaReserva
                : (entrada.entrada.FechaHoraEntrada || entrada.entrada.Fecha || '')
            
            const nuevaEntrada = {
              PrecioPagado: precioPagadoRedondeado.toFixed(2),
              DivisaId: datosInicio.DivisaRefId,
              NombreTipo: entrada.entrada.NombreTipo,
              TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
                ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
                : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
              FechaHoraEntrada: isVentanaSinFechaChecked ? '' : fechaHoraEntrada,
              Operacion: 'SA',
              BaseImponible: Number.parseFloat(
                entrada.entrada.PrecioBase.replace(',', '.')
              ),
              IVA: entrada.entrada.IVA,
              Devuelta: 0, // en venta siempre será 0,
              TipoAbreviado: '',
              AbonadoId: 0, // no aplica
              ClienteAsociadoId: 0, // no aplica
              OrdenCjta: entradaConjunta ? 1 : 0, // Yaribel 20210121 Verificamos si es entrada conjunta para agrupar todas las entradas del mismo  // Yaribel 20210223 Verificamos si es QR promo para agrupar todas las entradas de la promo
              Comision: Number.parseFloat(
                entrada.entrada.Comision.replace(',', '.')
              ), // campo comision
              IvaComision: Number.parseFloat(
                entrada.entrada.IvaComision.replace(',', '.')
              ), // campo iva comision,
              BonoTexto: '',
              DNIAbonadosExt: String(entrada.entrada.DNIAbonadosExt || ''),
              Identificacion: '',
              CodigoPromocional: entrada.codigoPromocional,
              IdTarifa: entrada.entrada.idTarifa,
              GrupoTarifa: entrada.entrada.GrupoTarifa || '',
              PromocionId: entrada.entrada.TipoPromocionId,
              PromocionNombre: entrada.entrada.NombreTipo,
              PromocionNumOrden: arrayNumOrdenPromos[indexNumOrdenPromos],
              PosicionVenta: miPosicionVenta, //Yaribel 20210226 Codigo para añadir posicionVenta
              TarjetaMonederoCodigo: '',
              //EntradasVendidasPersonas: this.cargarEntradasVendidasPersonas(entrada.entrada, val, contadorNominacion)
              EntradasVendidasPersonas: undefined,
              EntradasVendidasPersonasList: [],
              TipoProducto: entrada.entrada.TipoProducto,
              SinFechaEntrada: isVentanaSinFechaChecked ? 1 : sinFechaEntradaReserva !== null && sinFechaEntradaReserva !== undefined
                ? sinFechaEntradaReserva 
                : fechaHoraEntrada ? '0' : '1',
            };
            
            // 🔍 [CANJEAR RESERVA] Log de nuevaEntrada creada para promoción
            if (tieneLocalizador) {
              console.log(`🔍 [CANJEAR RESERVA] [AGRUPADA-PROMO] nuevaEntrada creada (i=${i}):`, {
                SinFechaEntrada: nuevaEntrada.SinFechaEntrada,
                FechaHoraEntrada: nuevaEntrada.FechaHoraEntrada,
                TipoEntradaId: nuevaEntrada.TipoEntradaId,
                ItemId: entrada.entrada.ItemId
              });
            }
            
            entrada.entrada.PromocionEntradas.forEach((val, index) => {
              //
              numEntradaCantidadProcededncia = 0;
              let tmpHora = '';
              let miFarId = 0;
              entrada.entrada.NumOrdenPromosConEntradas.forEach((x) => {
                if (
                  x.NumOrdenPromo == arrayNumOrdenPromos[indexNumOrdenPromos]
                ) {
                  miFarId = x.PromocionEntradas.find(
                    (promoEntrada) =>
                      promoEntrada.TipoEntradaId == val.TipoEntradaId
                  ).FarIds[0];
                }
              });
              if (
                val.CuentaVisitante &&
                val.CuentaVisitante == '1' &&
                this.estaenRecintoconHorarios(
                  val,
                  _this.venta.carritoValue.horarios
                )
              ) {
                //&& hay horario para este recinto
                let horario = this.venta.carritoValue.localizador
                  .ReservaRecintos[
                  this.venta.carritoValue.localizador.ReservaRecintos.findIndex(
                    (item) => item.idFilaAlbaranReserva == miFarId
                  )
                ]
                  ? this.venta.carritoValue.localizador.ReservaRecintos[
                      this.venta.carritoValue.localizador.ReservaRecintos.findIndex(
                        (item) => item.idFilaAlbaranReserva == miFarId
                      )
                    ]
                  : null;
                // Yaribel 20210122
                //tmpHora = moment(horario.sesion.Fecha).format('YYYY/MM/DD') + ' ' + horario.sesion.HoraInicio + ':00';
                tmpHora = horario ? horario.FechaEntrada : val.FechaHoraAcceso;
                //Prueba
                if (
                  val.ArgumentosCaracteristica != null &&
                  val.ArgumentosCaracteristica.length != 0
                )
                  contadorVisitantesHorarios++;
              } else {
                tmpHora = val.FechaHoraAcceso;
              }

              // TODO ojo, verificar si la fecha viene como año/mes/dia, en el caso de forfait 2 horas es asi, por ejemplo
              for (let j = 0; j < +val.NumeroEntradas; j++) {
                if (val.CuentaNominada == '1' || val.CuentaVisitante == '1') {
                  contadorNominacion++;
                }
                //Cargamos la procedencia si procede
                nuevaEntrada.FechaHoraEntrada = isVentanaSinFechaChecked ? '' : tmpHora || entrada.entrada.FechaHoraEntrada || entrada.entrada.Fecha || '';
                nuevaEntrada.TarjetaMonederoCodigo =
                  val.Caracteristicas !== undefined &&
                  val.Caracteristicas !== null &&
                  val.Caracteristicas.UsoMonedero === '1'
                    ? val.EntradasVendidasPersonas[contadorNominacion]
                        .TarjetaMonederoCodigo
                    : nuevaEntrada.TarjetaMonederoCodigo;
                //nuevaEntrada.EntradasVendidasPersonas =  this.cargarEntradasVendidasPersonas(entrada.entrada, val, contadorNominacion);
                let promocionEntrada = JSON.parse(JSON.stringify(nuevaEntrada));
                promocionEntrada.EntradasVendidasPersonas =
                  this.cargarEntradasVendidasPersonas(
                    entrada.entrada,
                    val,
                    contadorNominacion
                  );
                if (val.CuentaVisitante == '1') {
                  this.cargarProcedenciaEnEntrada(
                    promocionEntrada,
                    procedencias,
                    numProductoProcededncia,
                    numEntradaProductoProcededncia,
                    numEntradaCantidadProcededncia
                  );
                  // Actualizamos contadores
                  numEntradaCantidadProcededncia =
                    numEntradaCantidadProcededncia + 1;
                  totalProductosProcededncia = totalProductosProcededncia + 1;
                }

                if (
                  promocionEntrada.EntradasVendidasPersonas &&
                  promocionEntrada.EntradasVendidasPersonas !== ''
                )
                  nuevaEntrada.EntradasVendidasPersonasList.push({
                    TipoEntradaId: val.TipoEntradaId,
                    ...promocionEntrada.EntradasVendidasPersonas,
                  });
              }

              if (
                entrada.entrada.CaracteristicasEntrada != null &&
                entrada.entrada.CaracteristicasEntrada.length != 0 &&
                !(
                  entrada.entrada.PromocionEntradas != null &&
                  entrada.entrada.PromocionEntradas.length != 0
                )
              ) {
                contadorVisitantesHorarios++;
              }
            });

            this.generarRecintosVentaPorPromocion(
              entrada,
              nuevaEntrada,
              countEntrada,
              0
            );
            entradas.push(nuevaEntrada);
            numEntradaProductoProcededncia++;
          }
        } else if (entrada.entrada.TipoProducto === '1') {
          //<<Yaribel 20210226 Codigo para añadir posicionVenta Inicializamos valores para PosicionVenta
          let miPosicionVenta = '0';
          let cantidadEntradas = 0;
          let indice = 0;
          numEntradaCantidadProcededncia = 0;
          //Yaribel 20210226 >>
          //<< Yaribel 20210629 ya no buscamos los datos en el localizador lo buscamos en el objeto de posiciones venta
          let arrayFarId = [];
          let arrayNumOrden = [];
          entrada.entrada.PosicionesVenta.forEach((posicion) => {
            posicion.NumOrden.forEach((num) => {
              arrayNumOrden.push(num);
            });
            posicion.FarId.forEach((farId) => {
              arrayFarId.push(farId);
            });
          });

          for (let i = 0; i < entrada.cantidad; i++) {
            //<<Yaribel 20210629 Codigo para añadir posicionVenta
            entrada.entrada.PosicionesVenta.forEach((posicion) => {
              posicion.NumOrden.forEach((num) => {
                if (num == arrayNumOrden[i])
                  miPosicionVenta = posicion.PosicionVenta;
              });
            });
            let tmpHora = '';
            if (
              entrada.entrada.CuentaVisitante &&
              entrada.entrada.CuentaVisitante == '1' &&
              this.estaenRecintoconHorarios(
                entrada.entrada,
                _this.venta.carritoValue.horarios
              )
            ) {
              //&& hay horario para este recinto
              let horario =
                this.venta.carritoValue.localizador.ReservaRecintos[
                  this.venta.carritoValue.localizador.ReservaRecintos.findIndex(
                    (item) => item.idFilaAlbaranReserva == arrayFarId[i]
                  )
                ].FechaEntrada;
              tmpHora = horario;
              if (
                (entrada.entrada.ArgumentosCaracteristica != null &&
                  entrada.entrada.ArgumentosCaracteristica.length != 0) ||
                (entrada.entrada.CaracteristicasEntrada != null &&
                  entrada.entrada.CaracteristicasEntrada.length != 0)
              )
                contadorVisitantesHorarios++;
            } else {
              tmpHora = entrada.entrada.FechaHoraAcceso;
            }
            // PrecioPagado = base (sin restar comisión) para que el API sume (PrecioPagado+Comision)=total y coincida con TotalPagar (DV06)
            let precioPagado = Number.parseFloat(
              entrada.entrada.PrecioEnDivisaReferencia.replace(',', '.')
            );
            let precioPagadoRedondeado = Math.round(precioPagado * 100) / 100;

            // 🔍 [CANJEAR RESERVA] Buscar ReservaItem correspondiente para heredar fechas
            let reservaItem = null;
            let fechaHoraEntradaReserva = '';
            let sinFechaEntradaReserva = null;

            if (tieneLocalizador && this.venta.carritoValue.localizador.ReservaItems) {
              reservaItem = this.venta.carritoValue.localizador.ReservaItems.find(
                item => item.ItemId === entrada.entrada.ItemId ||
                        item.ItemId === entrada.entrada.TipoEntradaId ||
                        (entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 &&
                         item.ItemId === entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId)
              );

              if (reservaItem) {
                // 🔍 [CANJEAR RESERVA] Obtener valores de PosicionesVenta[0] si existe (nuevo cambio: ahora vienen de PosicionesVenta)
                const posicionVenta = reservaItem.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
                  ? reservaItem.PosicionesVenta[0] 
                  : null;
                
                // Heredar FechaHoraEntrada de PosicionesVenta[0] si existe (buscar tanto FechaHoraEntrada como fechaEntrada)
                const fechaHoraPosicionVenta = posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada;
                fechaHoraEntradaReserva = posicionVenta && fechaHoraPosicionVenta && fechaHoraPosicionVenta !== '' && fechaHoraPosicionVenta !== null
                  ? moment(fechaHoraPosicionVenta).isValid()
                    ? moment(fechaHoraPosicionVenta).format('YYYY/MM/DD HH:mm:ss')
                    : fechaHoraPosicionVenta
                  : (reservaItem.FechaHoraEntrada && reservaItem.FechaHoraEntrada !== '' && reservaItem.FechaHoraEntrada !== null
                    ? moment(reservaItem.FechaHoraEntrada).isValid()
                      ? moment(reservaItem.FechaHoraEntrada).format('YYYY/MM/DD HH:mm:ss')
                      : reservaItem.FechaHoraEntrada
                    : (moment(tmpHora).isValid() ? moment(tmpHora).format('YYYY/MM/DD HH:mm:ss') : ''));
                
                // Heredar sinFechaEntrada de PosicionesVenta[0] si existe (buscar tanto SinFechaEntrada como sinFechaEntrada, y convertir número a string)
                const sinFechaPosicionVenta = posicionVenta?.SinFechaEntrada !== undefined ? posicionVenta.SinFechaEntrada : 
                                             (posicionVenta?.sinFechaEntrada !== undefined ? posicionVenta.sinFechaEntrada : undefined);
                const sinFechaPosicionVentaStr = sinFechaPosicionVenta !== undefined && sinFechaPosicionVenta !== null 
                  ? String(sinFechaPosicionVenta) 
                  : '';
                
                if (posicionVenta && sinFechaPosicionVentaStr !== '') {
                  sinFechaEntradaReserva = sinFechaPosicionVentaStr;
                } else if (reservaItem['sinFechaEntrada'] !== undefined && reservaItem['sinFechaEntrada'] !== null && reservaItem['sinFechaEntrada'] !== '') {
                  // Fallback al ReservaItem si PosicionesVenta no tiene el valor
                  sinFechaEntradaReserva = reservaItem['sinFechaEntrada'];
                } else {
                  // Inferir de FechaHoraEntrada (priorizar PosicionesVenta, luego ReservaItem)
                  const fechaHoraParaInferir = fechaHoraPosicionVenta || reservaItem.FechaHoraEntrada;
                  if (!fechaHoraParaInferir || fechaHoraParaInferir === '' || fechaHoraParaInferir === null) {
                    sinFechaEntradaReserva = '1'; // Sin fecha
                  } else {
                    // Si tiene fecha pero el localizador dice sin fecha, priorizar el localizador
                    if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && 
                        this.venta.carritoValue.localizador.SinFechaEntrada !== null &&
                        this.venta.carritoValue.localizador.SinFechaEntrada === '1') {
                      sinFechaEntradaReserva = '1'; // Usar el del localizador (sin fecha)
                    } else {
                      sinFechaEntradaReserva = '0'; // Con fecha
                    }
                  }
                }
              } else if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && this.venta.carritoValue.localizador.SinFechaEntrada !== null) {
                // Si no se encuentra el ReservaItem, usar el del localizador principal
                sinFechaEntradaReserva = this.venta.carritoValue.localizador.SinFechaEntrada;
              }
            }
            
            // 🔍 [CANJEAR RESERVA] Log de valores calculados antes de crear nuevaEntrada (agrupada)
            if (tieneLocalizador) {
              const posicionVenta = reservaItem?.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
                ? reservaItem.PosicionesVenta[0] 
                : null;
              console.log(`🔍 [CANJEAR RESERVA] [AGRUPADA] Valores calculados para entrada (ItemId: ${entrada.entrada.ItemId || entrada.entrada.TipoEntradaId}, i=${i}):`, {
                reservaItemEncontrado: !!reservaItem,
                reservaItemId: reservaItem?.ItemId,
                posicionVenta: posicionVenta,
                posicionVentaSinFechaEntrada: posicionVenta?.SinFechaEntrada || posicionVenta?.sinFechaEntrada,
                posicionVentaFechaHoraEntrada: posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada,
                reservaItemSinFechaEntrada: reservaItem?.['sinFechaEntrada'], // Fallback
                reservaItemFechaHoraEntrada: reservaItem?.FechaHoraEntrada, // Fallback
                sinFechaEntradaReserva: sinFechaEntradaReserva,
                fechaHoraEntradaReserva: fechaHoraEntradaReserva,
                tmpHora: tmpHora,
                localizadorSinFechaEntrada: this.venta.carritoValue.localizador.SinFechaEntrada
              });
            }

            const fechaHoraEntrada = fechaHoraEntradaReserva !== '' 
                ? fechaHoraEntradaReserva
                : (moment(tmpHora).isValid() ? moment(tmpHora).format('YYYY/MM/DD HH:mm:ss') : '')
            
            const nuevaEntrada: any = {
              PrecioPagado: precioPagadoRedondeado.toFixed(2),
              DivisaId: datosInicio.DivisaRefId,
              NombreTipo: entrada.entrada.NombreTipo,
              TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
              ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
              : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
              FechaHoraEntrada: isVentanaSinFechaChecked ? '' : fechaHoraEntrada,
              Operacion: 'SA',
              BaseImponible: Number.parseFloat(
                entrada.entrada.PrecioBase.replace(',', '.')
              ),
              IVA: entrada.entrada.IVA,
              Devuelta: 0, // en venta siempre será 0,
              TipoAbreviado: '',
              AbonadoId: 0, // no aplica
              ClienteAsociadoId: 0, // no aplica
              OrdenCjta: entradaConjunta ? 1 : 0, // Yaribel 20210121 Verificamos si es entrada conjunta para agrupar todas las entradas del mismo // Yaribel 20210226 Añadimos  PosicionVenta
              Comision: Number.parseFloat(
                entrada.entrada.Comision.replace(',', '.')
              ), // campo comision
              IvaComision: Number.parseFloat(
                entrada.entrada.IvaComision.replace(',', '.')
              ), // campo iva comision,
              BonoTexto: '',
              DNIAbonadosExt: String(entrada.entrada.DNIAbonadosExt || ''),
              Identificacion: '',
              IdTarifa: entrada.entrada.idTarifa,
              GrupoTarifa: entrada.entrada.GrupoTarifa || '',
              CodigoPromocional: entrada.codigoPromocional,
              PosicionVenta: miPosicionVenta,
              TarjetaMonederoCodigo:
                entrada.entrada.Caracteristicas !== undefined &&
                entrada.entrada.Caracteristicas !== null &&
                entrada.entrada.Caracteristicas.UsoMonedero === '1'
                  ? entrada.entrada.EntradasVendidasPersonas[i]
                    ? entrada.entrada.EntradasVendidasPersonas[i]
                        .TarjetaMonederoCodigo
                    : ''
                  : '',
              EntradasVendidasPersonas: undefined, //this.cargarEntradasVendidasPersonas(entrada.entrada, entrada.entrada, i),
              EntradasVendidasPersonasList: [],
              TipoProducto: entrada.entrada.TipoProducto,
              SinFechaEntrada: isVentanaSinFechaChecked ? 1 : sinFechaEntradaReserva !== null && sinFechaEntradaReserva !== undefined
                ? sinFechaEntradaReserva 
                : fechaHoraEntrada ? 0 : 1,
            };
            
            // 🔍 [CANJEAR RESERVA] Log de nuevaEntrada creada (agrupada)
            if (tieneLocalizador) {
              console.log(`🔍 [CANJEAR RESERVA] [AGRUPADA] nuevaEntrada creada (i=${i}):`, {
                SinFechaEntrada: nuevaEntrada.SinFechaEntrada,
                FechaHoraEntrada: nuevaEntrada.FechaHoraEntrada,
                TipoEntradaId: nuevaEntrada.TipoEntradaId,
                ItemId: entrada.entrada.ItemId
              });
            }
            
            let Entrada = JSON.parse(JSON.stringify(nuevaEntrada));
            Entrada.EntradasVendidasPersonas =
              this.cargarEntradasVendidasPersonas(
                entrada.entrada,
                entrada.entrada,
                i
              );
            //Cargamos la procedencia si procede
            if (entrada.entrada.CuentaVisitante == '1') {
              this.cargarProcedenciaEnEntrada(
                Entrada,
                procedencias,
                numProductoProcededncia,
                0,
                numEntradaCantidadProcededncia
              );
              // Actualizamos contadores
              numEntradaCantidadProcededncia =
                numEntradaCantidadProcededncia + 1;
              totalProductosProcededncia = totalProductosProcededncia + 1;
            }
            this.generarRecintosVentaPorEntrada(
              entrada,
              nuevaEntrada,
              countEntrada,
              i
            );

            // extraData solo lo tienen las butacas
            if (
              'extraData' in entrada.entrada &&
              entrada.entrada.extraData !== undefined &&
              entrada.entrada.extraData !== null &&
              entrada.entrada.extraData.espectaculo !== null &&
              entrada.entrada.extraData.espectaculo !== undefined
            ) {
              nuevaEntrada.FechaHoraEntrada = moment(
                entrada.entrada.FechaHoraEntrada
              ).format('YYYY/MM/DD HH:mm:ss');
              nuevaEntrada.EntradasVendidasButacas = {
                EspectaculoId:
                  entrada.entrada.extraData.espectaculo.iEspectaculoId,
                RecintoId: entrada.entrada.extraData.dataIdRecinto,
                FechaHora: moment(entrada.entrada.FechaHoraEntrada).format(
                  'YYYY/MM/DD HH:mm:ss'
                ),
                Fila: entrada.entrada.extraData.dataNumFila,
                Butaca: entrada.entrada.extraData.dataNumButaca,
              };
            }
            // ListaButacas solo lo tienen las butacas recuperadas del localizador
            if (
              'ListaButacas' in entrada.entrada &&
              entrada.entrada.ListaButacas !== null &&
              entrada.entrada.ListaButacas.length > 0
            ) {
              nuevaEntrada.FechaHoraEntrada = moment(
                entrada.entrada.FechaHoraAccesoLocalizadorButaca
              ).format('YYYY/MM/DD HH:mm:ss');

              nuevaEntrada.EntradasVendidasButacas = {
                EspectaculoId: entrada.entrada.ListaButacas[i].IdEspectaculo,
                RecintoId: entrada.entrada.ListaButacas[i].IdRecinto,
                FechaHora: moment(
                  entrada.entrada.ListaButacas[i].FechaHora
                ).format('YYYY/MM/DD HH:mm:ss'),
                Fila: entrada.entrada.ListaButacas[i].NumFila,
                Butaca: entrada.entrada.ListaButacas[i].NumBuatca,
              };
            }
            if (
              Entrada.EntradasVendidasPersonas &&
              Entrada.EntradasVendidasPersonas !== ''
            )
              nuevaEntrada.EntradasVendidasPersonasList.push(
                Entrada.EntradasVendidasPersonas
              );
            entradas.push(nuevaEntrada);
          }
        }
      }
      //Procedencias
      numProductoProcededncia = numProductoProcededncia + 1;
      countEntrada++;
    });
    //
    return entradas;
  }

  private generarEntradasVendidasIndividual(): any {
    let promocionNumOrden = 0;
    let contadorVisitantesHorarios = 0;
    let _this = this;
    const datosInicio = this.configuration.datosInicioValue;
    const entradas = [];
    
    // 🔍 [CANJEAR RESERVA] Log al inicio de generarEntradasVendidasIndividual
    console.log('🔍 [CANJEAR RESERVA] ===== generarEntradasVendidasIndividual =====');
    const tieneReserva = this.venta.carritoValue.reserva && Object.keys(this.venta.carritoValue.reserva).length > 0;
    const tieneLocalizador = this.venta.carritoValue.localizador && Object.keys(this.venta.carritoValue.localizador).length > 0;
    console.log('🔍 [CANJEAR RESERVA] ¿Es canje de reserva?', {
      tieneReserva: tieneReserva,
      tieneLocalizador: tieneLocalizador,
      numEntradas: this.venta.carritoValue.entradas?.length || 0
    });
    
    if (tieneLocalizador && this.venta.carritoValue.localizador.ReservaItems) {
      console.log('🔍 [CANJEAR RESERVA] ReservaItems disponibles:', this.venta.carritoValue.localizador.ReservaItems.map(item => ({
        ItemId: item.ItemId,
        TipoProducto: item.TipoProducto,
        sinFechaEntrada: item['sinFechaEntrada'],
        FechaHoraEntrada: item.FechaHoraEntrada
      })));
    }
    
    //para modificar con procedencias
    let procedencias = this.procedenciaService.getprocedenciasValue();
    let numProductoProcededncia = 0;
    let numEntradaProductoProcededncia = 0;
    let numEntradaCantidadProcededncia = 0;
    let totalProductosProcededncia = 1;
    if (procedencias && procedencias.length > 0) {
    }
    this.venta.carritoValue.entradas.forEach((entrada) => {
      // ;
      if (
        entrada.entrada.noEditable === undefined ||
        entrada.entrada.noEditable === null ||
        entrada.entrada.noEditable === false
      ) {
        if (entrada.entrada.TipoProducto === '2') {
          let contadorNominacion = 0;

          for (let i = 0; i < entrada.cantidad; i++) {
            promocionNumOrden++;
            entrada.entrada.PromocionEntradas.forEach((val) => {
              //
              let tmpHora = '';
              if (
                val.CuentaVisitante &&
                val.CuentaVisitante == '1' &&
                this.estaenRecintoconHorarios(
                  val,
                  _this.venta.carritoValue.horarios
                )
              ) {
                //&& hay horario para este recinto
                //

                //<< Yaribel 20210122 Modificamos para que obtenga el horario del recinto que le corresponde
                //let horario = _this.venta.carritoValue.horarios[contadorVisitantesHorarios];
                let horario =
                  _this.venta.carritoValue.horarios[
                    _this.venta.carritoValue.horarios.findIndex(
                      (item) =>
                        item.recinto.TipoEntrada === val.TipoEntradaId ||
                        (item.recinto.entradas != null &&
                          item.recinto.entradas.some(
                            (itemEntrada) => itemEntrada == val.TipoEntradaId
                          ))
                    )
                  ];
                // Yaribel 20210122 >>
                tmpHora =
                  moment(horario.sesion.Fecha).format('YYYY/MM/DD') +
                  ' ' +
                  horario.sesion.HoraInicio +
                  ':00';
                //Prueba
                if (
                  val.ArgumentosCaracteristica != null &&
                  val.ArgumentosCaracteristica.length != 0
                )
                  contadorVisitantesHorarios++;
              } else {
                tmpHora = val.FechaHoraAcceso;
              }
              // TODO ojo, verificar si la fecha viene como año/mes/dia, en el caso de forfait 2 horas es asi, por ejemplo
              for (let j = 0; j < val.NumeroEntradas; j++) {
                // 🔍 [CANJEAR RESERVA] Log antes de crear nuevaEntrada
                if (tieneLocalizador) {
                  const reservaItem = this.venta.carritoValue.localizador.ReservaItems?.find(
                    item => item.ItemId === entrada.entrada.ItemId || item.ItemId === entrada.entrada.TipoPromocionId
                  );
                  console.log(`🔍 [CANJEAR RESERVA] Creando entrada para promoción (i=${i}, j=${j}):`, {
                    ItemId: entrada.entrada.ItemId,
                    TipoPromocionId: entrada.entrada.TipoPromocionId,
                    reservaItemEncontrado: !!reservaItem,
                    reservaItemSinFechaEntrada: reservaItem?.['sinFechaEntrada'],
                    reservaItemFechaHoraEntrada: reservaItem?.FechaHoraEntrada,
                    tmpHora: tmpHora,
                    valFechaHoraAcceso: val.FechaHoraAcceso
                  });
                }
                
                const nuevaEntrada = {
                  PrecioPagado: Number.parseFloat(
                    val.PrecioEnDivisaReferencia
                      ? val.PrecioEnDivisaReferencia.replace(',', '.')
                      : val.PrecioTotal.replace(',', '.')
                  ),
                  DivisaId: datosInicio.DivisaRefId,
                  NombreTipo: val.NombreTipo,
                  TipoEntradaId: val.TipoEntradaId,
                  //FechaHoraEntrada: val.FechaHoraAcceso,
                  FechaHoraEntrada: tmpHora,
                  /* FechaHoraEntrada: moment(
                    val.FechaHoraAcceso,
                    'DD/MM/YYYY HH:mm:ss'
                  ).format('YYYY/MM/DD HH:mm:ss'), */
                  Operacion: 'SA',
                  BaseImponible: Number.parseFloat(
                    val.PrecioBase.replace(',', '.')
                  ),
                  IVA: val.IVA,
                  Devuelta: 0, // en venta siempre será 0,
                  TipoAbreviado: '',
                  AbonadoId: 0, // no aplica
                  ClienteAsociadoId: 0, // no aplica
                  OrdenCjta: 0, // no aplica
                  Comision: Number.parseFloat(
                    entrada.entrada.Comision.replace(',', '.')
                  ), // campo comision
                  IvaComision: Number.parseFloat(
                    entrada.entrada.IvaComision.replace(',', '.')
                  ), // campo iva comision,
                  BonoTexto: '',
                  DNIAbonadosExt: String(entrada.entrada.DNIAbonadosExt || ''),
                  Identificacion: '',
                  CodigoPromocional: entrada.codigoPromocional,
                  IdTarifa: entrada.entrada.idTarifa,
                  PromocionId: entrada.entrada.TipoPromocionId,
                  PromocionNombre: entrada.entrada.NombreTipo,
                  PromocionNumOrden: promocionNumOrden,
                  TarjetaMonederoCodigo:
                    val.Caracteristicas !== undefined &&
                    val.Caracteristicas !== null &&
                    val.Caracteristicas.UsoMonedero === '1'
                      ? val.EntradasVendidasPersonas[contadorNominacion]
                          .TarjetaMonederoCodigo
                      : '',
                  EntradasVendidasPersonas: this.cargarEntradasVendidasPersonas(
                    entrada.entrada,
                    val,
                    contadorNominacion
                  ),
                  // val.EntradasVendidasPersonas !== undefined
                  // //? this.generarNominativas(val.EntradasVendidasPersonas[j])
                  // //? this.generarNominativas(val.EntradasVendidasPersonas.length > val.NumeroEntradas && entrada.cantidad > val.NumeroEntradas ? val.EntradasVendidasPersonas[i] : val.EntradasVendidasPersonas[j])
                  // ?  this.generarNominativas(val.EntradasVendidasPersonas[contadorNominacion]) //i+j
                  // : (entrada.entrada.FiltrosCaracteristicas && (val.CuentaNominada == "1" || val.CuentaVisitante =="1") ? this.generarNominativasxFiltroCaracteristica(entrada.entrada.FiltrosCaracteristicas[contadorNominacion]): '')
                  // //Controlamos como se mandan varios bonos como array dentro de las entradas, pero con la cantidad en el bono
                };
                if (val.CuentaNominada == '1' || val.CuentaVisitante == '1') {
                  contadorNominacion++;
                }
                //Cargamos la procedencia si procede
                if (val.CuentaVisitante == '1') {
                  this.cargarProcedenciaEnEntrada(
                    nuevaEntrada,
                    procedencias,
                    numProductoProcededncia,
                    numEntradaProductoProcededncia,
                    numEntradaCantidadProcededncia
                  );
                  // Actualizamos contadores
                  numEntradaCantidadProcededncia =
                    numEntradaCantidadProcededncia + 1;
                  totalProductosProcededncia = totalProductosProcededncia + 1;
                }
                //
                entradas.push(nuevaEntrada);
              }
              numEntradaProductoProcededncia++;
              if (
                entrada.entrada.CaracteristicasEntrada != null &&
                entrada.entrada.CaracteristicasEntrada.length != 0 &&
                !(
                  entrada.entrada.PromocionEntradas != null &&
                  entrada.entrada.PromocionEntradas.length != 0
                )
              ) {
                contadorVisitantesHorarios++;
              }
            });
          }
        } else if (entrada.entrada.TipoProducto === '1') {
          for (let i = 0; i < entrada.cantidad; i++) {
            // 🔍 [CANJEAR RESERVA] Buscar ReservaItem correspondiente para heredar fechas
            let reservaItem = null;
            let fechaHoraEntradaReserva = '';
            let sinFechaEntradaReserva = null;
            if (tieneLocalizador && this.venta.carritoValue.localizador.ReservaItems) {
              reservaItem = this.venta.carritoValue.localizador.ReservaItems.find(
                item => item.ItemId === entrada.entrada.ItemId || item.ItemId === entrada.entrada.TipoEntradaId
              );
              if (reservaItem) {
                // 🔍 [CANJEAR RESERVA] Obtener valores de PosicionesVenta[0] si existe (nuevo cambio: ahora vienen de PosicionesVenta)
                const posicionVenta = reservaItem.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
                  ? reservaItem.PosicionesVenta[0] 
                  : null;
                
                // Heredar sinFechaEntrada de PosicionesVenta[0] si existe (buscar tanto SinFechaEntrada como sinFechaEntrada, y convertir número a string)
                const sinFechaPosicionVenta = posicionVenta?.SinFechaEntrada !== undefined ? posicionVenta.SinFechaEntrada : 
                                             (posicionVenta?.sinFechaEntrada !== undefined ? posicionVenta.sinFechaEntrada : undefined);
                const sinFechaPosicionVentaStr = sinFechaPosicionVenta !== undefined && sinFechaPosicionVenta !== null 
                  ? String(sinFechaPosicionVenta) 
                  : '';
                
                console.log(`🔍 [CANJEAR RESERVA] [INDIVIDUAL] Creando entrada individual (i=${i}):`, {
                  ItemId: entrada.entrada.ItemId,
                  TipoEntradaId: entrada.entrada.TipoEntradaId,
                  reservaItemEncontrado: !!reservaItem,
                  posicionVenta: posicionVenta,
                  posicionVentaSinFechaEntrada: posicionVenta?.SinFechaEntrada || posicionVenta?.sinFechaEntrada,
                  posicionVentaFechaHoraEntrada: posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada,
                  sinFechaPosicionVentaStr: sinFechaPosicionVentaStr,
                  reservaItemSinFechaEntrada: reservaItem?.['sinFechaEntrada'], // Fallback
                  reservaItemFechaHoraEntrada: reservaItem?.FechaHoraEntrada, // Fallback
                  entradaFecha: entrada.entrada.Fecha,
                  entradaFechaHoraEntrada: entrada.entrada.FechaHoraEntrada
                });
                
                if (posicionVenta && sinFechaPosicionVentaStr !== '') {
                  sinFechaEntradaReserva = sinFechaPosicionVentaStr;
                } else if (reservaItem['sinFechaEntrada'] !== undefined && reservaItem['sinFechaEntrada'] !== null && reservaItem['sinFechaEntrada'] !== '') {
                  // Fallback al ReservaItem si PosicionesVenta no tiene el valor
                  sinFechaEntradaReserva = reservaItem['sinFechaEntrada'];
                } else {
                  // Inferir de FechaHoraEntrada (priorizar PosicionesVenta, luego ReservaItem)
                  const fechaHoraParaInferir = posicionVenta?.FechaHoraEntrada || reservaItem.FechaHoraEntrada;
                  if (!fechaHoraParaInferir || fechaHoraParaInferir === '' || fechaHoraParaInferir === null) {
                    sinFechaEntradaReserva = '1'; // Sin fecha
                  } else {
                    // Si tiene fecha pero el localizador dice sin fecha, priorizar el localizador
                    if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && 
                        this.venta.carritoValue.localizador.SinFechaEntrada !== null &&
                        this.venta.carritoValue.localizador.SinFechaEntrada === '1') {
                      sinFechaEntradaReserva = '1'; // Usar el del localizador (sin fecha)
                    } else {
                      sinFechaEntradaReserva = '0'; // Con fecha
                    }
                  }
                }
              } else if (tieneLocalizador && this.venta.carritoValue.localizador.SinFechaEntrada !== undefined && this.venta.carritoValue.localizador.SinFechaEntrada !== null) {
                // Si no se encuentra el ReservaItem, usar el del localizador principal
                sinFechaEntradaReserva = this.venta.carritoValue.localizador.SinFechaEntrada;
              }
            }
            
            //
            //   moment(entrada.entrada.FechaHoraAcceso).format(
            //     'YYYY/MM/DD HH:mm:ss'
            //   )
            // );
            let tmpHora = '';
            if (
              entrada.entrada.CuentaVisitante &&
              entrada.entrada.CuentaVisitante == '1' &&
              this.estaenRecintoconHorarios(
                entrada.entrada,
                _this.venta.carritoValue.horarios
              )
            ) {
              //&& hay horario para este recinto
              //
              //<< Yaribel 20210122 Modificamos para que obtenga el horario del recinto que le corresponde
              //let horario = _this.venta.carritoValue.horarios[contadorVisitantesHorarios];
              let horario =
                _this.venta.carritoValue.horarios[
                  _this.venta.carritoValue.horarios.findIndex(
                    (item) =>
                      item.recinto.TipoEntrada ===
                        entrada.entrada.TipoEntradaId ||
                      (item.recinto.entradas != null &&
                        item.recinto.entradas.some(
                          (itemEntrada) =>
                            itemEntrada == entrada.entrada.TipoEntradaId
                        ))
                  )
                ];
              // Yaribel 20210122 >>
              tmpHora =
                moment(horario.sesion.Fecha).format('YYYY/MM/DD') +
                ' ' +
                horario.sesion.HoraInicio +
                ':00';
              //Prueba
              if (
                (entrada.entrada.ArgumentosCaracteristica != null &&
                  entrada.entrada.ArgumentosCaracteristica.length != 0) ||
                (entrada.entrada.CaracteristicasEntrada != null &&
                  entrada.entrada.CaracteristicasEntrada.length != 0)
              )
                contadorVisitantesHorarios++;
            } else {
              tmpHora = entrada.entrada.FechaHoraAcceso;
            }
            
            // 🔍 [CANJEAR RESERVA] Usar FechaHoraEntrada de PosicionesVenta[0] si existe (nuevo cambio: ahora viene de PosicionesVenta)
            const posicionVenta = reservaItem?.PosicionesVenta && reservaItem.PosicionesVenta.length > 0 
              ? reservaItem.PosicionesVenta[0] 
              : null;
            
            const fechaHoraPosicionVenta = posicionVenta?.FechaHoraEntrada || posicionVenta?.fechaEntrada;
            if (posicionVenta && fechaHoraPosicionVenta && fechaHoraPosicionVenta !== '' && fechaHoraPosicionVenta !== null) {
              fechaHoraEntradaReserva = moment(fechaHoraPosicionVenta).isValid()
                ? moment(fechaHoraPosicionVenta).format('YYYY/MM/DD HH:mm:ss')
                : fechaHoraPosicionVenta;
            } else if (reservaItem && reservaItem.FechaHoraEntrada && reservaItem.FechaHoraEntrada !== '' && reservaItem.FechaHoraEntrada !== null) {
              // Fallback al ReservaItem si PosicionesVenta no tiene el valor
              fechaHoraEntradaReserva = moment(reservaItem.FechaHoraEntrada).isValid()
                ? moment(reservaItem.FechaHoraEntrada).format('YYYY/MM/DD HH:mm:ss')
                : reservaItem.FechaHoraEntrada;
            } else {
              fechaHoraEntradaReserva = moment(tmpHora).isValid() ? moment(tmpHora).format('YYYY/MM/DD HH:mm:ss') : '';
            }
            
            // 🔍 [CANJEAR RESERVA] Log de valores calculados antes de crear nuevaEntrada (individual)
            if (tieneLocalizador) {
              console.log(`🔍 [CANJEAR RESERVA] [INDIVIDUAL] Valores calculados para entrada (ItemId: ${entrada.entrada.ItemId || entrada.entrada.TipoEntradaId}, i=${i}):`, {
                reservaItemEncontrado: !!reservaItem,
                reservaItemId: reservaItem?.ItemId,
                reservaItemSinFechaEntrada: reservaItem?.['sinFechaEntrada'],
                reservaItemFechaHoraEntrada: reservaItem?.FechaHoraEntrada,
                sinFechaEntradaReserva: sinFechaEntradaReserva,
                fechaHoraEntradaReserva: fechaHoraEntradaReserva,
                tmpHora: tmpHora,
                localizadorSinFechaEntrada: this.venta.carritoValue.localizador.SinFechaEntrada,
                entradaFechaHoraAcceso: entrada.entrada.FechaHoraAcceso,
                entradaFecha: entrada.entrada.Fecha,
                entradaFechaHoraEntrada: entrada.entrada.FechaHoraEntrada
              });
            }
            
            const nuevaEntrada: any = {
              PrecioPagado: Number.parseFloat(
                entrada.entrada.PrecioEnDivisaReferencia.replace(',', '.')
              ),
              DivisaId: datosInicio.DivisaRefId,
              NombreTipo: entrada.entrada.NombreTipo,
              TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
              ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
              : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
              // FechaHoraEntrada: moment(entrada.entrada.FechaHoraAcceso).format(
              //   'YYYY/MM/DD HH:mm:ss'
              // ),
              FechaHoraEntrada: fechaHoraEntradaReserva,
              Operacion: 'SA',
              BaseImponible: Number.parseFloat(
                entrada.entrada.PrecioBase.replace(',', '.')
              ),
              IVA: entrada.entrada.IVA,
              Devuelta: 0, // en venta siempre será 0,
              TipoAbreviado: '',
              AbonadoId: 0, // no aplica
              ClienteAsociadoId: 0, // no aplica
              OrdenCjta: 0, // no aplica
              Comision: Number.parseFloat(
                entrada.entrada.Comision.replace(',', '.')
              ), // campo comision
              IvaComision: Number.parseFloat(
                entrada.entrada.IvaComision.replace(',', '.')
              ), // campo iva comision,
              BonoTexto: '',
              DNIAbonadosExt: String(entrada.entrada.DNIAbonadosExt || ''),
              Identificacion: '',
              IdTarifa: entrada.entrada.idTarifa,
              CodigoPromocional: entrada.codigoPromocional,
              TarjetaMonederoCodigo:
                entrada.entrada.Caracteristicas !== undefined &&
                entrada.entrada.Caracteristicas !== null &&
                entrada.entrada.Caracteristicas.UsoMonedero === '1'
                  ? entrada.entrada.EntradasVendidasPersonas[i]
                    ? entrada.entrada.EntradasVendidasPersonas[i]
                        .TarjetaMonederoCodigo
                    : ''
                  : '',
              EntradasVendidasPersonas: this.cargarEntradasVendidasPersonas(
                entrada.entrada,
                entrada.entrada,
                i
              ),
              SinFechaEntrada: sinFechaEntradaReserva !== null && sinFechaEntradaReserva !== undefined
                ? sinFechaEntradaReserva 
                : (fechaHoraEntradaReserva === '' || fechaHoraEntradaReserva === null ? 1 : 0),
            };
            
            // 🔍 [CANJEAR RESERVA] Log de nuevaEntrada creada (individual)
            if (tieneLocalizador) {
              console.log(`🔍 [CANJEAR RESERVA] [INDIVIDUAL] nuevaEntrada creada (i=${i}):`, {
                SinFechaEntrada: nuevaEntrada.SinFechaEntrada,
                FechaHoraEntrada: nuevaEntrada.FechaHoraEntrada,
                TipoEntradaId: nuevaEntrada.TipoEntradaId,
                ItemId: entrada.entrada.ItemId
              });
            }
            
            //Cargamos la procedencia si procede
            if (entrada.entrada.CuentaVisitante == '1') {
              this.cargarProcedenciaEnEntrada(
                nuevaEntrada,
                procedencias,
                numProductoProcededncia,
                0,
                numEntradaCantidadProcededncia
              );
              // Actualizamos contadores
              numEntradaCantidadProcededncia =
                numEntradaCantidadProcededncia + 1;
              totalProductosProcededncia = totalProductosProcededncia + 1;
            }
            //
            //
            // extraData solo lo tienen las butacas
            if (
              'extraData' in entrada.entrada &&
              entrada.entrada.extraData !== undefined &&
              entrada.entrada.extraData !== null &&
              entrada.entrada.extraData.espectaculo !== null &&
              entrada.entrada.extraData.espectaculo !== undefined
            ) {
              nuevaEntrada.FechaHoraEntrada = moment(
                entrada.entrada.FechaHoraEntrada
              ).format('YYYY/MM/DD HH:mm:ss');
              nuevaEntrada.EntradasVendidasButacas = {
                EspectaculoId:
                  entrada.entrada.extraData.espectaculo.iEspectaculoId,
                RecintoId: entrada.entrada.extraData.dataIdRecinto,
                FechaHora: moment(entrada.entrada.FechaHoraEntrada).format(
                  'YYYY/MM/DD HH:mm:ss'
                ),
                Fila: entrada.entrada.extraData.dataNumFila,
                Butaca: entrada.entrada.extraData.dataNumButaca,
              };
            }
            // ListaButacas solo lo tienen las butacas recuperadas del localizador
            if (
              'ListaButacas' in entrada.entrada &&
              entrada.entrada.ListaButacas !== null &&
              entrada.entrada.ListaButacas.length > 0
            ) {
              //
              nuevaEntrada.FechaHoraEntrada = moment(
                entrada.entrada.FechaHoraAccesoLocalizadorButaca
              ).format('YYYY/MM/DD HH:mm:ss');

              nuevaEntrada.EntradasVendidasButacas = {
                EspectaculoId: entrada.entrada.ListaButacas[i].IdEspectaculo,
                RecintoId: entrada.entrada.ListaButacas[i].IdRecinto,
                FechaHora: moment(
                  entrada.entrada.ListaButacas[i].FechaHora
                ).format('YYYY/MM/DD HH:mm:ss'),
                Fila: entrada.entrada.ListaButacas[i].NumFila,
                Butaca: entrada.entrada.ListaButacas[i].NumBuatca,
              };
            }
            entradas.push(nuevaEntrada);
          }
        }
      }
      //Procedencias
      numProductoProcededncia = numProductoProcededncia + 1;
    });
    
    // 🔍 [CANJEAR RESERVA] Log al final de generarEntradasVendidasIndividual
    console.log('🔍 [CANJEAR RESERVA] Entradas generadas (individual):', entradas.length);
    if (entradas.length > 0 && tieneLocalizador) {
      console.log('🔍 [CANJEAR RESERVA] Muestra de primeras 3 entradas:', entradas.slice(0, 3).map(e => ({
        TipoEntradaId: e.TipoEntradaId,
        FechaHoraEntrada: e.FechaHoraEntrada,
        SinFechaEntrada: e.SinFechaEntrada
      })));
    }
    console.log('🔍 [CANJEAR RESERVA] ===========================================');
    
    return entradas;
  }
  private generarEntradasVendidasConjunta() {
    const entradas = this.venta.carritoValue.entradas.map((linea, index) => {
      const nuevasEntradas = [];
      for (let i = 0; i < linea.cantidad; i++) {
        const nuevaEntrada = {
          PrecioPagado:
            Number.parseFloat(
              linea.entrada.PrecioEnDivisaReferencia.replace(/,/, '.')
            ) -
            (Number.parseFloat(linea.entrada.IVA) / 100) *
              Number.parseFloat(
                linea.entrada.PrecioEnDivisaReferencia.replace(/,/, '.')
              ),
          DivisaId: this.turnoService.turnoValue.DivisaId,
          NombreTipo: linea.entrada.NombreTipo,
          TipoEntradaId: linea.entrada.TipoEntradaId,
          // tslint:disable-next-line:max-line-length
          FechaHoraEntrada:
            moment(linea.entrada.Fecha).format('YYYY/MM/DD') +
            ' ' +
            this.venta.carritoValue.fecha.split(' ')[1],
          Operacion: 'SA',
          BaseImponible: linea.entrada.PrecioBase.replace(',', '.'),
          IVA: linea.entrada.IVA,
          Devuelta: 0, // en venta siempre será 0,
          TipoAbreviado: '',
          AbonadoId: 0, // no aplica
          ClienteAsociadoId: 0, // no aplica
          OrdenCjta: 1 + index, // no aplica
          Comision: linea.entrada.Comision, // campo comision
          IvaComision: linea.entrada.IvaComision, // campo iva comision,
          BonoTexto: '',
          DNIAbonadosExt: linea.entrada.DNIAbonadosExt || '',
          Identificacion: '',
        };
        if (linea.entrada.noEditable === null || !linea.entrada.noEditable) {
          nuevasEntradas.push(nuevaEntrada);
        }
      }
      //
      return nuevasEntradas;
    });
    return entradas.flat(1);
  }

  generarRecintosVenta(): any[] {
    let recintos = [];
    let countEntrada = 0;
    let conuntCaracteristica = 0;
    this.venta.carritoValue.entradas.forEach((entrada) => {
      if (
        entrada.entrada.noEditable === undefined ||
        entrada.entrada.noEditable === null ||
        entrada.entrada.noEditable === false
      ) {
        // ;
        // extraData solo lo tienen las butacas compradas en taquilla
        if (
          'extraData' in entrada.entrada &&
          entrada.entrada.extraData !== undefined &&
          entrada.entrada.extraData !== null &&
          entrada.entrada.extraData.espectaculo !== null &&
          entrada.entrada.extraData.espectaculo !== undefined
        ) {
          //
          recintos.push({
            TipoSesionId: entrada.entrada.extraData.espectaculo.idTipoSesion,
            TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
              ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
              : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
            recintoId: entrada.entrada.extraData.dataIdRecinto,
            fechaEntrada: moment(entrada.entrada.FechaHoraEntrada).isValid() 
              ? moment(entrada.entrada.FechaHoraEntrada).format('YYYY/MM/DD HH:mm:ss')
              : (entrada.entrada.Fecha || ''),
            EspectaculoId: entrada.entrada.extraData.espectaculo.iEspectaculoId,
          });
        }
        // ListaButacas solo lo tienen las butacas recuperadas del localizador
        if (
          'ListaButacas' in entrada.entrada &&
          entrada.entrada.ListaButacas !== null &&
          entrada.entrada.ListaButacas.length > 0
        ) {
          entrada.entrada.ListaButacas.forEach((butaca) => {
            recintos.push({
              TipoSesionId: butaca.IdTipoSesion,
              TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
              ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
              : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
              recintoId: butaca.IdRecinto,
              fechaEntrada: butaca.FechaHora,
              EspectaculoId: butaca.IdEspectaculo,
            });
          });
        }

        if (
          entrada.entrada.recintos !== undefined &&
          entrada.entrada.recintos !== null &&
          entrada.entrada.recintos.length > 0
        ) {
          entrada.entrada.recintos.forEach((recinto) => {
            if (recinto.EsActividad === '0' && recinto.ControlaAforo === '0') {
              recintos.push({
                TipoEntradaId: recinto.TipoEntrada,
                recintoId: recinto.Recinto,
                fechaEntrada: moment(entrada.entrada.FechaHoraAcceso).isValid() 
                  ? moment(entrada.entrada.FechaHoraAcceso).format('YYYY/MM/DD HH:mm:ss')
                  : (entrada.entrada.FechaHoraEntrada || entrada.entrada.Fecha || ''),
              });
            } else if (
              recinto.EsActividad === '0' &&
              recinto.ControlaAforo === '1'
            ) {
              if (this.venta.carritoValue.horarios.length > 0) {
                this.venta.carritoValue.horarios.forEach((item) => {
                  let existeEntrada = false;

                  if (
                    item.recinto &&
                    item.recinto.entradas &&
                    Array.isArray(item.recinto.entradas)
                  ) {
                    existeEntrada = item.recinto.entradas.includes(
                      recinto.TipoEntrada
                    );
                  }
                  if (
                    item.recinto.Recinto === recinto.Recinto &&
                    existeEntrada
                  ) {
                    if (
                      entrada.entrada.CaracteristicasEntrada &&
                      entrada.entrada.CaracteristicasEntrada !== null &&
                      entrada.entrada.CaracteristicasEntrada.length > 0
                    ) {
                      if (
                        item.indiceEntrada == countEntrada &&
                        item.indiceCaracteristica == conuntCaracteristica
                      ) {
                        recintos.push({
                          TipoEntradaId: recinto.TipoEntrada,
                          recintoId: recinto.Recinto,
                          fechaEntrada:
                            moment(item.sesion.Fecha).format('YYYY/MM/DD') +
                            ' ' +
                            item.sesion.HoraInicio +
                            ':00',
                          Guia:
                            item.GuiaSeleccionado &&
                            item.GuiaSeleccionado.iGuiaId
                              ? item.GuiaSeleccionado.iGuiaId
                              : '',
                          RecintosSesionesId: item.sesion.RecintosSesionId,
                          CaracteristicaId: item.indiceCaracteristica,
                        });
                        conuntCaracteristica++;
                        // if (conuntCaracteristica >= entrada.entrada.CaracteristicasEntrada.length)
                        // {
                        //   conuntCaracteristica = 0;
                        //   countEntrada++;
                        // }
                      }
                    } else {
                      recintos.push({
                        TipoEntradaId: recinto.TipoEntrada,
                        recintoId: recinto.Recinto,
                        fechaEntrada:
                          moment(item.sesion.Fecha).format('YYYY/MM/DD') +
                          ' ' +
                          item.sesion.HoraInicio +
                          ':00',
                        Guia:
                          item.GuiaSeleccionado && item.GuiaSeleccionado.iGuiaId
                            ? item.GuiaSeleccionado.iGuiaId
                            : '',
                        RecintosSesionesId: item.sesion.RecintosSesionId,
                        CaracteristicaId: item.indiceCaracteristica,
                      });
                    }
                  }
                });
              }
            } else if (
              recinto.EsActividad === '1' &&
              recinto.ControlaAforo === '1'
            ) {
              if (this.venta.carritoValue.horarios.length > 0) {
                this.venta.carritoValue.horarios.forEach((item) => {
                  let existeEntrada = false;

                  if (
                    item.recinto &&
                    item.recinto.entradas &&
                    Array.isArray(item.recinto.entradas)
                  ) {
                    existeEntrada = item.recinto.entradas.includes(
                      recinto.TipoEntrada
                    );
                  }
                  if (
                    item.recinto.Recinto === recinto.Recinto &&
                    existeEntrada
                  ) {
                    recintos.push({
                      TipoEntradaId: recinto.TipoEntrada,
                      recintoId: recinto.Recinto,
                      fechaEntrada:
                        moment(item.sesion.Fecha).format('YYYY/MM/DD') +
                        ' ' +
                        item.sesion.HoraInicio +
                        ':00',
                    });
                  }
                });
              }
            }
          });
        }
      }
      conuntCaracteristica = 0;
      countEntrada++;
    });
    // quitamos duplicados
    recintos = recintos.filter(
      (recinto, index, self) =>
        index ===
        self.findIndex(
          (r) =>
            r.TipoEntradaId === recinto.TipoEntradaId &&
            r.recintoId === recinto.recintoId &&
            r.fechaEntrada === recinto.fechaEntrada &&
            r.Guia === recinto.Guia //&&
          //r.CaracteristicaId === recinto.CaracteristicaId
        )
    );

    /*     const fechasRecintos = [];
    recintos.forEach(recinto => {
      fechasRecintos.push(recinto.fechaEntrada);
    });
    const moments = fechasRecintos.map(d => moment(d, 'YYYY/MM/DD HH:mm:ss'));
    const fechaMinima = moment.min(moments).format('YYYY/MM/DD HH:mm:ss');
    
    this.venta.carritoValue.FechaHoraAcceso = fechaMinima; */
    return recintos;
  }

  private generarAforosVenta(): any {
    const res = [];
    this.venta.carritoValue.horarios.forEach((infoHorario) => {
      if (!infoHorario.EsButaca) {
        if (
          infoHorario.recinto.ControlaAforo !== '0' ||
          infoHorario.recinto.ControlaAforo !== null ||
          infoHorario.recinto.ControlaAforo !== undefined
        ) {
          res.push({
            RecintoId: infoHorario.recinto.Recinto,
            FechaHora:
              moment(infoHorario.sesion.Fecha).format('YYYY/MM/DD') +
              ' ' +
              infoHorario.sesion.HoraInicio +
              ':00',
            cantidad: infoHorario.cantidad,
          });
        }
      }
    });
    return res;
  }
  private generarMotivosGrupos(): any[] {
    const motivosArr = [];
    this.venta.carritoValue.entradas.forEach((entrada) => {
      // ;
      if (entrada.descuentos.length > 0) {
        if (entrada.entrada.TipoProducto == '1') {
          entrada.descuentos.forEach((desc) => {
            motivosArr.push({
              turnoId: this.turnoService.turnoValue.pkid,
              usuarioId: this.turnoService.turnoValue.UsuarioId,
              numMotivos: entrada.cantidad,
              itemId: desc.motivoDescuento.PkId,
              TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
              ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
              : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
            });
          });
        } else if (entrada.entrada.TipoProducto == '2') {
          entrada.entrada.PromocionEntradas.forEach((item) => {
            entrada.descuentos.forEach((desc) => {
              if (item.GrupoMotivoId === desc.motivoDescuento.GrupoId) {
                motivosArr.push({
                  turnoId: this.turnoService.turnoValue.pkid,
                  usuarioId: this.turnoService.turnoValue.UsuarioId,
                  numMotivos:
                    entrada.cantidad * parseInt(item.NumeroEntradas, 10),
                  itemId: desc.motivoDescuento.PkId,
                  TipoEntradaId: item.TipoEntradaId,
                });
              }
            });
          });
        }
      } else {
        //
        if (
          entrada.entrada.TipoEntradaId &&
          entrada.entrada.TipoEntradaId != '' &&
          entrada.entrada.TipoEntradaId != '0'
        ) {
          const tipoEntradaIdPayload = entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0
            ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId
            : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0');
          const motivoSel = (this.motivoService.motivosSeleccionadosValue || []).find((m) => m.idEntrada === entrada.entrada.idTarifa);
          const usarMotivosSeleccionados = motivoSel && motivoSel.lista && motivoSel.lista.length > 0 &&
            (!entrada.entrada.ListaMotivos || motivoSel.lista.length >= entrada.entrada.ListaMotivos.length);
          if (usarMotivosSeleccionados) {
            motivoSel.lista.forEach((motivoItem) => {
              motivosArr.push({
                turnoId: this.turnoService.turnoValue.pkid,
                usuarioId: this.turnoService.turnoValue.UsuarioId,
                numMotivos: motivoItem.numMotivo || 0,
                itemId: motivoItem.PkId,
                TipoEntradaId: tipoEntradaIdPayload,
              });
            });
          } else if (
            entrada.entrada.ListaMotivos &&
            entrada.entrada.ListaMotivos.length > 0
          ) {
            entrada.entrada.ListaMotivos.forEach((motivo) => {
              motivosArr.push({
                turnoId: this.turnoService.turnoValue.pkid,
                usuarioId: this.turnoService.turnoValue.UsuarioId,
                numMotivos: motivo.Cantidad,
                itemId: motivo.MotivoId,
                TipoEntradaId: tipoEntradaIdPayload,
              });
            });
          }
        } else if (
          entrada.entrada.TipoPromocionId &&
          entrada.entrada.TipoPromocionId != '' &&
          entrada.entrada.TipoPromocionId != '0'
        ) {
          entrada.entrada.PromocionEntradas.forEach((item) => {
            this.motivoService.motivosSeleccionadosValue.forEach((motivo) => {
              if (
                entrada.entrada.idTarifa == motivo.idEntrada &&
                item.GrupoMotivoId &&
                item.GrupoMotivoId != ''
              ) {
                motivo.lista.forEach((motivoItem) => {
                  motivosArr.push({
                    turnoId: this.turnoService.turnoValue.pkid,
                    usuarioId: this.turnoService.turnoValue.UsuarioId,
                    numMotivos:
                      motivoItem.numMotivo * parseInt(item.NumeroEntradas, 10),
                    itemId: motivoItem.PkId,
                    TipoEntradaId: item.TipoEntradaId,
                  });
                });
              }
            });
          });
        }
      }
    });
    return motivosArr;
  }

  private generarCajasTurnoCantidad(): any[] {
    return this.turnoService.cajasValue.map((caja) => {
      const findMedioPago = this.generarMediosPagoVenta().find(
        (medioDePago) =>
          medioDePago && caja && medioDePago.FOP === caja.MedioPago
      );

      return {
        CajasTurnoId: caja.pkid,
        Cantidad:
          (this.generarMediosPagoVenta().find(
            (medioDePago) => medioDePago.FOP === caja.MedioPago
          ) &&
            this.generarMediosPagoVenta().find(
              (medioDePago) => medioDePago.FOP === caja.MedioPago
            ).Importe) ||
          0,
      };
    });
  }

  private generarResumenTurno(importeTotalVenta?: number): any {
    let totalEntradas = 0;
    this.venta.carritoValue.entradas.forEach((entrada) => {
      if (entrada.entrada.TipoProducto === '2') {
        entrada.entrada.PromocionEntradas.forEach((val) => {
          totalEntradas =
            totalEntradas + parseInt(val.NumeroEntradas, 10) * entrada.cantidad;
        });
      } else if (entrada.entrada.TipoProducto === '1') {
        totalEntradas = totalEntradas + parseInt(entrada.cantidad, 10);
      }
    });
    return {
      TurnoId: this.turnoService.turnoValue.pkid,
      UsuarioId: this.turnoService.turnoValue.UsuarioId,
      TPVId: this.turnoService.turnoValue.TpvId,
      NumEntradas: totalEntradas,
      IngresoAlbaranes: importeTotalVenta ?? this.venta.carritoValue.precioTotal,
    };
  }

  generarPedidoTarjeta() {
    const conexionId = JSON.parse(localStorage.getItem('conexionId'));
    const body = {
      ConexionIacpos: conexionId,
      Plataforma: '2',
      Prefijo: '',
      TPVId: this.turnoService.turnoValue.TpvId,
      EsContingencia: '0',
    };
    return this.http
      .post(
        `${this.config.getConfig(
          'API_URL'
        )}api/VentaTaquilla/GenerarNumAlbaranIdentificador`,
        body
      )
      .pipe(
        map((res: any) => {
          if (!res.DatosResult) {
            this.toastService.error(
              'Error al generar el albarán de identificación',
              'Error de conexión',
              { toastComponent: ErrorToast }
            );
            return {};
          }
          this.datosTarjeta = res.DatosResult;
          return res.DatosResult;
        })
      );
  }

  datosCabecera() {
    const conexionId = JSON.parse(localStorage.getItem('conexionId'));
    const body = {
      ConexionIacpos: conexionId,
    };
    return this.http
      .post(
        `${this.config.getConfig(
          'API_URL'
        )}api/Configuracion/GetCabeceraListados`,
        body
      )
      .pipe(
        map((res: any) => {
          if (res && res.DatosResult) return res.DatosResult;
        })
      );
  }

  carritoAplicarCodigoPromocional(carrito, codigoPromocional) {
    const conexionId = JSON.parse(localStorage.getItem('conexionId'));
    const body = {
      ConexionIacpos: conexionId,
      carrito: carrito,
      CodigoPromocional: codigoPromocional,
    };
    return this.http
      .post(
        `${this.config.getConfig(
          'API_URL'
        )}api/VentaTaquilla/CarritoAplicarCodigoPromocional`,
        body
      )
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  generarFechaHoraEntrada() {}

  generarNominativas(formulario) {
    //
    return {
      idListaBlanca: null,
      nombre: formulario.nombre || null,
      apellido1: formulario.apellidos || null,
      apellido2: null,
      cifoDni: formulario.documento || null,
      domicilio: null,
      tipoCalle: null,
      numCalle: null,
      piso: null,
      localidad: null,
      cp: formulario.codigoPostal || '',
      provincia: null,
      pais: null,
      telefono: formulario.telefono || null,
      telefonoMovil: null,
      fax: null,
      email: formulario.email || null,
      fechaNacimiento: null,
      sexo: formulario.sexo || 'H',
      tipoCentro: formulario.Tipo
        ? formulario.Tipo || null
        : formulario.tipoCentro
        ? formulario.tipoCentro || null
        : formulario.tipoTabla || null, //tipoTabla
      edad: formulario.edad || null,
      rangoEdad: formulario.Rango
        ? formulario.Rango || null
        : formulario.rangoEdad || null,
      minusvalido: 0,
      jubilado: 0,
      publicidadInterna: 0,
      publicidadExterna: 0,
      nombreResponsable: null,
      apellido1Responsable: null,
      apellido2Responsable: null,
      nota: null,
      audioguia: 0,
      publicidadOrganizador: 0,
      curso: formulario.Nivel
        ? formulario.Nivel || null
        : formulario.curso || null,
      lse: 0,
      pregunta1: null,
      respuesta1: formulario.dia || null,
      pregunta2: null,
      respuesta2: formulario.trimestre || null,
      pregunta3: null,
      respuesta3: formulario.tipoDocumento || null,
      pregunta4: null,
      respuesta4: formulario.nidLocalizadorExterno
        ? formulario.nidLocalizadorExterno || null
        : formulario.respuesta4 || null,
      nombreCentro: null,
      nacionalidad: null,
      politicaPrivacidad: null,
      GuiaExterno: formulario.Profesor || null,
      idxEntrada: formulario.idxEntrada || null,
      idContacto: formulario.idContacto || null,
      CodigoAsignado: formulario.nidClienteExterno
        ? formulario.nidClienteExterno || null
        : formulario.CodigoAsignado || null,
      TarjetaMonederoCodigo: formulario.TarjetaMonederoCodigo || null,
    };
  }

  generarNominativasxFiltroCaracteristica(caracteristica) {
    //
    return {
      idListaBlanca: null,
      nombre: null,
      apellido1: null,
      apellido2: null,
      cifoDni: caracteristica.documento || null,
      domicilio: null,
      tipoCalle: null,
      numCalle: null,
      piso: null,
      localidad: null,
      cp: null,
      provincia: null,
      pais: null,
      telefono: null,
      telefonoMovil: null,
      fax: null,
      email: null,
      fechaNacimiento: null,
      sexo: null,
      tipoCentro: caracteristica.Tipo || null,
      edad: null,
      rangoEdad: caracteristica.Rango || null,
      minusvalido: 0,
      jubilado: 0,
      publicidadInterna: 0,
      publicidadExterna: 0,
      nombreResponsable: null,
      apellido1Responsable: null,
      apellido2Responsable: null,
      nota: null,
      audioguia: 0,
      publicidadOrganizador: 0,
      curso: caracteristica.Nivel || null,
      lse: 0,
      pregunta1: null,
      respuesta1: null,
      pregunta2: null,
      respuesta2: null,
      pregunta3: null,
      respuesta3: null,
      pregunta4: null,
      respuesta4: null,
      nombreCentro: null,
      nacionalidad: null,
      politicaPrivacidad: null,
      GuiaExterno: caracteristica.Profesor || null,
      idxEntrada: caracteristica.idxEntrada || null,
      idContacto: caracteristica.idContacto || null,
      CodigoAsignado: null,
    };
  }

  estaenRecintoconHorarios(entrada, horarios): boolean {
    let respuesta = false;
    //.log(horarios);
    //;
    if (
      horarios.findIndex(
        (item) =>
          item.recinto.TipoEntrada === entrada.TipoEntradaId ||
          (item.recinto.entradas != null &&
            item.recinto.entradas.some(
              (itemEntrada) => itemEntrada == entrada.TipoEntradaId
            ))
      ) >= 0
    ) {
      //
      respuesta = true;
    }
    return respuesta;
  }

  // Yaribel 03112020   Añadimos peticion al api para validar tarjeta monedero
  validarTarjetaMonedero(codigoTarjeta) {
    const conexionId = JSON.parse(localStorage.getItem('conexionId'));
    const body = {
      ConexionIacpos: conexionId,
      TarjetaMonederoCodigo: codigoTarjeta,
    };
    return this.http
      .post(
        `${this.config.getConfig('API_URL')}api/TarjetaMonederoEstado`,
        body
      )
      .pipe(
        map((res: any) => {
          // ;
          return res;
        })
      );
  }
  // Yaribel 11112020  Añadimos metodo para Asignar Entradas Vendidas
  cargarEntradasVendidasPersonas(entrada, formulario, contador) {
    if (entrada.TipoProducto === '2') {
      return formulario.Caracteristicas !== undefined &&
        formulario.Caracteristicas !== null &&
        formulario.Caracteristicas.UsoMonedero === '1'
        ? ''
        : (formulario.Caracteristicas !== undefined &&
          formulario.Caracteristicas !== null
            ? formulario.Caracteristicas.UsoMonedero !== '1'
            : true) &&
          formulario.EntradasVendidasPersonas !== undefined &&
          formulario.EntradasVendidasPersonas !== null &&
          contador < formulario.EntradasVendidasPersonas.length
        ? this.generarNominativas(formulario.EntradasVendidasPersonas[contador])
        : entrada.FiltrosCaracteristicas &&
          entrada.FiltrosCaracteristicas !== undefined &&
          entrada.FiltrosCaracteristicas !== null &&
          contador < entrada.FiltrosCaracteristicas[contador] &&
          (formulario.CuentaNominada == '1' ||
            formulario.CuentaVisitante == '1')
        ? this.generarNominativasxFiltroCaracteristica(
            entrada.FiltrosCaracteristicas[contador]
          )
        : '';

    } else if (entrada.TipoProducto === '1') {
      return formulario.Caracteristicas !== undefined &&
        formulario.Caracteristicas !== null &&
        formulario.Caracteristicas.UsoMonedero === '1'
        ? ''
        : (formulario.Caracteristicas !== undefined &&
          formulario.Caracteristicas !== null
            ? formulario.Caracteristicas.UsoMonedero !== '1'
            : true) &&
          formulario.EntradasVendidasPersonas !== undefined &&
          formulario.EntradasVendidasPersonas !== null &&
          contador < formulario.EntradasVendidasPersonas.length
        ? this.generarNominativas(formulario.EntradasVendidasPersonas[contador])
        : formulario.FiltrosCaracteristicas &&
          entrada.FiltrosCaracteristicas !== undefined &&
          entrada.FiltrosCaracteristicas !== null &&
          contador < entrada.FiltrosCaracteristicas[contador]
        ? this.generarNominativasxFiltroCaracteristica(
            formulario.FiltrosCaracteristicas[contador]
          )
        : '';
    }
    return '';
  }
  cargarEntradasVendidasPersonasList(entrada, formulario, contador) {
    let list = [];
    if (entrada.TipoProducto === '2') {
      if (
        formulario.Caracteristicas !== undefined &&
        formulario.Caracteristicas !== null &&
        formulario.Caracteristicas.UsoMonedero === '1'
      ) {
      } else if (
        (formulario.Caracteristicas !== undefined &&
        formulario.Caracteristicas !== null
          ? formulario.Caracteristicas.UsoMonedero !== '1'
          : true) &&
        formulario.EntradasVendidasPersonas !== undefined &&
        formulario.EntradasVendidasPersonas !== null &&
        contador < formulario.EntradasVendidasPersonas.length
      ) {
        list.push(
          this.generarNominativas(formulario.EntradasVendidasPersonas[contador])
        );
      } else if (
        entrada.FiltrosCaracteristicas &&
        entrada.FiltrosCaracteristicas !== undefined &&
        entrada.FiltrosCaracteristicas !== null &&
        contador < entrada.FiltrosCaracteristicas[contador] &&
        (formulario.CuentaNominada == '1' || formulario.CuentaVisitante == '1')
      ) {
        list.push(
          this.generarNominativasxFiltroCaracteristica(
            entrada.FiltrosCaracteristicas[contador]
          )
        );
      }
    } else if (entrada.TipoProducto === '1') {
      if (
        formulario.Caracteristicas !== undefined &&
        formulario.Caracteristicas !== null &&
        formulario.Caracteristicas.UsoMonedero === '1'
      ) {
      } else if (
        (formulario.Caracteristicas !== undefined &&
        formulario.Caracteristicas !== null
          ? formulario.Caracteristicas.UsoMonedero !== '1'
          : true) &&
        formulario.EntradasVendidasPersonas !== undefined &&
        formulario.EntradasVendidasPersonas !== null &&
        contador < formulario.EntradasVendidasPersonas.length
      ) {
        list.push(
          this.generarNominativas(formulario.EntradasVendidasPersonas[contador])
        );
      } else if (
        formulario.FiltrosCaracteristicas &&
        entrada.FiltrosCaracteristicas !== undefined &&
        entrada.FiltrosCaracteristicas !== null &&
        contador < entrada.FiltrosCaracteristicas[contador]
      ) {
        list.push(
          this.generarNominativasxFiltroCaracteristica(
            formulario.FiltrosCaracteristicas[contador]
          )
        );
      }
    }
    return list;
  }

  cargarProcedenciaEnEntrada(
    entrada,
    procedencias,
    contadorProducto,
    contadorEntradaProducto,
    contadorCantidad
  ) {
    let procedencia =
      procedencias && procedencias != null && procedencias != undefined
        ? procedencias.filter(
            (p) =>
              p.data.numeroProducto == contadorProducto &&
              p.data.numEntradaProducto == contadorEntradaProducto &&
              p.data.numEntradaCantidad == contadorCantidad
          )
        : [];
    if (procedencia.length > 0) {
      //&& (entrada && entrada.EntradasVendidasPersonas && entrada.EntradasVendidasPersonas != "" && entrada.EntradasVendidasPersonas.length == 1)
      if (entrada.EntradasVendidasPersonas == '') {
        entrada.EntradasVendidasPersonas = {};
      }
      if (procedencia[0].procedencias.pais) {
        entrada.EntradasVendidasPersonas.Pais =
          procedencia[0].procedencias.pais.NombrePais;
        entrada.EntradasVendidasPersonas.CodigoPais =
          procedencia[0].procedencias.pais.CodPais;
      }
      if (procedencia[0].procedencias.comunidad) {
        entrada.EntradasVendidasPersonas.CodComunidad =
          procedencia[0].procedencias.comunidad.CodComunidad;
      }
      if (
        procedencia[0].procedencias.ciudad &&
        procedencia[0].procedencias.ciudad != ''
      ) {
        if (typeof procedencia[0].procedencias.ciudad != 'string') {
          if (
            procedencia[0].procedencias.ciudad.CodCiudad &&
            procedencia[0].procedencias.ciudad.CodCiudad.trim() != ''
          ) {
            entrada.EntradasVendidasPersonas.CodCiudad =
              procedencia[0].procedencias.ciudad.CodCiudad;
            entrada.EntradasVendidasPersonas.Localidad =
              procedencia[0].procedencias.ciudad.Ciudad;
          } else {
            entrada.EntradasVendidasPersonas.CodProvincia =
              procedencia[0].procedencias.ciudad.CodProvincia;
            entrada.EntradasVendidasPersonas.Provincia =
              procedencia[0].procedencias.ciudad.NombreProvincia;
          }
        } else {
          entrada.EntradasVendidasPersonas.CP =
            procedencia[0].procedencias.ciudad;
        }
      }
    }
    return entrada;
  }

  //entrada es la entrada del carrito
  //nuevaEntrada es el registro de entradasVendidas
  //indexEntrada es la posicion de las entradas individuales en el arrayFarId
  // generarRecintosVentaPorEntrada(
  //   entrada,
  //   nuevaEntrada,
  //   countEntrada,
  //   indiceEntrada
  // ) {
  //   let recintos = [];
  //   let conuntCaracteristica = 0;
  //   let misRecintos = [];
  //   let misHorarios = [];
  //   if (this.venta.carritoValue.reservaAgrupada == '1') {
  //     let TipoProducto = nuevaEntrada.PromocionId > 0 ? '2' : '1';
  //     const indexEntrada =
  //       this.venta.carritoValue.localizador.ReservaItems.findIndex(
  //         (x) =>
  //           x.TipoProducto == TipoProducto &&
  //           x.ItemId ==
  //             (TipoProducto == '2'
  //               ? nuevaEntrada.PromocionId
  //               : nuevaEntrada.TipoEntradaId)
  //       );
  //     // const arrayFarId = this.venta.carritoValue.localizador.ReservaItems[indexEntrada].FarId.split(',');
  //     let arrayFarId = [];
  //     entrada.entrada.PosicionesVenta.forEach((posicion) => {
  //       posicion.FarId.forEach((farId) => {
  //         arrayFarId.push(farId);
  //       });
  //     });
  //     let miFarId;
  //     if (TipoProducto == '2') {
  //       entrada.entrada.NumOrdenPromosConEntradas.forEach((x) => {
  //         if (x.NumOrdenPromo == nuevaEntrada.PromocionNumOrden) {
  //           miFarId = x.PromocionEntradas.find(
  //             (entrada) => entrada.TipoEntradaId == nuevaEntrada.TipoEntradaId
  //           ).FarIds[0];
  //         }
  //       });
  //       // const indexPromocionEntrada = this.venta.carritoValue.localizador.ReservaItems[indexEntrada].PromocionEntradas.findIndex(x=> x.NumOrdenPromo == nuevaEntrada.PromocionNumOrden && x.TipoEntradaId == nuevaEntrada.TipoEntradaId);
  //       // miFarId = this.venta.carritoValue.localizador.ReservaItems[indexEntrada].PromocionEntradas[indexPromocionEntrada].PosicionesVenta[0]?this.venta.carritoValue.localizador.ReservaItems[indexEntrada].PromocionEntradas[indexPromocionEntrada].PosicionesVenta[0].FarId[0]: '0';
  //     } else {
  //       miFarId = arrayFarId[indiceEntrada];
  //     }
  //     const misRecintosReserva =
  //       this.venta.carritoValue.localizador.ReservaRecintos.filter(
  //         (item) => item.idFilaAlbaranReserva == miFarId
  //       );

  //     if (entrada.entrada.recintos) {
  //       entrada.entrada.recintos.forEach((element) => {
  //         if (misRecintosReserva.find((x) => x.RecintoId == element.Recinto))
  //           misRecintos.push(element);
  //       });
  //     }
  //     misHorarios = this.mapHorarios(misRecintosReserva);
  //   } else {
  //     if (
  //       entrada.entrada.recintos &&
  //       entrada.entrada.recintos !== undefined &&
  //       entrada.entrada.recintos !== null &&
  //       entrada.entrada.recintos.length > 0
  //     ) {
  //       //misRecintos = entrada.entrada.recintos;
  //       entrada.entrada.recintos.forEach((element) => {
  //         if (element.entradas.find((x) => x == nuevaEntrada.TipoEntradaId))
  //           misRecintos.push(element);
  //       });
  //       misHorarios = this.venta.carritoValue.horarios;
  //     }
  //   }
  //   if (
  //     entrada.entrada.noEditable === undefined ||
  //     entrada.entrada.noEditable === null ||
  //     entrada.entrada.noEditable === false
  //   ) {
  //     // extraData solo lo tienen las butacas compradas en taquilla
  //     if (
  //       'extraData' in entrada.entrada &&
  //       entrada.entrada.extraData !== undefined &&
  //       entrada.entrada.extraData !== null
  //     ) {
  //       //
  //       recintos.push({
  //         TipoSesionId: entrada.entrada.extraData.espectaculo.idTipoSesion,
  //         TipoEntradaId: entrada.entrada.TipoEntradaId,
  //         recintoId: entrada.entrada.extraData.dataIdRecinto,
  //         fechaEntrada: moment(nuevaEntrada.FechaHoraEntrada).format(
  //           'YYYY/MM/DD HH:mm:ss'
  //         ),
  //         EspectaculoId: entrada.entrada.extraData.espectaculo.iEspectaculoId,
  //       });
  //     }
  //     // ListaButacas solo lo tienen las butacas recuperadas del localizador
  //     if (
  //       'ListaButacas' in entrada.entrada &&
  //       entrada.entrada.ListaButacas !== null &&
  //       entrada.entrada.ListaButacas.length > 0
  //     ) {
  //       entrada.entrada.ListaButacas.forEach((butaca) => {
  //         recintos.push({
  //           TipoSesionId: butaca.IdTipoSesion,
  //           TipoEntradaId: entrada.entrada.TipoEntradaId,
  //           recintoId: butaca.IdRecinto,
  //           fechaEntrada: butaca.FechaHora,
  //           EspectaculoId: butaca.IdEspectaculo,
  //         });
  //       });
  //     }

  //     if (
  //       misRecintos !== undefined &&
  //       misRecintos !== null &&
  //       misRecintos.length > 0
  //     ) {
  //       misRecintos.forEach((recinto) => {
  //         if (recinto.EsActividad === '0' && recinto.ControlaAforo === '0') {
  //           recintos.push({
  //             TipoEntradaId: recinto.TipoEntrada,
  //             recintoId: recinto.Recinto,
  //             fechaEntrada: moment(nuevaEntrada.FechaHoraEntrada).format(
  //               'YYYY/MM/DD HH:mm:ss'
  //             ),
  //           });
  //         } else if (
  //           recinto.EsActividad === '0' &&
  //           recinto.ControlaAforo === '1'
  //         ) {
  //           if (misHorarios.length > 0) {
  //             misHorarios.forEach((item) => {
  //               let existeEntrada;
  //               if (item.recinto.entradas) {
  //                 existeEntrada = item.recinto.entradas.includes(
  //                   recinto.TipoEntrada
  //                 );
  //               }
  //               if (item.recinto.Recinto === recinto.Recinto && existeEntrada) {
  //                 if (
  //                   entrada.entrada.CaracteristicasEntrada &&
  //                   entrada.entrada.CaracteristicasEntrada !== null &&
  //                   entrada.entrada.CaracteristicasEntrada.length > 0
  //                 ) {
  //                   if (
  //                     item.indiceEntrada == countEntrada &&
  //                     item.indiceCaracteristica == conuntCaracteristica &&
  //                     moment(
  //                       nuevaEntrada.FechaHoraEntrada,
  //                       'YYYY/MM/DD HH:mm:ss'
  //                     ).format('HH:mm') == item.sesion.HoraInicio
  //                   ) {
  //                     recintos.push({
  //                       TipoEntradaId: recinto.TipoEntrada,
  //                       recintoId: recinto.Recinto,
  //                       fechaEntrada:
  //                         moment(item.sesion.Fecha).format('YYYY/MM/DD') +
  //                         ' ' +
  //                         item.sesion.HoraInicio +
  //                         ':00',
  //                       Guia:
  //                         item.GuiaSeleccionado && item.GuiaSeleccionado.iGuiaId
  //                           ? item.GuiaSeleccionado.iGuiaId
  //                           : '',
  //                       RecintosSesionesId: item.sesion.RecintosSesionId,
  //                       CaracteristicaId: item.indiceCaracteristica,
  //                     });
  //                     conuntCaracteristica++;
  //                   }
  //                   else if (
  //                     item.indiceEntrada === countEntrada &&
  //                     item.indiceCaracteristica === conuntCaracteristica &&
  //                     moment(nuevaEntrada.FechaHoraEntrada, 'YYYY/MM/DD HH:mm:ss').format('YYYY/MM/DD') === moment(item.sesion.Fecha).format('YYYY/MM/DD')
  //                   ) {
  //                     recintos.push({
  //                       TipoEntradaId: recinto.TipoEntrada,
  //                       recintoId: recinto.Recinto,
  //                       fechaEntrada: moment(item.sesion.Fecha).format('YYYY/MM/DD') + ' ' + item.sesion.HoraInicio + ':00',
  //                       Guia: item.GuiaSeleccionado && item.GuiaSeleccionado.iGuiaId ? item.GuiaSeleccionado.iGuiaId : '',
  //                       RecintosSesionesId: item.sesion.RecintosSesionId,
  //                       CaracteristicaId: item.indiceCaracteristica,
  //                     });
  //                     conuntCaracteristica++;
  //                   }
  //                 } else {
  //                   if (
  //                     moment(
  //                       nuevaEntrada.FechaHoraEntrada,
  //                       'YYYY/MM/DD HH:mm:ss'
  //                     ).format('HH:mm') == item.sesion.HoraInicio
  //                   ) {
  //                     recintos.push({
  //                       TipoEntradaId: recinto.TipoEntrada,
  //                       recintoId: recinto.Recinto,
  //                       fechaEntrada:
  //                         moment(item.sesion.Fecha).format('YYYY/MM/DD') +
  //                         ' ' +
  //                         item.sesion.HoraInicio +
  //                         ':00',
  //                       Guia:
  //                         item.GuiaSeleccionado && item.GuiaSeleccionado.iGuiaId
  //                           ? item.GuiaSeleccionado.iGuiaId
  //                           : '',
  //                       RecintosSesionesId: item.sesion.RecintosSesionId,
  //                       CaracteristicaId: item.indiceCaracteristica,
  //                     });
  //                   } else if (
  //                     moment(nuevaEntrada.FechaHoraEntrada, 'YYYY/MM/DD HH:mm:ss').format('YYYY/MM/DD') === moment(item.sesion.Fecha).format('YYYY/MM/DD')
  //                   ) {
  //                     recintos.push({
  //                       TipoEntradaId: recinto.TipoEntrada,
  //                       recintoId: recinto.Recinto,
  //                       fechaEntrada: moment(item.sesion.Fecha).format('YYYY/MM/DD') + ' ' + item.sesion.HoraInicio + ':00',
  //                       Guia: item.GuiaSeleccionado && item.GuiaSeleccionado.iGuiaId ? item.GuiaSeleccionado.iGuiaId : '',
  //                       RecintosSesionesId: item.sesion.RecintosSesionId,
  //                       CaracteristicaId: item.indiceCaracteristica,
  //                     });
  //                   }
  //                 }
  //               }
  //             });
  //           }
  //         } else if (
  //           recinto.EsActividad === '1' &&
  //           recinto.ControlaAforo === '1'
  //         ) {
  //           if (misHorarios.length > 0) {
  //             misHorarios.forEach((item) => {
  //               const existeEntrada = item.recinto.entradas.includes(
  //                 recinto.TipoEntrada
  //               );
  //               if (item.recinto.Recinto === recinto.Recinto && existeEntrada) {
  //                 if (
  //                   moment(
  //                     nuevaEntrada.FechaHoraEntrada,
  //                     'YYYY/MM/DD HH:mm:ss'
  //                   ).format('YYYY/MM/DD') ==
  //                     moment(item.sesion.Fecha).format('YYYY/MM/DD')
  //                   //   &&
  //                   // moment(
  //                   //   nuevaEntrada.FechaHoraEntrada,
  //                   //   'YYYY/MM/DD HH:mm:ss'
  //                   // ).format('HH:mm') == item.sesion.HoraInicio
  //                 )
  //                   recintos.push({
  //                     TipoEntradaId: recinto.TipoEntrada,
  //                     recintoId: recinto.Recinto,
  //                     fechaEntrada:
  //                       moment(item.sesion.Fecha).format('YYYY/MM/DD') +
  //                       ' ' +
  //                       item.sesion.HoraInicio +
  //                       ':00',
  //                   });
  //               }
  //             });
  //           }
  //         }
  //       });
  //     }
  //   }
  //   // quitamos duplicados
  //   recintos = recintos.filter(
  //     (recinto, index, self) =>
  //       index ===
  //       self.findIndex(
  //         (r) =>
  //           r.TipoEntradaId === recinto.TipoEntradaId &&
  //           r.recintoId === recinto.recintoId &&
  //           r.fechaEntrada === recinto.fechaEntrada &&
  //           r.Guia === recinto.Guia
  //       )
  //   );
  //   nuevaEntrada.recintosVenta = recintos;
  //   return nuevaEntrada;
  // }

  generarRecintosVentaPorEntrada(
    entrada,
    nuevaEntrada,
    countEntrada,
    indiceEntrada
  ) {
    let recintos = [];
    let conuntCaracteristica = 0;
    let misRecintos = [];
    let misHorarios = [];

    if (this.venta.carritoValue.reservaAgrupada == '1') {
      let TipoProducto = nuevaEntrada.PromocionId > 0 ? '2' : '1';
      const indexEntrada =
        this.venta.carritoValue.localizador.ReservaItems.findIndex(
          (x) =>
            x.TipoProducto == TipoProducto &&
            x.ItemId ==
              (TipoProducto == '2'
                ? nuevaEntrada.PromocionId
                : nuevaEntrada.TipoEntradaId)
        );

      let arrayFarId = [];
      entrada.entrada.PosicionesVenta.forEach((posicion) => {
        posicion.FarId.forEach((farId) => {
          arrayFarId.push(farId);
        });
      });

      let miFarId;
      if (TipoProducto == '2') {
        entrada.entrada.NumOrdenPromosConEntradas.forEach((x) => {
          if (x.NumOrdenPromo == nuevaEntrada.PromocionNumOrden) {
            miFarId = x.PromocionEntradas.find(
              (entrada) => entrada.TipoEntradaId == nuevaEntrada.TipoEntradaId
            ).FarIds[0];
          }
        });
      } else {
        miFarId = arrayFarId[indiceEntrada];
      }

      const misRecintosReserva =
        this.venta.carritoValue.localizador.ReservaRecintos.filter(
          (item) => item.idFilaAlbaranReserva == miFarId
        );

      if (entrada.entrada.recintos) {
        entrada.entrada.recintos.forEach((element) => {
          if (misRecintosReserva.find((x) => x.RecintoId == element.Recinto))
            misRecintos.push(element);
        });
      }
      misHorarios = this.mapHorarios(misRecintosReserva);
    } else if (
      entrada.entrada.recintos &&
      entrada.entrada.recintos.length > 0
    ) {
      entrada.entrada.recintos.forEach((element) => {
        if (element.entradas.find((x) => x == nuevaEntrada.TipoEntradaId))
          misRecintos.push(element);
      });
      misHorarios = this.venta.carritoValue.horarios;
    }

    if (
      entrada.entrada.noEditable === undefined ||
      entrada.entrada.noEditable === null ||
      entrada.entrada.noEditable === false
    ) {
      // Agregar butacas compradas en taquilla
      if (
        'extraData' in entrada.entrada &&
        entrada.entrada.extraData !== undefined &&
        entrada.entrada.extraData !== null &&
        entrada.entrada.extraData.espectaculo !== null &&
        entrada.entrada.extraData.espectaculo !== undefined
      ) {
        this.agregarRecinto(recintos, {
          TipoSesionId: entrada.entrada.extraData.espectaculo.idTipoSesion,
          TipoEntradaId: entrada.entrada.TipoEntradaId,
          recintoId: entrada.entrada.extraData.dataIdRecinto,
          fechaEntrada: moment(nuevaEntrada.FechaHoraEntrada).format(
            'YYYY/MM/DD HH:mm:ss'
          ),
          EspectaculoId: entrada.entrada.extraData.espectaculo.iEspectaculoId,
        });
      }

      // Agregar ListaButacas
      if (
        'ListaButacas' in entrada.entrada &&
        entrada.entrada.ListaButacas &&
        entrada.entrada.ListaButacas.length > 0
      ) {
        entrada.entrada.ListaButacas.forEach((butaca) => {
          this.agregarRecinto(recintos, {
            TipoSesionId: butaca.IdTipoSesion,
            TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
              ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
              : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
            recintoId: butaca.IdRecinto,
            fechaEntrada: butaca.FechaHora,
            EspectaculoId: butaca.IdEspectaculo,
          });
        });
      }

      // Procesa cada recinto según EsActividad y ControlaAforo
      misRecintos.forEach((recinto) => {
        if (recinto.EsActividad === '0' && recinto.ControlaAforo === '0') {
          this.agregarRecinto(recintos, {
            TipoEntradaId: recinto.TipoEntrada,
            recintoId: recinto.Recinto,
            fechaEntrada: moment(nuevaEntrada.FechaHoraEntrada).format(
              'YYYY/MM/DD HH:mm:ss'
            ),
          });
        } else if (
          recinto.EsActividad === '0' &&
          recinto.ControlaAforo === '1'
        ) {
          if (misHorarios.length > 0) {
            misHorarios.forEach((item) => {
              // Cambiamos la comparación para verificar más características, similar a `generarRecintosVenta`
              let existeEntrada = false;

              if (
                item.recinto &&
                item.recinto.entradas &&
                Array.isArray(item.recinto.entradas)
              ) {
                existeEntrada = item.recinto.entradas.includes(
                  recinto.TipoEntrada
                );
              }
              const coincideRecinto =
                item.recinto.Recinto == recinto.Recinto ||
                item.recinto.IdRecinto == recinto.Recinto;

              if (coincideRecinto && existeEntrada) {
                this.agregarRecinto(recintos, {
                  TipoEntradaId: recinto.TipoEntrada,
                  recintoId: recinto.Recinto,
                  fechaEntrada:
                    moment(item.sesion.Fecha).format('YYYY/MM/DD') +
                    ' ' +
                    item.sesion.HoraInicio +
                    ':00',
                  Guia:
                    item.GuiaSeleccionado && item.GuiaSeleccionado.iGuiaId
                      ? item.GuiaSeleccionado.iGuiaId
                      : '',
                  RecintosSesionesId: item.sesion.RecintosSesionId,
                  CaracteristicaId: item.indiceCaracteristica,
                });
              }
            });
          }
        } else if (
          recinto.EsActividad === '1' &&
          recinto.ControlaAforo === '1'
        ) {
          if (misHorarios.length > 0) {
            misHorarios.forEach((item) => {
              let existeEntrada = false;

              if (
                item.recinto &&
                item.recinto.entradas &&
                Array.isArray(item.recinto.entradas)
              ) {
                existeEntrada = item.recinto.entradas.includes(
                  recinto.TipoEntrada
                );
              }
              const coincideRecinto =
                item.recinto.Recinto == recinto.Recinto ||
                item.recinto.IdRecinto == recinto.Recinto;

              if (coincideRecinto && existeEntrada) {
                this.agregarRecinto(recintos, {
                  TipoEntradaId: recinto.TipoEntrada,
                  recintoId: recinto.Recinto,
                  fechaEntrada:
                    moment(item.sesion.Fecha).format('YYYY/MM/DD') +
                    ' ' +
                    item.sesion.HoraInicio +
                    ':00',
                });
              }
            });
          }
        }
      });
    }

    // Eliminar duplicados
    recintos = recintos.filter(
      (recinto, index, self) =>
        index ===
        self.findIndex(
          (r) =>
            r.TipoEntradaId === recinto.TipoEntradaId &&
            r.recintoId === recinto.recintoId &&
            r.fechaEntrada === recinto.fechaEntrada &&
            r.Guia === recinto.Guia
        )
    );

    nuevaEntrada.recintosVenta = recintos;
    return nuevaEntrada;
  }

  agregarRecinto(recintos, datos) {
    recintos.push({
      TipoSesionId: datos.TipoSesionId || null,
      TipoEntradaId: datos.TipoEntradaId,
      recintoId: datos.recintoId,
      fechaEntrada: datos.fechaEntrada,
      EspectaculoId: datos.EspectaculoId || null,
      Guia: datos.Guia || '',
      RecintosSesionesId: datos.RecintosSesionesId || null,
      CaracteristicaId: datos.CaracteristicaId || null,
    });
  }

  generarRecintosVentaPorPromocion(
    entrada,
    nuevaEntrada,
    countEntrada,
    indiceEntrada
  ) {
    let recintos = [];
    let conuntCaracteristica = 0;
    let misRecintos = [];
    let misHorarios = [];
    let TipoProducto = nuevaEntrada.PromocionId > 0 ? '2' : '1';
    if (this.venta.carritoValue.reservaAgrupada == '1') {
      const indexEntrada =
        this.venta.carritoValue.localizador.ReservaItems.findIndex(
          (x) =>
            x.TipoProducto == TipoProducto &&
            x.ItemId ==
              (TipoProducto == '2'
                ? nuevaEntrada.PromocionId
                : nuevaEntrada.TipoEntradaId)
        );
      let arrayFarId = [];
      entrada.entrada.PosicionesVenta.forEach((posicion) => {
        posicion.FarId.forEach((farId) => {
          arrayFarId.push(farId);
        });
      });
      let miFarId = [];
      if (TipoProducto == '2') {
        entrada.entrada.NumOrdenPromosConEntradas.forEach((x) => {
          if (x.NumOrdenPromo == nuevaEntrada.PromocionNumOrden) {
            x.PromocionEntradas.forEach((element) => {
              miFarId.push(element.FarIds[0]);
            });
            //miFarId =  x.PromocionEntradas.find(entrada => entrada.TipoEntradaId == nuevaEntrada.TipoEntradaId).FarIds[0];
          }
        });
      } else {
        miFarId.push(arrayFarId[indiceEntrada]);
        //miFarId = arrayFarId[indiceEntrada];
      }

      let misRecintosReserva = [];
      miFarId.forEach((FarId) => {
        this.venta.carritoValue.localizador.ReservaRecintos.forEach(
          (recinto) => {
            if (recinto.idFilaAlbaranReserva == FarId) {
              misRecintosReserva.push(recinto);
            }
          }
        );
      });

      //const misRecintosReserva = this.venta.carritoValue.localizador.ReservaRecintos.filter((item) => item.idFilaAlbaranReserva == miFarId);

      entrada.entrada.recintos.forEach((element) => {
        if (misRecintosReserva.find((x) => x.RecintoId == element.Recinto))
          misRecintos.push(element);
      });
      misHorarios = this.mapHorarios(misRecintosReserva);
    } else {
      if (
        entrada.entrada.recintos &&
        entrada.entrada.recintos !== undefined &&
        entrada.entrada.recintos !== null &&
        entrada.entrada.recintos.length > 0
      ) {
        misRecintos = entrada.entrada.recintos;
        misHorarios = this.venta.carritoValue.horarios;
      }
    }
    if (
      entrada.entrada.noEditable === undefined ||
      entrada.entrada.noEditable === null ||
      entrada.entrada.noEditable === false
    ) {
      // extraData solo lo tienen las butacas compradas en taquilla
      if (
        'extraData' in entrada.entrada &&
        entrada.entrada.extraData !== undefined &&
        entrada.entrada.extraData !== null &&
        entrada.entrada.extraData.espectaculo !== null &&
        entrada.entrada.extraData.espectaculo !== undefined
      ) {
        //
        recintos.push({
          TipoSesionId: entrada.entrada.extraData.espectaculo.idTipoSesion,
          TipoEntradaId: entrada.entrada.TipoEntradaId,
          recintoId: entrada.entrada.extraData.dataIdRecinto,
          fechaEntrada: moment(nuevaEntrada.FechaHoraEntrada).format(
            'YYYY/MM/DD HH:mm:ss'
          ),
          EspectaculoId: entrada.entrada.extraData.espectaculo.iEspectaculoId,
        });
      }
      // ListaButacas solo lo tienen las butacas recuperadas del localizador
      if (
        'ListaButacas' in entrada.entrada &&
        entrada.entrada.ListaButacas !== null &&
        entrada.entrada.ListaButacas.length > 0
      ) {
        entrada.entrada.ListaButacas.forEach((butaca) => {
          recintos.push({
            TipoSesionId: butaca.IdTipoSesion,
            TipoEntradaId: entrada.entrada.NumOrdenPromosConEntradas && entrada.entrada.NumOrdenPromosConEntradas.length > 0 
              ? entrada.entrada.NumOrdenPromosConEntradas[0].PromocionEntradas[0].TipoEntradaId 
              : (entrada.entrada.TipoEntradaId && entrada.entrada.TipoEntradaId !== '0' ? entrada.entrada.TipoEntradaId : entrada.entrada.ItemId || '0'),
            recintoId: butaca.IdRecinto,
            fechaEntrada: butaca.FechaHora,
            EspectaculoId: butaca.IdEspectaculo,
          });
        });
      }

      if (
        misRecintos !== undefined &&
        misRecintos !== null &&
        misRecintos.length > 0
      ) {
        misRecintos.forEach((recinto) => {
          if (recinto.EsActividad === '0' && recinto.ControlaAforo === '0') {
            recintos.push({
              TipoEntradaId: recinto.TipoEntrada,
              recintoId: recinto.Recinto,
              fechaEntrada: moment(nuevaEntrada.FechaHoraEntrada).format(
                'YYYY/MM/DD HH:mm:ss'
              ),
            });
          } else if (
            recinto.EsActividad === '0' &&
            recinto.ControlaAforo === '1'
          ) {
            if (misHorarios.length > 0) {
              misHorarios.forEach((item) => {
                let existeEntrada = false;

                if (
                  item.recinto &&
                  item.recinto.entradas &&
                  Array.isArray(item.recinto.entradas)
                ) {
                  existeEntrada = item.recinto.entradas.includes(
                    recinto.TipoEntrada
                  );
                }
                if (item.recinto.Recinto === recinto.Recinto && existeEntrada) {
                  if (
                    entrada.entrada.CaracteristicasEntrada &&
                    entrada.entrada.CaracteristicasEntrada !== null &&
                    entrada.entrada.CaracteristicasEntrada.length > 0
                  ) {
                    if (
                      item.indiceEntrada == countEntrada &&
                      item.indiceCaracteristica == conuntCaracteristica
                    ) {
                      recintos.push({
                        TipoEntradaId: recinto.TipoEntrada,
                        recintoId: recinto.Recinto,
                        fechaEntrada:
                          moment(item.sesion.Fecha).format('YYYY/MM/DD') +
                          ' ' +
                          item.sesion.HoraInicio +
                          ':00',
                        Guia:
                          item.GuiaSeleccionado && item.GuiaSeleccionado.iGuiaId
                            ? item.GuiaSeleccionado.iGuiaId
                            : '',
                        RecintosSesionesId: item.sesion.RecintosSesionId,
                        CaracteristicaId: item.indiceCaracteristica,
                      });
                      conuntCaracteristica++;
                    }
                  } else {
                    recintos.push({
                      TipoEntradaId: recinto.TipoEntrada,
                      recintoId: recinto.Recinto,
                      fechaEntrada:
                        moment(item.sesion.Fecha).format('YYYY/MM/DD') +
                        ' ' +
                        item.sesion.HoraInicio +
                        ':00',
                      Guia:
                        item.GuiaSeleccionado && item.GuiaSeleccionado.iGuiaId
                          ? item.GuiaSeleccionado.iGuiaId
                          : '',
                      RecintosSesionesId: item.sesion.RecintosSesionId,
                      CaracteristicaId: item.indiceCaracteristica,
                    });
                  }
                }
              });
            }
          } else if (
            recinto.EsActividad === '1' &&
            recinto.ControlaAforo === '1'
          ) {
            if (misHorarios.length > 0) {
              misHorarios.forEach((item) => {
                let existeEntrada = false;

                if (
                  item.recinto &&
                  item.recinto.entradas &&
                  Array.isArray(item.recinto.entradas)
                ) {
                  existeEntrada = item.recinto.entradas.includes(
                    recinto.TipoEntrada
                  );
                }
                if (item.recinto.Recinto === recinto.Recinto && existeEntrada) {
                  recintos.push({
                    TipoEntradaId: recinto.TipoEntrada,
                    recintoId: recinto.Recinto,
                    fechaEntrada:
                      moment(item.sesion.Fecha).format('YYYY/MM/DD') +
                      ' ' +
                      item.sesion.HoraInicio +
                      ':00',
                  });
                }
              });
            }
          }
        });
      }
    }
    // quitamos duplicados
    recintos = recintos.filter(
      (recinto, index, self) =>
        index ===
        self.findIndex(
          (r) =>
            r.TipoEntradaId === recinto.TipoEntradaId &&
            r.recintoId === recinto.recintoId &&
            r.fechaEntrada === recinto.fechaEntrada &&
            r.Guia === recinto.Guia
        )
    );
    nuevaEntrada.recintosVenta = recintos;
    return nuevaEntrada;
  }

  mapHorarios(recintos) {
    return this.venta.mapHorarios(recintos);
  }

  //entradaConjunta Variable que indica si se imprime o no Conjunta
  //entrada: datos de la entrada que vamos a evaluar
  //promocionEntrada: datos de la entrada de la promocion que evaluamos
  //miPosicionVenta: posicionVenta de la entrada que evaluamos (VentasAgrupadas)
  //tipoEntradaIdAgrupadas: array con las agrupaciones de las entradas
  generarOrdenConjunta(
    entradaConjunta,
    entrada,
    promocionEntrada,
    miPosicionVenta,
    tipoEntradaIdAgrupadas,
    CantidadEntrada,
    CantidadPromocionEntrada
  ) {
    let OrdenCjta;
    let OrdenCjtas;
    if (entradaConjunta) {
      if (entrada.entrada.TipoProducto === '1') {
        OrdenCjta =
          1 +
          tipoEntradaIdAgrupadas.indexOf(
            tipoEntradaIdAgrupadas.find(
              (x) =>
                x.posicionVenta == miPosicionVenta &&
                x.tipoProducto == '1' &&
                x.promoId == '0' &&
                x.entradaId == entrada.entrada.TipoEntradaId &&
                x.codigoPromocional == entrada.codigoPromocional
            )
          ); // Yaribel 20210121 Verificamos si es entrada conjunta para agrupar todas las entradas del mismo // Yaribel 20210226 Añadimos  PosicionVenta
      } else if (entrada.entrada.TipoProducto === '2') {
        OrdenCjta =
          1 +
          (entrada.entrada.ImprimirBarcode == '1'
            ? tipoEntradaIdAgrupadas.indexOf(
                tipoEntradaIdAgrupadas.find(
                  (x) =>
                    x.posicionVenta == miPosicionVenta &&
                    x.tipoProducto == '2' &&
                    x.promoId == entrada.entrada.TipoPromocionId &&
                    x.entradaId == '0' &&
                    x.codigoPromocional == entrada.codigoPromocional
                )
              )
            : tipoEntradaIdAgrupadas.indexOf(
                tipoEntradaIdAgrupadas.find(
                  (x) =>
                    x.posicionVenta == miPosicionVenta &&
                    x.tipoProducto == '2' &&
                    x.promoId == entrada.entrada.TipoPromocionId &&
                    x.entradaId == promocionEntrada.TipoEntradaId &&
                    x.codigoPromocional == entrada.codigoPromocional
                )
              )); // Yaribel 20210121 Verificamos si es entrada conjunta para agrupar todas las entradas del mismo  // Yaribel 20210223 Verificamos si es QR promo para agrupar todas las entradas de la promo
      }
    } else {
      OrdenCjta = 0;
    }
  }

  abrirCajon(): void {
    //llamar a Kore para que abra el cajon
    if (
      this.window.external &&
      typeof this.window.external.abrirCajon === 'function'
    ) {
      this.window.external.abrirCajon();
    } else {
    }
  }

  //<< Yaribel 20210728 cliente mapfre VSTS 8370
  //Yaribel 20210728 añadimos el calcular descuento de forma generica VSTS 8370
  calculoDescuento(productosDescuento) {
    const arrayProdDescuento = Object.values(productosDescuento);
    const prodCarrito = this.venta.carritoValue.entradas;
    const idsDescuento: any[] = arrayProdDescuento.map(
      (p: any) => p.ProductoId
    );
    const productos = [];
    idsDescuento.forEach((id) => {
      productos.push(prodCarrito.filter((p) => p.entrada.ItemId === id));
    });
    const productosFlat = productos.reduce((acc, val) => acc.concat(val), []);
    this.calcObjetoInsercion(arrayProdDescuento, productosFlat);
    let descuentoTotal = 0;
    let descuentoGrupo = 0;
    const ProductosAplicados = this.descuentoObjetoValue;
    ProductosAplicados.forEach((item) => {
      const prodDescuento: any = arrayProdDescuento.find(
        (p: any) =>
          p.ProductoId == item.ProductoId &&
          p.TipoProducto === item.TipoProducto
      );
      const precioInicial = Number.parseFloat(
        productosFlat
          .find(
            (x) =>
              x.entrada.TipoProducto === item.TipoProducto &&
              x.entrada.ItemId === item.ProductoId
          )
          .entrada.PrecioEnDivisaReferencia.replace(/,/, '.')
      );
      const precioConDescuento = Number.parseFloat(
        prodDescuento.PrecioEnDivisaReferencia.replace(/,/, '.')
      );
      const descuentoPorEntrada = precioInicial - precioConDescuento;
      descuentoGrupo = descuentoPorEntrada * item.Cantidad;
      descuentoTotal = descuentoTotal + descuentoGrupo;
    });
    return descuentoTotal;
  }

  calcObjetoInsercion(arrayProdDescuento, productos) {
    const ProductosSeleccionados = [];
    productos.forEach((entrada) => {
      const prodDescuento: any = arrayProdDescuento.find(
        (p: any) =>
          p.ProductoId === entrada.entrada.ItemId &&
          p.TipoProducto === entrada.entrada.TipoProducto
      );
      const TipoProducto = prodDescuento.ProductosBono ? '2' : '1';
      const ProductoId = prodDescuento.ProductoId;
      const IdCategoria_GI = entrada.entrada.CategoriaId;
      let Cantidad;
      const CantidadAplicada = ProductosSeleccionados.find(
        (x) => x.TipoProducto === TipoProducto && x.ProductoId == ProductoId
      )
        ? ProductosSeleccionados.find(
            (x) => x.TipoProducto === TipoProducto && x.ProductoId == ProductoId
          ).Cantidad
        : 0;

      if (CantidadAplicada > 0) {
        const indice = ProductosSeleccionados.findIndex(
          (x) => x.TipoProducto === TipoProducto && x.ProductoId == ProductoId
        );

        if (
          parseInt(prodDescuento.MaximoTarifa, 10) - CantidadAplicada >=
          entrada.cantidad
        ) {
          ProductosSeleccionados[indice].Cantidad += entrada.cantidad;
        } else if (
          parseInt(prodDescuento.MaximoTarifa, 10) - CantidadAplicada >
          0
        ) {
          ProductosSeleccionados[indice].Cantidad +=
            parseInt(prodDescuento.MaximoTarifa, 10) - CantidadAplicada;
        }
      } else {
        if (parseInt(prodDescuento.MaximoTarifa, 10) >= entrada.cantidad) {
          Cantidad = entrada.cantidad;
        } else {
          Cantidad = parseInt(prodDescuento.MaximoTarifa, 10);
        }
        const prodFinal = {
          TipoProducto,
          ProductoId,
          Cantidad,
          IdCategoria_GI,
        };
        ProductosSeleccionados.push(prodFinal);
      }
    });
    this.setDescuentoObjeto(ProductosSeleccionados);
  }

  validarClienteAsociado(tipoDocumento, codigoCliente) {
    const conexionId = JSON.parse(localStorage.getItem('conexionId'));
    const body = {
      ConexionIacpos: conexionId,
      Idioma: this.idioma,
      Codigo: { TipoDocumento: tipoDocumento, NumeroDocumento: codigoCliente },
      Entradas: this.calcEntradas(),
      Bonos: this.calcBonos(),
      Plataforma: '2',
      // FechaDesde: moment().format('YYYY/MM/DD'),
      // FechaHasta: moment().add(1,'days').format('YYYY/MM/DD'),
      FechaDesde: this.minDia,
      FechaHasta: this.maxDia,
    };
    return this.http
      .post(
        `${this.config.getConfig('API_URL')}api/ValidarCompraClienteMapfrev2`,
        body
      )
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  calcEntradas() {
    const entradasSinBonos = this.venta.carritoValue.entradas.filter(
      (p) => p.entrada.TipoProducto == '1'
    );
    const entradas = [];
    entradasSinBonos.forEach((entrada) => {
      entradas.push({
        ProductoId: entrada.entrada.ItemId,
        IdCategoria_GI: entrada.entrada.CategoriaId,
        Cantidad: entrada.cantidad,
      });
    });
    const result = Array.from(
      entradas.reduce((m, t) => m.set(t.ProductoId, t), new Map()).values()
    );
    return result;
  }

  calcBonos() {
    const entradasConBonos = this.venta.carritoValue.entradas.filter(
      (p) => p.entrada.TipoProducto == '2'
    );
    const bonos = [];
    entradasConBonos.forEach((entrada) => {
      bonos.push({
        ProductoId: entrada.entrada.ItemId,
        IdCategoria_GI: entrada.entrada.CategoriaId,
        Cantidad: entrada.cantidad,
      });
    });
    const result = Array.from(
      bonos.reduce((m, t) => m.set(t.ProductoId, t), new Map()).values()
    );
    return result;
  }

  calcDiaMinimo() {
    this.minDia = '';
    this.venta.carritoValue.horarios.forEach((horario) => {
      if (horario.sesion.Fecha) {
        const compraDia = moment(horario.sesion.Fecha, 'YYYYMMDD').format(
          'YYYY/MM/DD'
        ); //moment(entrada.horarios.FechaHoraAcceso, 'YYYY/MM/DD HH:mm:ss');
        if (!this.minDia) {
          this.minDia = compraDia;
        } else {
          if (this.minDia > compraDia) {
            this.minDia = compraDia;
          }
        }
      } else {
        this.minDia = moment();
      }
    });
  }

  calcDiaMaximo() {
    this.maxDia = '';
    this.venta.carritoValue.horarios.forEach((horario) => {
      if (horario.sesion.Fecha) {
        const compraDia = moment(horario.sesion.Fecha, 'YYYYMMDD').format(
          'YYYY/MM/DD'
        );
        if (!this.maxDia) {
          this.maxDia = compraDia;
        } else {
          if (this.maxDia < compraDia) {
            this.maxDia = compraDia;
          }
        }
      } else {
        this.maxDia = moment();
      }
    });
  }

  setDescuentoCliente(
    Datos,
    importeDescuento,
    importeTotal,
    importeTotalConDescuento
  ) {
    this.venta.carritoValue.descuentoClienteAsociadoDatos = Datos;
    this.venta.carritoValue.importeDescuento = importeDescuento;
    this.venta.carritoValue.precioTotalSinDescuento = importeTotal;
  }
  // Yaribel 20210728 cliente mapfre VSTS 8370 >>

  calcDescuentoCliente() {
    const Datos = 0;
    return Datos;
  }

  consultarClienteAsociado(tipoDocumento, codigoCliente, fecha) {
    const conexionId = JSON.parse(localStorage.getItem('conexionId'));
    const body = {
      ConexionIacpos: conexionId,
      Idioma: this.idioma,
      Codigo: { TipoDocumento: tipoDocumento, NumeroDocumento: codigoCliente },
      Plataforma: '2',
      TPVId: this.turnoService.turnoValue.TpvId,
      FechaDesde: this.minDia == '' ? fecha || '' : this.minDia,
      FechaHasta: this.maxDia == '' ? fecha || '' : this.maxDia,
    };
    return this.http
      .post(
        `${this.config.getConfig('API_URL')}api/MapfreTarifasCategoriasv2`,
        body
      )
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  setContactoId(value: string): void {
    this.contactoId.next(value);
  }

  parseJCCResponseString(input): PinPandResponse {
    input = input.trim();
    let codeError = '';

    // Case 1: Starts with ERR
    if (/^-?\s*ERR[:\s]/i.test(input)) {
      const match = input.match(/Cod[:\s]*([0-9]+)/i);
      const code = match ? parseInt(match[1], 10) : null;
      codeError = String(code).replace(/\s*/g, '');
    }

    // Case 2: Starts with OK:
    if (/^OK:/i.test(input)) {
      const jsonPart = input.replace(/^[\s\/-]*?(OK)?\s*?:\s*/i, '');
      try {
        const result = JSON.parse(jsonPart) as PinPandResponse;
        codeError = result.Results[0].ResponseCode;

        return result;
      } catch (err) {
        throw new Error('Invalid JSON after OK:');
      }
    }

    if (codeError && codeError !== '00') {
      if (codeError === '10') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_10') || 'Declined/Failed'
        );
      }

      if (codeError === '11') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_11') || 'Batch Status Error'
        );
      }

      if (codeError === '12') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_12') ||
            'Offline Declined(Not ALLOWED)'
        );
      }

      if (codeError === '13') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_13') ||
            'Offline Declined(Installments)'
        );
      }

      if (codeError === '14') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_14') ||
            'No Transaction match'
        );
      }

      if (codeError === '30') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_30') || 'User Break'
        );
      }

      if (codeError === '40') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_40') ||
            'No Connection - Please Try again'
        );
      }

      if (codeError === '50') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_50') ||
            'HOLD - Reset Payment Result Timeout'
        );
      }

      if (codeError === '51') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_51') ||
            'DCC Alert - EFT/POS alerts ECR operator that a DCC transaction is in progress'
        );
      }

      if (codeError === '53') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_53') || 'BOC Loalty Alert'
        );
      }

      if (codeError === '54') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_54') ||
            'Cashback Alert - EFT/POS alerts ECR operator that card is Cashback Eligible'
        );
      }

      if (codeError === '70') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_70') ||
            'Online Approved(Loalty / Gift)'
        );
      }

      if (codeError === '71') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_71') ||
            'Offline Approved(Loalty / Gift)'
        );
      }

      if (codeError === '72') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_72') ||
            'Online Approved(Loalty / Gift)'
        );
      }

      if (codeError === '73') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_73') ||
            'Offline Approved(Loalty / Gift)'
        );
      }

      if (codeError === '80') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_80') ||
            'Invalid Generic Loalty Falg'
        );
      }

      if (codeError === '81') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_81') ||
            'Generic Loalty card not in bins'
        );
      }

      if (codeError === '98') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_98') ||
            'Unknown Transaction/Function'
        );
      }

      if (codeError === '99') {
        throw new Error(
          this.translate.instant('JCC_RESPONSE_CODE_99') ||
            'Cashier Request Format Error'
        );
      }
    }

    throw new Error('Unrecognized format');
  }

  setOtrasFopData(data: OtrosFopData[]): void {
    this.otrasFopData.next(data);
  }

  setCVaccancePago(data: CVacanceData): void {
    this.cVaccanceData.next(data);
  }
}
